import { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { useSearchParams, useNavigate, useParams } from "react-router-dom";
import { CheckoutReviewSheet } from "@/components/menu/CheckoutReviewSheet";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "@/components/ui/button";
import {
  ShoppingCart, Plus, Minus, Search, X, Loader2, Check,
  ArrowLeft, CheckCircle2, MessageCircle, User, LogIn,
  Crown, Star, Stamp, CreditCard, Ticket, Gift, Tag, Trophy,
  Clock, MapPin, ChevronRight, Info, Phone, Instagram, Globe, AlertTriangle, CalendarDays,
  Bell, Users, Receipt, Split, Heart, Percent, Sparkles, Zap
} from "lucide-react";
import { useTableTab } from "@/hooks/useTableTab";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { supabase } from "@/integrations/supabase/client";
import type { Tables } from "@/integrations/supabase/types";
import { isWithinDeliveryRadius } from "@/lib/geo-utils";
import { PwaInstallBanner } from "@/components/PwaInstallBanner";
import { initGA, initMetaPixel, trackAddToCart, trackBeginCheckout, trackPurchase, trackViewContent } from "@/lib/analytics";
import DeliveryAddressForm from "@/components/DeliveryAddressForm";

type Product = Tables<"products">;
type Category = Tables<"categories">;

interface AddonCategoryData {
  id: string;
  name: string;
  is_required: boolean;
  min_select: number;
  max_select: number;
  sort_order: number;
}

interface Addon {
  id: string;
  name: string;
  price: number;
  min_qty: number;
  max_qty: number;
  image_url?: string;
  addon_category_id?: string;
}

interface SelectedAddon {
  id: string;
  name: string;
  price: number;
  qty: number;
  categoryName?: string;
}

interface CartItem {
  cartId: string;
  productId: string;
  name: string;
  price: number;
  qty: number;
  addons: SelectedAddon[];
}

interface LoyaltyProgram {
  id: string;
  type: "points" | "stamp" | "cashback";
  name: string;
  description: string | null;
  points_per_real: number;
  points_to_redeem: number;
  reward_value: number;
  stamps_required: number;
  stamp_reward_description: string;
  cashback_percentage: number;
  min_order_value: number;
}

interface CustomerLoyalty {
  id: string;
  loyalty_program_id: string;
  points_balance: number;
  stamps_count: number;
  cashback_balance: number;
  total_orders: number;
}

interface PromoPopup {
  id: string;
  title: string;
  description: string | null;
  image_url: string | null;
  link_url: string | null;
  show_on_open: boolean;
}

interface AvailableCoupon {
  id: string;
  code: string;
  type: string;
  value: number;
  description: string | null;
  min_order_value: number;
  applies_to_discounted: boolean;
  valid_until: string | null;
}

const fmt = (v: number) => `R$ ${v.toFixed(2).replace(".", ",")}`;
const MenuDigital = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { slug: paramSlug } = useParams();
  const restaurantSlug = paramSlug || searchParams.get("r");
  const mesaParam = searchParams.get("mesa");

  const [loading, setLoading] = useState(true);
  const [restaurant, setRestaurant] = useState<Tables<"restaurants"> | null>(null);
  const [categories, setCategories] = useState<Category[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [productAddons, setProductAddons] = useState<Record<string, Addon[]>>({});
  const [productAddonCategories, setProductAddonCategories] = useState<Record<string, AddonCategoryData[]>>({});

  const [activeCat, setActiveCat] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [showSearch, setShowSearch] = useState(false);
  const [cart, setCart] = useState<CartItem[]>([]);
  const [showCart, setShowCart] = useState(false);
  const [checkoutStep, setCheckoutStep] = useState<"cart" | "auth" | "form" | "review" | "mp_payment" | "mp_pix" | "success" | "rating" | "comanda" | "tab_closing" | "tab_payment">("cart");
  const [submitting, setSubmitting] = useState(false);
  const [successOrderId, setSuccessOrderId] = useState<string | null>(null);
  const [whatsappUrl, setWhatsappUrl] = useState<string | null>(null);
  const [orderCompleted, setOrderCompleted] = useState(false);

  // Table tab (comanda) hook
  const {
    isTableMode, tab, tabItems, tabTotal, tabItemCount,
    loading: tabLoading, waiterRequested,
    addItemsToTab, requestWaiter, cancelWaiterRequest,
  } = useTableTab(restaurant?.id || null, mesaParam);

  // Tab closing state
  const [showComanda, setShowComanda] = useState(false);
  const [splitMode, setSplitMode] = useState<"single" | "equal" | "by_item">("single");
  const [splitCount, setSplitCount] = useState(2);
  const [selectedSplitItems, setSelectedSplitItems] = useState<Set<string>>(new Set());
  const [tabPaymentMethod, setTabPaymentMethod] = useState<string>("");

  // Rating state
  const [foodRating, setFoodRating] = useState(0);
  const [serviceRating, setServiceRating] = useState(0);
  const [ratingComment, setRatingComment] = useState("");
  const [ratingSubmitting, setRatingSubmitting] = useState(false);

  // Auth state
  const [currentUser, setCurrentUser] = useState<any>(null);
  const [authModal, setAuthModal] = useState(false);
  const [authMode, setAuthMode] = useState<"login" | "register">("login");
  const [authEmail, setAuthEmail] = useState("");
  const [authPassword, setAuthPassword] = useState("");
  const [authName, setAuthName] = useState("");
  const [authPhone, setAuthPhone] = useState("");
  const [authLoading, setAuthLoading] = useState(false);
  const [authError, setAuthError] = useState("");

  // Checkout form
  const [customerName, setCustomerName] = useState("");
  const [customerPhone, setCustomerPhone] = useState("");
  const [orderType, setOrderType] = useState<"dine_in" | "takeout" | "delivery">("dine_in");
  const [tableNumber, setTableNumber] = useState(mesaParam || "");
  const [deliveryAddress, setDeliveryAddress] = useState("");
  const [deliveryValidation, setDeliveryValidation] = useState<"idle" | "checking" | "ok" | "too_far" | "not_found">("idle");
  const [deliveryDistance, setDeliveryDistance] = useState<number | null>(null);
  const [calculatedDeliveryFee, setCalculatedDeliveryFee] = useState<number | null>(null);
  const [paymentMethod, setPaymentMethod] = useState<string>("");
  const [orderNotes, setOrderNotes] = useState("");
  const [deliveryPaymentMethod, setDeliveryPaymentMethod] = useState<string>("");
  const [needsChange, setNeedsChange] = useState(false);
  const [changeAmount, setChangeAmount] = useState("");

  // Scheduling state
  const [isScheduled, setIsScheduled] = useState(false);
  const [scheduledDate, setScheduledDate] = useState("");
  const [scheduledTime, setScheduledTime] = useState("");

  // Split payment state
  const [showSplitCalc, setShowSplitCalc] = useState(false);
  const [splitPeople, setSplitPeople] = useState(2);

  // MercadoPago payment state
  const [mpLoading, setMpLoading] = useState(false);
  const [mpProcessing, setMpProcessing] = useState(false);
  const [mpError, setMpError] = useState("");
  const mpBrickRef = useRef<any>(null);
  const mpContainerRef = useRef<HTMLDivElement>(null);

  // PIX payment state
  const [pixQrCode, setPixQrCode] = useState("");
  const [pixQrCodeBase64, setPixQrCodeBase64] = useState("");
  const [pixPaymentId, setPixPaymentId] = useState<string | null>(null);
  const [pixLoading, setPixLoading] = useState(false);
  const [pixCopied, setPixCopied] = useState(false);
  const [pixEmail, setPixEmail] = useState("");
  const [pixCpf, setPixCpf] = useState("");

  // Coupon
  const [couponCode, setCouponCode] = useState("");
  const [appliedCoupon, setAppliedCoupon] = useState<any>(null);
  const [couponError, setCouponError] = useState("");
  const [couponLoading, setCouponLoading] = useState(false);

  // Loyalty
  const [loyaltyPrograms, setLoyaltyPrograms] = useState<LoyaltyProgram[]>([]);
  const [customerLoyalty, setCustomerLoyalty] = useState<CustomerLoyalty[]>([]);

  // Favorites
  const [customerFavorites, setCustomerFavorites] = useState<Set<string>>(new Set());

  // Loyalty redemption
  const [redeemPoints, setRedeemPoints] = useState(false);
  const [redeemCashback, setRedeemCashback] = useState(false);

  // Product detail modal
  const [detailProduct, setDetailProduct] = useState<Product | null>(null);
  const [detailQty, setDetailQty] = useState(1);

  // Addon selection modal
  const [addonModal, setAddonModal] = useState<Product | null>(null);
  const [selectedAddons, setSelectedAddons] = useState<Record<string, number>>({});

  // Store info dialog
  const [showStoreInfo, setShowStoreInfo] = useState(false);
  const [promoPopups, setPromoPopups] = useState<PromoPopup[]>([]);
  
  // AI Combo state
  const [aiCombos, setAiCombos] = useState<any[]>([]);
  const [combosLoading, setCombosLoading] = useState(false);
  const [showComboModal, setShowComboModal] = useState<any>(null);
  const [showPromoPopup, setShowPromoPopup] = useState<PromoPopup | null>(null);

  // Rating state (store avg)
  const [storeRating, setStoreRating] = useState<{ avg: number; count: number } | null>(null);

  // Available coupons
  const [availableCoupons, setAvailableCoupons] = useState<AvailableCoupon[]>([]);
  const [showCouponsPopup, setShowCouponsPopup] = useState(false);

  // Refs for category scroll
  const categoryRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const tabsRef = useRef<HTMLDivElement>(null);

  // Check auth on mount
  useEffect(() => {
    const checkAuth = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        setCurrentUser(user);
        const { data: profile } = await supabase.from("profiles").select("full_name, phone").eq("user_id", user.id).maybeSingle();
        if (profile) {
          setCustomerName(profile.full_name || "");
          setCustomerPhone(profile.phone || "");
        }
      }
    };
    checkAuth();

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setCurrentUser(session?.user || null);
    });
    return () => subscription.unsubscribe();
  }, []);

  useEffect(() => {
    const load = async () => {
      let query = supabase.from("restaurants").select("*").eq("is_active", true);
      if (restaurantSlug) query = query.eq("slug", restaurantSlug);
      const { data: restData } = await query.limit(1).maybeSingle();

      if (!restData) { setLoading(false); return; }

      // Fetch payment credentials separately (secure table)
      const { data: paymentCreds } = await supabase
        .from("restaurant_payment_credentials")
        .select("mp_access_token, mp_public_key")
        .eq("restaurant_id", restData.id)
        .maybeSingle();

      const enrichedRestData = {
        ...restData,
        mp_access_token: paymentCreds?.mp_access_token || null,
        mp_public_key: paymentCreds?.mp_public_key || null,
      };
      setRestaurant(enrichedRestData as any);

      // Init analytics if configured
      if ((restData as any).ga_measurement_id) initGA((restData as any).ga_measurement_id);
      if ((restData as any).meta_pixel_id) initMetaPixel((restData as any).meta_pixel_id);

      const [catRes, prodRes, addonsRes, addonCatsRes, loyaltyRes, popupsRes] = await Promise.all([
        supabase.from("categories").select("*").eq("restaurant_id", restData.id).eq("is_active", true).order("sort_order"),
        supabase.from("products").select("*").eq("restaurant_id", restData.id).eq("is_available", true).order("sort_order"),
        supabase.from("product_addons").select("*").eq("is_available", true).order("sort_order"),
        supabase.from("addon_categories" as any).select("*").eq("restaurant_id", restData.id).order("sort_order"),
        supabase.from("loyalty_programs").select("*").eq("restaurant_id", restData.id).eq("is_active", true),
        supabase.from("promo_popups").select("*").eq("restaurant_id", restData.id).eq("is_active", true).order("sort_order") as any,
      ]);

      setCategories(catRes.data || []);
      setProducts(prodRes.data || []);
      setLoyaltyPrograms((loyaltyRes.data as any[]) || []);

      // Set promo popups and show first one
      const activePopups = (popupsRes.data || []) as PromoPopup[];
      setPromoPopups(activePopups);
      const openPopup = activePopups.find((p: PromoPopup) => p.show_on_open);
      if (openPopup) setTimeout(() => setShowPromoPopup(openPopup), 500);

      const grouped: Record<string, Addon[]> = {};
      (addonsRes.data || []).forEach((a: any) => {
        if (!grouped[a.product_id]) grouped[a.product_id] = [];
        grouped[a.product_id].push({
          id: a.id, name: a.name, price: Number(a.price), min_qty: a.min_qty, max_qty: a.max_qty,
          image_url: a.image_url || undefined, addon_category_id: a.addon_category_id || undefined,
        });
      });
      setProductAddons(grouped);

      // Group addon categories by product
      const addonCatsGrouped: Record<string, AddonCategoryData[]> = {};
      ((addonCatsRes.data || []) as any[]).forEach((c: any) => {
        if (!addonCatsGrouped[c.product_id]) addonCatsGrouped[c.product_id] = [];
        addonCatsGrouped[c.product_id].push({
          id: c.id, name: c.name, is_required: c.is_required,
          min_select: c.min_select, max_select: c.max_select, sort_order: c.sort_order,
        });
      });
      setProductAddonCategories(addonCatsGrouped);

      // Load store rating if enabled
      if ((restData as any).show_rating) {
        const { data: ratingsData } = await supabase
          .from("order_ratings")
          .select("food_rating, service_rating")
          .eq("restaurant_id", restData.id);
        if (ratingsData && ratingsData.length > 0) {
          const avg = ratingsData.reduce((s, r) => s + (Number(r.food_rating) + Number(r.service_rating)) / 2, 0) / ratingsData.length;
          setStoreRating({ avg: Math.round(avg * 10) / 10, count: ratingsData.length });
        }
      }

      // Load available coupons
      const { data: couponsData } = await supabase
        .from("coupons")
        .select("id, code, type, value, description, min_order_value, valid_until, applies_to_discounted")
        .eq("restaurant_id", restData.id)
        .eq("is_active", true) as any;
      if (couponsData) {
        const validCoupons = (couponsData as any[]).filter((c: any) => {
          if (c.valid_until && new Date(c.valid_until) < new Date()) return false;
          return true;
        });
        setAvailableCoupons(validCoupons);
      }

      // Check for reorder cart from MinhaConta
      const reorderCart = sessionStorage.getItem("viamenu_reorder_cart");
      if (reorderCart) {
        try {
          const items = JSON.parse(reorderCart) as CartItem[];
          if (items.length > 0) {
            setCart(items);
            setShowCart(true);
          }
        } catch { /* ignore */ }
        sessionStorage.removeItem("viamenu_reorder_cart");
      }

      setLoading(false);
    };
    load();
  }, [restaurantSlug]);

  // Load customer loyalty & favorites when user logs in
  useEffect(() => {
    if (currentUser && restaurant) {
      const loadUserData = async () => {
        const [loyaltyRes, favRes] = await Promise.all([
          supabase.from("customer_loyalty").select("*").eq("customer_id", currentUser.id).eq("restaurant_id", restaurant.id),
          supabase.from("customer_favorites").select("product_id").eq("customer_id", currentUser.id).eq("restaurant_id", restaurant.id),
        ]);
        setCustomerLoyalty((loyaltyRes.data as any[]) || []);
        setCustomerFavorites(new Set((favRes.data || []).map((f: any) => f.product_id)));
      };
      loadUserData();
    } else {
      setCustomerLoyalty([]);
      setCustomerFavorites(new Set());
    }
  }, [currentUser, restaurant]);

  // Auth handlers
  const handleLogin = async () => {
    setAuthLoading(true);
    setAuthError("");
    try {
      const { error } = await supabase.auth.signInWithPassword({ email: authEmail, password: authPassword });
      if (error) throw error;
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        setCurrentUser(user);
        const { data: profile } = await supabase.from("profiles").select("full_name, phone").eq("user_id", user.id).maybeSingle();
        if (profile) {
          setCustomerName(profile.full_name || "");
          setCustomerPhone(profile.phone || "");
        }
      }
      setAuthModal(false);
      if (checkoutStep === "auth") setCheckoutStep("form");
    } catch (err: any) {
      setAuthError(err.message === "Invalid login credentials" ? "E-mail ou senha incorretos" : err.message);
    } finally {
      setAuthLoading(false);
    }
  };

  const handleRegister = async () => {
    setAuthLoading(true);
    setAuthError("");
    try {
      const { error } = await supabase.auth.signUp({
        email: authEmail,
        password: authPassword,
        options: {
          data: { full_name: authName, phone: authPhone },
          emailRedirectTo: window.location.origin,
        },
      });
      if (error) throw error;
      setCustomerName(authName);
      setCustomerPhone(authPhone);
      setAuthModal(false);
      if (checkoutStep === "auth") setCheckoutStep("form");
    } catch (err: any) {
      setAuthError(err.message);
    } finally {
      setAuthLoading(false);
    }
  };

  const handleLogout = async () => {
    await supabase.auth.signOut();
    setCurrentUser(null);
    setCustomerLoyalty([]);
    setCustomerFavorites(new Set());
  };

  const toggleFavorite = async (productId: string) => {
    if (!currentUser || !restaurant) {
      toast.info("Faça login para salvar favoritos.");
      return;
    }
    const isFav = customerFavorites.has(productId);
    if (isFav) {
      await supabase.from("customer_favorites").delete().eq("customer_id", currentUser.id).eq("product_id", productId);
      setCustomerFavorites(prev => { const s = new Set(prev); s.delete(productId); return s; });
      toast.success("Removido dos favoritos");
    } else {
      await supabase.from("customer_favorites").insert({ customer_id: currentUser.id, restaurant_id: restaurant.id, product_id: productId } as any);
      setCustomerFavorites(prev => new Set(prev).add(productId));
      toast.success("Adicionado aos favoritos ❤️");
    }
  };

  // Coupon validation
  const applyCoupon = async () => {
    if (!couponCode.trim() || !restaurant) return;
    setCouponLoading(true);
    setCouponError("");
    try {
      const { data, error } = await supabase.from("coupons")
        .select("*")
        .eq("restaurant_id", restaurant.id)
        .eq("code", couponCode.trim().toUpperCase())
        .eq("is_active", true)
        .maybeSingle();

      if (error) throw error;
      if (!data) { setCouponError("Cupom inválido ou expirado"); return; }

      if (data.valid_until && new Date(data.valid_until) < new Date()) {
        setCouponError("Cupom expirado"); return;
      }
      if (data.max_uses && data.uses_count >= data.max_uses) {
        setCouponError("Cupom esgotado"); return;
      }
      if (cartTotal < Number(data.min_order_value)) {
        setCouponError(`Pedido mínimo de ${fmt(Number(data.min_order_value))}`); return;
      }

      // Check if coupon applies to discounted products
      const hasDiscountedItems = cart.some(item => {
        const prod = products.find(p => p.id === item.productId);
        return prod && (prod as any).discount_price && Number((prod as any).discount_price) > 0 && Number((prod as any).discount_price) < Number(prod.price);
      });

      if (hasDiscountedItems && (data as any).applies_to_discounted === false) {
        setCouponError("Este cupom não pode ser aplicado porque seu carrinho contém produtos que já estão em promoção. Remova os itens com desconto ou use outro cupom.");
        return;
      }

      setAppliedCoupon(data);
      setCouponError("");
    } catch (err: any) {
      setCouponError(err.message);
    } finally {
      setCouponLoading(false);
    }
  };

  const removeCoupon = () => {
    setAppliedCoupon(null);
    setCouponCode("");
    setCouponError("");
  };

  // Group products by category for iFood-style layout
  const groupedProducts = useMemo(() => {
    let list = products;
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      list = list.filter(p => p.name.toLowerCase().includes(q) || (p.description && p.description.toLowerCase().includes(q)));
    }

    const groups: { id: string; name: string; items: Product[] }[] = [];

    // Promoções — products with discount_price
    const promoItems = list.filter(p => (p as any).discount_price && Number((p as any).discount_price) > 0 && Number((p as any).discount_price) < Number(p.price));
    if (promoItems.length > 0) groups.push({ id: "promos", name: "🔥 Promoções", items: promoItems });

    // Featured
    const featured = list.filter(p => p.is_featured && !promoItems.some(pr => pr.id === p.id));
    if (featured.length > 0) groups.push({ id: "featured", name: "Destaques", items: featured });

    // By category
    categories.forEach(c => {
      const items = list.filter(p => p.category_id === c.id);
      if (items.length > 0) groups.push({ id: c.id, name: c.name, items });
    });

    // Uncategorized
    const uncategorized = list.filter(p => !p.category_id && !p.is_featured);
    if (uncategorized.length > 0) groups.push({ id: "outros", name: "Outros", items: uncategorized });

    return groups;
  }, [products, categories, searchQuery]);

  const categoryTabs = useMemo(() => {
    return groupedProducts.map(g => ({ id: g.id, name: g.name }));
  }, [groupedProducts]);

  const scrollToCategory = (catId: string) => {
    setActiveCat(catId);
    const el = categoryRefs.current[catId];
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  const cartTotal = cart.reduce((s, i) => {
    const addonTotal = i.addons.reduce((a, ad) => a + ad.price * ad.qty, 0);
    return s + (i.price + addonTotal) * i.qty;
  }, 0);
  const cartCount = cart.reduce((s, i) => s + i.qty, 0);

  const couponDiscount = useMemo(() => {
    if (!appliedCoupon) return 0;
    if (appliedCoupon.type === "percentage") return cartTotal * Number(appliedCoupon.value) / 100;
    if (appliedCoupon.type === "fixed") return Math.min(Number(appliedCoupon.value), cartTotal);
    if (appliedCoupon.type === "free_shipping") return Number(restaurant?.delivery_fee || 0);
    return 0;
  }, [appliedCoupon, cartTotal, restaurant]);

  const loyaltyRedemptionDiscount = useMemo(() => {
    let discount = 0;
    if (!currentUser) return discount;

    if (redeemPoints) {
      const pointsProg = loyaltyPrograms.find(p => p.type === "points");
      const pointsLoyalty = customerLoyalty.find(cl => cl.loyalty_program_id === pointsProg?.id);
      if (pointsProg && pointsLoyalty && pointsLoyalty.points_balance >= (pointsProg.points_to_redeem || 100)) {
        const redeemSets = Math.floor(Number(pointsLoyalty.points_balance) / (pointsProg.points_to_redeem || 100));
        const maxDiscount = redeemSets * Number(pointsProg.reward_value || 10);
        discount += Math.min(maxDiscount, cartTotal - couponDiscount);
      }
    }

    if (redeemCashback) {
      const cashbackProg = loyaltyPrograms.find(p => p.type === "cashback");
      const cashbackLoyalty = customerLoyalty.find(cl => cl.loyalty_program_id === cashbackProg?.id);
      if (cashbackProg && cashbackLoyalty && Number(cashbackLoyalty.cashback_balance) > 0) {
        const remaining = cartTotal - couponDiscount - discount;
        discount += Math.min(Number(cashbackLoyalty.cashback_balance), remaining);
      }
    }

    return Math.max(0, discount);
  }, [redeemPoints, redeemCashback, loyaltyPrograms, customerLoyalty, cartTotal, couponDiscount, currentUser]);

  const effectiveDeliveryFee = useMemo(() => {
    if (orderType !== "delivery") return 0;
    if (calculatedDeliveryFee !== null) return calculatedDeliveryFee;
    return Number(restaurant?.delivery_fee || 0);
  }, [orderType, calculatedDeliveryFee, restaurant]);

  const finalTotal = Math.max(0, cartTotal - couponDiscount - loyaltyRedemptionDiscount + effectiveDeliveryFee);

  const loyaltyPreview = useMemo(() => {
    return loyaltyPrograms.map((p) => {
      if (p.type === "points") return { ...p, earn: `+${Math.floor(finalTotal * p.points_per_real)} pontos` };
      if (p.type === "stamp") return { ...p, earn: "+1 carimbo" };
      if (p.type === "cashback") return { ...p, earn: `+${fmt(finalTotal * p.cashback_percentage / 100)} cashback` };
      return { ...p, earn: "" };
    });
  }, [loyaltyPrograms, finalTotal]);

  const handleAddClick = (prod: Product) => {
    const addons = productAddons[prod.id];
    if (addons && addons.length > 0) {
      setAddonModal(prod);
      setSelectedAddons({});
    } else {
      addToCart(prod, []);
    }
  };

  const addToCart = (prod: Product, addons: SelectedAddon[], overridePrice?: number) => {
    const cartId = `${prod.id}-${Date.now()}`;
    const effectivePrice = overridePrice ?? Number((prod as any).discount_price && Number((prod as any).discount_price) > 0 ? (prod as any).discount_price : prod.price);
    setCart(prev => [...prev, { cartId, productId: prod.id, name: prod.name, price: effectivePrice, qty: 1, addons }]);
    trackAddToCart({ id: prod.id, name: prod.name, price: effectivePrice, quantity: 1 });
  };

  // Load AI combos
  useEffect(() => {
    if (!restaurant || !(restaurant as any).ai_combo_enabled) return;
    const loadCombos = async () => {
      setCombosLoading(true);
      try {
        const { data, error } = await supabase.functions.invoke("suggest-combos", {
          body: { restaurant_id: restaurant.id },
        });
        if (!error && data?.combos) {
          setAiCombos(data.combos);
        }
      } catch (e) {
        console.error("Combo load error:", e);
      } finally {
        setCombosLoading(false);
      }
    };
    loadCombos();
  }, [restaurant]);

  const addComboToCart = (combo: any) => {
    combo.items.forEach((item: any) => {
      const product = products.find(p => p.id === item.id);
      if (product) {
        const comboItemPrice = (combo.combo_price / combo.items.length);
        addToCart(product, [], Math.round(comboItemPrice * 100) / 100);
      }
    });
    setShowComboModal(null);
    toast.success(`${combo.name} adicionado ao carrinho! 🎉`);
  };

  const confirmAddons = () => {
    if (!addonModal) return;
    const addons = productAddons[addonModal.id] || [];
    const categories = productAddonCategories[addonModal.id] || [];
    
    // Validate category requirements
    for (const cat of categories) {
      const catAddons = addons.filter(a => a.addon_category_id === cat.id);
      const totalSelected = catAddons.reduce((s, a) => s + (selectedAddons[a.id] || 0), 0);
      if (cat.is_required && totalSelected < cat.min_select) return;
    }
    
    // Also validate uncategorized addons with min_qty
    const uncategorized = addons.filter(a => !a.addon_category_id);
    for (const addon of uncategorized) {
      if ((selectedAddons[addon.id] || 0) < addon.min_qty) return;
    }
    
    // Build selected addons with category name
    const selected: SelectedAddon[] = addons.filter(a => (selectedAddons[a.id] || 0) > 0)
      .map(a => {
        const cat = categories.find(c => c.id === a.addon_category_id);
        return { id: a.id, name: a.name, price: a.price, qty: selectedAddons[a.id] || 0, categoryName: cat?.name || undefined };
      });
    addToCart(addonModal, selected);
    setAddonModal(null);
    setSelectedAddons({});
  };

  const updateAddonQty = (addonId: string, delta: number, max: number, categoryId?: string) => {
    setSelectedAddons(prev => {
      const currentQty = prev[addonId] || 0;
      const newQty = Math.max(0, Math.min(max, currentQty + delta));
      
      // If this addon belongs to a category, enforce category max_select
      if (categoryId && addonModal) {
        const categories = productAddonCategories[addonModal.id] || [];
        const cat = categories.find(c => c.id === categoryId);
        if (cat) {
          const allAddons = productAddons[addonModal.id] || [];
          const catAddons = allAddons.filter(a => a.addon_category_id === categoryId);
          const currentCatTotal = catAddons.reduce((s, a) => s + (prev[a.id] || 0), 0);
          const catDelta = newQty - currentQty;
          
          if (currentCatTotal + catDelta > cat.max_select) {
            // If max_select is 1 (radio-like), deselect all others first
            if (cat.max_select === 1 && delta > 0) {
              const updated = { ...prev };
              catAddons.forEach(a => { updated[a.id] = 0; });
              updated[addonId] = 1;
              return updated;
            }
            // Otherwise, don't allow exceeding
            return prev;
          }
        }
      }
      
      return { ...prev, [addonId]: newQty };
    });
  };

  const updateCartQty = (cartId: string, delta: number) => {
    setCart(prev => prev.map(i => i.cartId === cartId ? { ...i, qty: Math.max(0, i.qty + delta) } : i).filter(i => i.qty > 0));
  };

  const proceedToCheckout = () => {
    // Track begin_checkout event
    trackBeginCheckout(
      cart.map(i => ({ id: i.productId, name: i.name, price: i.price, quantity: i.qty })),
      cartTotal
    );
    if (isTableMode) {
      handleAddToTab();
      return;
    }
    if (!currentUser) {
      setCheckoutStep("auth");
    } else {
      setCheckoutStep("form");
    }
  };

  // Add cart items to the open tab (comanda) AND send to kitchen
  const handleAddToTab = async () => {
    if (cart.length === 0 || !restaurant) return;
    setSubmitting(true);
    const items = cart.map((i) => ({
      productId: i.productId,
      name: i.name,
      price: i.price,
      qty: i.qty,
      addons: i.addons,
    }));
    const success = await addItemsToTab(items);
    if (success) {
      // Also create an order so it appears in the kitchen panel
      try {
        const tableNum = mesaParam ? parseInt(mesaParam) : null;
        await supabase.functions.invoke("create-order", {
          body: {
            restaurant_id: restaurant.id,
            customer_name: `Mesa ${tableNum || "?"}`,
            customer_phone: null,
            customer_id: currentUser?.id || null,
            order_type: "dine_in",
            table_number: tableNum,
            payment_method: null,
            notes: null,
            items: items.map((i) => ({
              productId: i.productId,
              name: i.name,
              price: i.price,
              qty: i.qty,
              addons: i.addons.map((a) => ({ name: a.name, price: a.price, qty: a.qty })),
            })),
            discount: 0,
            delivery_fee: 0,
          },
        });
      } catch (e) {
        console.error("Error sending table order to kitchen:", e);
      }
      toast.success("Itens adicionados à comanda!");
      setCart([]);
      setShowCart(false);
      setCheckoutStep("cart");
    } else {
      toast.error("Erro ao adicionar itens à comanda");
    }
    setSubmitting(false);
  };

  // Handle finalizar atendimento (request waiter)
  const handleFinalizarAtendimento = async () => {
    await requestWaiter();
    toast.success("Garçom chamado! Aguarde o atendimento.");
    setCheckoutStep("tab_closing");
  };

  // Close the tab and create the order
  const handleCloseTab = async () => {
    if (!restaurant || !tab) return;
    setSubmitting(true);
    try {
      // Calculate the amount to pay based on split mode
      let amountToPay = tabTotal;
      if (splitMode === "equal") {
        amountToPay = tabTotal / splitCount;
      } else if (splitMode === "by_item") {
        amountToPay = tabItems
          .filter((i) => selectedSplitItems.has(i.id))
          .reduce((s, i) => {
            const addonTotal = (i.addons || []).reduce((a: number, ad: any) => a + (ad.price || 0) * (ad.qty || 1), 0);
            return s + (Number(i.unit_price) + addonTotal) * i.quantity;
          }, 0);
      }

      // If paying via MP PIX, redirect to PIX flow
      if (tabPaymentMethod === "mp_pix") {
        // Build items for the order from tab items
        const orderItems = (splitMode === "by_item" ? tabItems.filter(i => selectedSplitItems.has(i.id)) : tabItems).map((i) => ({
          productId: i.product_id || "",
          name: i.product_name,
          price: Number(i.unit_price),
          qty: i.quantity,
          addons: i.addons || [],
        }));

        // Set cart temporarily for PIX flow
        setCart(orderItems.map(i => ({
          cartId: crypto.randomUUID(),
          productId: i.productId,
          name: i.name,
          price: i.price,
          qty: i.qty,
          addons: (i.addons || []).map((a: any) => ({ id: a.id || "", name: a.name || "", price: a.price || 0, qty: a.qty || 1 })),
        })));

        setOrderType("dine_in");
        setTableNumber(mesaParam || "");
        setPaymentMethod("mp_pix");
        setCheckoutStep("form");
        setSubmitting(false);
        return;
      }

      // For local payment, create the order directly
      const projectId = import.meta.env.VITE_SUPABASE_PROJECT_ID;
      const orderItems = (splitMode === "by_item" ? tabItems.filter(i => selectedSplitItems.has(i.id)) : tabItems).map((i) => ({
        productId: i.product_id || "",
        name: i.product_name,
        price: Number(i.unit_price),
        qty: i.quantity,
        addons: i.addons || [],
      }));

      const paymentMethodMap: Record<string, string> = {
        pix: "pix", credit_card: "credit_card", debit_card: "debit_card", cash: "cash",
      };

      const res = await fetch(
        `https://${projectId}.supabase.co/functions/v1/create-order`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            restaurant_id: restaurant.id,
            customer_name: customerName || "Mesa " + mesaParam,
            customer_phone: customerPhone,
            customer_id: currentUser?.id || null,
            order_type: "dine_in",
            table_number: parseInt(mesaParam || "0"),
            payment_method: paymentMethodMap[tabPaymentMethod] || null,
            notes: splitMode === "equal" ? `Conta dividida por ${splitCount} (${fmt(amountToPay)} por pessoa)` : splitMode === "by_item" ? "Pagamento parcial (por item)" : `Comanda Mesa ${mesaParam}`,
            discount: 0,
            items: orderItems,
          }),
        }
      );
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Erro ao enviar pedido");

      // Close the tab
      await supabase
        .from("table_tabs" as any)
        .update({ status: "closed", closed_at: new Date().toISOString() } as any)
        .eq("id", tab.id);

      setSuccessOrderId(data.order_id);
      setCheckoutStep("success");
      setShowComanda(false);
      toast.success("Comanda fechada com sucesso!");
    } catch (err: any) {
      toast.error(err.message || "Erro ao fechar comanda");
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmitOrder = async () => {
    if (!restaurant || cart.length === 0 || !customerName.trim()) return;
    setSubmitting(true);
    try {
      const projectId = import.meta.env.VITE_SUPABASE_PROJECT_ID;
      const res = await fetch(
        `https://${projectId}.supabase.co/functions/v1/create-order`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify((() => {
            // Resolve actual payment method
            let resolvedPayment = paymentMethod;
            if (paymentMethod === "pay_on_delivery") {
              resolvedPayment = deliveryPaymentMethod || "cash";
            }
            // Map to valid enum values
            const paymentMethodMap: Record<string, string> = {
              mercadopago: "mercadopago",
              pix: "pix",
              credit_card: "credit_card",
              debit_card: "debit_card",
              cash: "cash",
            };
            const finalPaymentMethod = paymentMethodMap[resolvedPayment] || null;

            // Build payment notes
            const paymentNotes: string[] = [];
            if (paymentMethod === "pay_on_delivery") {
              const subLabels: Record<string, string> = { pix: "PIX", credit_card: "Cartão de Crédito", cash: "Dinheiro" };
              paymentNotes.push(`💳 Pagar na entrega: ${subLabels[deliveryPaymentMethod] || deliveryPaymentMethod}`);
            }
            if ((paymentMethod === "cash" || (paymentMethod === "pay_on_delivery" && deliveryPaymentMethod === "cash")) && needsChange && changeAmount) {
              const changeValue = Number(changeAmount) - finalTotal;
              paymentNotes.push(`💰 Troco para R$ ${Number(changeAmount).toFixed(2).replace(".", ",")} (troco: ${fmt(changeValue > 0 ? changeValue : 0)})`);
            }

            // Split payment notes
            if (showSplitCalc && splitPeople >= 2 && (restaurant as any).split_payment_enabled) {
              const perPerson = finalTotal / splitPeople;
              paymentNotes.push(`💳 Conta dividida entre ${splitPeople} pessoas — ${fmt(perPerson)} cada`);
            }

            const allNotes = [orderNotes, ...paymentNotes].filter(Boolean).join("\n");

            // Build scheduled_for timestamp
            let scheduledForValue = null;
            if (isScheduled && scheduledDate && scheduledTime && (restaurant as any).scheduled_orders_enabled) {
              scheduledForValue = `${scheduledDate}T${scheduledTime}:00`;
            }

            return {
              restaurant_id: restaurant.id,
              customer_name: customerName,
              customer_phone: customerPhone,
              customer_id: currentUser?.id || null,
              order_type: isScheduled && scheduledForValue ? "scheduled" : orderType,
              table_number: orderType === "dine_in" && tableNumber ? parseInt(tableNumber) : null,
              delivery_address: orderType === "delivery" ? deliveryAddress : null,
              payment_method: finalPaymentMethod,
              notes: allNotes,
              discount: couponDiscount + loyaltyRedemptionDiscount,
              delivery_fee: effectiveDeliveryFee,
              coupon_id: appliedCoupon?.id || null,
              scheduled_for: scheduledForValue,
              loyalty_redemption: loyaltyRedemptionDiscount > 0 ? {
                points_redeemed: redeemPoints,
                cashback_redeemed: redeemCashback,
                discount_amount: loyaltyRedemptionDiscount,
              } : null,
              items: cart.map((i) => ({
                productId: i.productId, name: i.name, price: i.price, qty: i.qty, addons: i.addons,
              })),
            };
          })()),
        }
      );
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Erro ao enviar pedido");

      const orderId = data.order_id?.slice(0, 6).toUpperCase() || "";
      const orderTypeLabels: Record<string, string> = { dine_in: "No local", takeout: "Retirada", delivery: "Entrega" };
      let msg = `✅ *Pedido Confirmado!*\n📋 *#${orderId}*\n\n👤 ${customerName}\n📦 ${orderTypeLabels[orderType] || orderType}`;
      if (orderType === "dine_in" && tableNumber) msg += ` — Mesa ${tableNumber}`;
      if (orderType === "delivery" && deliveryAddress) msg += `\n📍 ${deliveryAddress}`;
      msg += `\n\n*Itens:*\n`;
      cart.forEach((item) => {
        const addonTotal = item.addons.reduce((a, ad) => a + ad.price * ad.qty, 0);
        msg += `• ${item.qty}x ${item.name} — ${fmt((item.price + addonTotal) * item.qty)}\n`;
        // Group addons by category for WhatsApp message
        const grouped: Record<string, typeof item.addons> = {};
        item.addons.forEach(ad => {
          const cat = ad.categoryName || "";
          if (!grouped[cat]) grouped[cat] = [];
          grouped[cat].push(ad);
        });
        Object.entries(grouped).forEach(([cat, ads]) => {
          if (cat) msg += `  _${cat}:_\n`;
          ads.forEach(ad => { msg += `  + ${ad.name}${ad.qty > 1 ? ` (x${ad.qty})` : ""}\n`; });
        });
      });
      if (couponDiscount > 0) msg += `\n🎟️ Desconto: -${fmt(couponDiscount)}`;
      msg += `\n💰 *Total: ${fmt(finalTotal)}*`;
      if (paymentMethod) {
        const pmLabels: Record<string, string> = { pix: "PIX", credit_card: "Crédito", debit_card: "Débito", cash: "Dinheiro", mercadopago: "Mercado Pago", pay_on_delivery: "Pagar na Entrega" };
        msg += `\n💳 ${pmLabels[paymentMethod] || paymentMethod}`;
        if (paymentMethod === "pay_on_delivery" && deliveryPaymentMethod) {
          const subLabels: Record<string, string> = { pix: "PIX", credit_card: "Cartão de Crédito", cash: "Dinheiro" };
          msg += ` (${subLabels[deliveryPaymentMethod] || deliveryPaymentMethod})`;
        }
        const isCash = paymentMethod === "cash" || (paymentMethod === "pay_on_delivery" && deliveryPaymentMethod === "cash");
        if (isCash && needsChange && changeAmount) {
          const changeValue = Number(changeAmount) - finalTotal;
          msg += `\n💰 Troco para R$ ${Number(changeAmount).toFixed(2).replace(".", ",")} (troco: ${fmt(changeValue > 0 ? changeValue : 0)})`;
        }
      }
      if (orderNotes) msg += `\n📝 ${orderNotes}`;
      msg += `\n\nObrigado por seu pedido! 🙏`;

      const restPhone = restaurant.phone?.replace(/\D/g, "") || "";
      const waUrl = restPhone ? `https://wa.me/55${restPhone}?text=${encodeURIComponent(msg)}` : `https://wa.me/?text=${encodeURIComponent(msg)}`;
      setWhatsappUrl(waUrl);

      // Track purchase conversion
      trackPurchase(
        data.order_id,
        cart.map(i => ({ id: i.productId, name: i.name, price: i.price, quantity: i.qty })),
        finalTotal
      );

      setSuccessOrderId(data.order_id);
      setOrderCompleted(false);
      setFoodRating(0);
      setServiceRating(0);
      setRatingComment("");
      setCheckoutStep("success");
      setCart([]);
      setAppliedCoupon(null);
      setCouponCode("");
      setRedeemPoints(false);
      setRedeemCashback(false);
    } catch (err: any) {
      alert(err.message || "Erro ao finalizar pedido");
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmitRating = async () => {
    if (!successOrderId || !restaurant) return;
    if (foodRating === 0 || serviceRating === 0) return;
    setRatingSubmitting(true);
    try {
      await supabase.functions.invoke("submit-rating", {
        body: {
          order_id: successOrderId,
          food_rating: foodRating,
          service_rating: serviceRating,
          comment: ratingComment.trim() || null,
        },
      });
    } catch (err) {
      console.error("Rating error:", err);
    } finally {
      setRatingSubmitting(false);
      setCheckoutStep("success");
    }
  };

  const resetCheckout = () => {
    setShowCart(false);
    setCheckoutStep("cart");
    setCustomerName(currentUser ? customerName : "");
    setCustomerPhone(currentUser ? customerPhone : "");
    setOrderType("dine_in");
    setTableNumber("");
    setDeliveryAddress("");
    setDeliveryValidation("idle");
    setDeliveryDistance(null);
    setCalculatedDeliveryFee(null);
    setPaymentMethod("");
    setOrderNotes("");
    setDeliveryPaymentMethod("");
    setNeedsChange(false);
    setChangeAmount("");
    setIsScheduled(false);
    setScheduledDate("");
    setScheduledTime("");
    setShowSplitCalc(false);
    setSplitPeople(2);
    setSuccessOrderId(null);
    setWhatsappUrl(null);
    setRedeemPoints(false);
    setRedeemCashback(false);
    setOrderCompleted(false);
    setMpError("");
    setMpProcessing(false);
    setMpLoading(false);
    if (mpBrickRef.current) {
      try { mpBrickRef.current.unmount(); } catch (e) {}
      mpBrickRef.current = null;
    }
  };

  // MercadoPago Brick initialization
  useEffect(() => {
    if (checkoutStep !== "mp_payment" || !restaurant) return;

    const publicKey = (restaurant as any).mp_public_key;
    if (!publicKey) {
      setMpError("Chave pública do Mercado Pago não configurada.");
      setMpLoading(false);
      return;
    }

    const loadAndInit = async () => {
      // Load MP SDK if not loaded
      if (!(window as any).MercadoPago) {
        await new Promise<void>((resolve, reject) => {
          if (document.getElementById("mp-sdk-script")) {
            const check = setInterval(() => {
              if ((window as any).MercadoPago) { clearInterval(check); resolve(); }
            }, 100);
            setTimeout(() => { clearInterval(check); reject(new Error("SDK timeout")); }, 10000);
            return;
          }
          const script = document.createElement("script");
          script.id = "mp-sdk-script";
          script.src = "https://sdk.mercadopago.com/js/v2";
          script.onload = () => resolve();
          script.onerror = () => reject(new Error("Falha ao carregar SDK"));
          document.head.appendChild(script);
        });
      }

      try {
        const mp = new (window as any).MercadoPago(publicKey, { locale: "pt-BR" });
        const bricks = mp.bricks();

        // Unmount previous brick if exists
        if (mpBrickRef.current) {
          try { mpBrickRef.current.unmount(); } catch (e) {}
        }

        const brick = await bricks.create("cardPayment", "mp-card-container", {
          initialization: { amount: finalTotal },
          customization: {
            visual: { style: { theme: "default" } },
            paymentMethods: { maxInstallments: 12, minInstallments: 1 },
          },
          callbacks: {
            onReady: () => setMpLoading(false),
            onSubmit: async (cardFormData: any) => {
              setMpProcessing(true);
              setMpError("");
              try {
                const projectId = import.meta.env.VITE_SUPABASE_PROJECT_ID;

                // Build payment notes
                const paymentNotes: string[] = [];
                if (orderNotes) paymentNotes.push(orderNotes);

                // Resolve order data
                const orderPayload = {
                  customer_name: customerName,
                  customer_phone: customerPhone,
                  customer_id: currentUser?.id || null,
                  order_type: orderType,
                  table_number: orderType === "dine_in" && tableNumber ? parseInt(tableNumber) : null,
                  delivery_address: orderType === "delivery" ? deliveryAddress : null,
                  notes: paymentNotes.join("\n"),
                  discount: couponDiscount + loyaltyRedemptionDiscount,
                  coupon_id: appliedCoupon?.id || null,
                  loyalty_redemption: loyaltyRedemptionDiscount > 0 ? {
                    points_redeemed: redeemPoints,
                    cashback_redeemed: redeemCashback,
                    discount_amount: loyaltyRedemptionDiscount,
                  } : null,
                  items: cart.map((i) => ({
                    productId: i.productId, name: i.name, price: i.price, qty: i.qty, addons: i.addons,
                  })),
                };

                const res = await fetch(
                  `https://${projectId}.supabase.co/functions/v1/process-mp-payment`,
                  {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({
                      restaurant_id: restaurant.id,
                      payment_data: cardFormData,
                      order_data: orderPayload,
                    }),
                  }
                );

                const data = await res.json();

                if (data.success) {
                  // Payment approved, order created
                  const orderId = data.order_id?.slice(0, 6).toUpperCase() || "";
                  const orderTypeLabels: Record<string, string> = { dine_in: "No local", takeout: "Retirada", delivery: "Entrega" };
                  let msg = `✅ *Pedido Confirmado!*\n📋 *#${orderId}*\n💳 Pago via Mercado Pago\n\n👤 ${customerName}\n📦 ${orderTypeLabels[orderType] || orderType}`;
                  if (orderType === "dine_in" && tableNumber) msg += ` — Mesa ${tableNumber}`;
                  if (orderType === "delivery" && deliveryAddress) msg += `\n📍 ${deliveryAddress}`;
                  msg += `\n\n*Itens:*\n`;
                  cart.forEach((item) => {
                    const addonTotal = item.addons.reduce((a, ad) => a + ad.price * ad.qty, 0);
                    msg += `• ${item.qty}x ${item.name} — ${fmt((item.price + addonTotal) * item.qty)}\n`;
                  });
                  msg += `\n💰 *Total: ${fmt(finalTotal)}*`;
                  msg += `\n\nObrigado por seu pedido! 🙏`;

                  const restPhone = restaurant.phone?.replace(/\D/g, "") || "";
                  const waUrl = restPhone ? `https://wa.me/55${restPhone}?text=${encodeURIComponent(msg)}` : null;
                  setWhatsappUrl(waUrl);
                  setSuccessOrderId(data.order_id);
                  setOrderCompleted(false);
                  setFoodRating(0);
                  setServiceRating(0);
                  setRatingComment("");
                  setCheckoutStep("success");
                  setCart([]);
                  setAppliedCoupon(null);
                  setCouponCode("");
                  setRedeemPoints(false);
                  setRedeemCashback(false);
                  setMpProcessing(false);
                } else {
                  setMpError(data.error_message || "Pagamento recusado.");
                  setMpProcessing(false);
                  throw new Error(data.error_message); // Brick resets to editable
                }
              } catch (err: any) {
                if (!mpError) setMpError(err.message || "Erro ao processar pagamento.");
                setMpProcessing(false);
                throw err; // Let brick know submission failed
              }
            },
            onError: (error: any) => {
              console.error("MP Brick error:", error);
            },
          },
        });

        mpBrickRef.current = brick;
      } catch (err: any) {
        console.error("MP Brick init error:", err);
        setMpError("Erro ao carregar formulário de pagamento.");
        setMpLoading(false);
      }
    };

    loadAndInit();

    return () => {
      if (mpBrickRef.current) {
        try { mpBrickRef.current.unmount(); } catch (e) {}
        mpBrickRef.current = null;
      }
    };
  }, [checkoutStep, restaurant]);

  // Track view_content when product detail opens
  useEffect(() => {
    if (detailProduct) {
      trackViewContent({ id: detailProduct.id, name: detailProduct.name, price: Number(detailProduct.price), quantity: 1 });
    }
  }, [detailProduct]);

  // PIX payment: create and poll
  const buildOrderPayload = useCallback(() => ({
    customer_name: customerName,
    customer_phone: customerPhone,
    customer_id: currentUser?.id || null,
    order_type: orderType,
    table_number: orderType === "dine_in" && tableNumber ? parseInt(tableNumber) : null,
    delivery_address: orderType === "delivery" ? deliveryAddress : null,
    notes: orderNotes,
    discount: couponDiscount + loyaltyRedemptionDiscount,
    delivery_fee: effectiveDeliveryFee,
    coupon_id: appliedCoupon?.id || null,
    loyalty_redemption: loyaltyRedemptionDiscount > 0 ? {
      points_redeemed: redeemPoints,
      cashback_redeemed: redeemCashback,
      discount_amount: loyaltyRedemptionDiscount,
    } : null,
    items: cart.map((i) => ({
      productId: i.productId, name: i.name, price: i.price, qty: i.qty, addons: i.addons,
    })),
  }), [customerName, customerPhone, currentUser, orderType, tableNumber, deliveryAddress, orderNotes, couponDiscount, loyaltyRedemptionDiscount, effectiveDeliveryFee, appliedCoupon, redeemPoints, redeemCashback, cart]);

  const handlePixPayment = useCallback(async () => {
    if (!restaurant) return;
    setPixLoading(true);
    setMpError("");
    try {
      const projectId = import.meta.env.VITE_SUPABASE_PROJECT_ID;
      const res = await fetch(
        `https://${projectId}.supabase.co/functions/v1/process-mp-payment`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            action: "create_pix",
            restaurant_id: restaurant.id,
            transaction_amount: finalTotal,
            payer_email: pixEmail || currentUser?.email || "cliente@email.com",
            payer_first_name: customerName.split(" ")[0] || "Cliente",
            payer_last_name: customerName.split(" ").slice(1).join(" ") || "",
            payer_cpf: pixCpf.replace(/\D/g, "") || undefined,
            order_data: buildOrderPayload(),
          }),
        }
      );
      const data = await res.json();
      if (data.success && data.qr_code) {
        setPixQrCode(data.qr_code);
        setPixQrCodeBase64(data.qr_code_base64 || "");
        setPixPaymentId(data.payment_id);
        setCheckoutStep("mp_pix");
      } else if (data.success && data.order_id) {
        // Instant approval
        setSuccessOrderId(data.order_id);
        setCheckoutStep("success");
        setCart([]);
      } else {
        setMpError(data.error_message || "Erro ao gerar PIX.");
      }
    } catch (err: any) {
      setMpError(err.message || "Erro ao gerar PIX.");
    } finally {
      setPixLoading(false);
    }
  }, [restaurant, finalTotal, pixEmail, pixCpf, customerName, currentUser, buildOrderPayload]);

  // Poll PIX payment status
  useEffect(() => {
    if (checkoutStep !== "mp_pix" || !pixPaymentId || !restaurant) return;
    let cancelled = false;

    const poll = async () => {
      try {
        const projectId = import.meta.env.VITE_SUPABASE_PROJECT_ID;
        const res = await fetch(
          `https://${projectId}.supabase.co/functions/v1/process-mp-payment`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              action: "check_pix_status",
              restaurant_id: restaurant.id,
              payment_id: pixPaymentId,
            }),
          }
        );
        const data = await res.json();
        if (cancelled) return;

        if (data.status === "approved") {
          // Confirm order
          const confirmRes = await fetch(
            `https://${projectId}.supabase.co/functions/v1/process-mp-payment`,
            {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({
                action: "confirm_pix_order",
                restaurant_id: restaurant.id,
                payment_id: pixPaymentId,
                order_data: buildOrderPayload(),
              }),
            }
          );
          const confirmData = await confirmRes.json();
          if (cancelled) return;

          if (confirmData.success) {
            const orderId = confirmData.order_id?.slice(0, 6).toUpperCase() || "";
            const orderTypeLabels: Record<string, string> = { dine_in: "No local", takeout: "Retirada", delivery: "Entrega" };
            let msg = `✅ *Pedido Confirmado!*\n📋 *#${orderId}*\n📱 Pago via PIX\n\n👤 ${customerName}\n📦 ${orderTypeLabels[orderType] || orderType}`;
            if (orderType === "dine_in" && tableNumber) msg += ` — Mesa ${tableNumber}`;
            if (orderType === "delivery" && deliveryAddress) msg += `\n📍 ${deliveryAddress}`;
            msg += `\n\n*Itens:*\n`;
            cart.forEach((item) => {
              const addonTotal = item.addons.reduce((a, ad) => a + ad.price * ad.qty, 0);
              msg += `• ${item.qty}x ${item.name} — ${fmt((item.price + addonTotal) * item.qty)}\n`;
            });
            msg += `\n💰 *Total: ${fmt(finalTotal)}*`;
            msg += `\n\nObrigado por seu pedido! 🙏`;

            const restPhone = restaurant.phone?.replace(/\D/g, "") || "";
            const waUrl = restPhone ? `https://wa.me/55${restPhone}?text=${encodeURIComponent(msg)}` : null;
            setWhatsappUrl(waUrl);
            setSuccessOrderId(confirmData.order_id);
            setOrderCompleted(false);
            setFoodRating(0);
            setServiceRating(0);
            setRatingComment("");
            setCheckoutStep("success");
            setCart([]);
            setAppliedCoupon(null);
            setCouponCode("");
            setRedeemPoints(false);
            setRedeemCashback(false);
          } else {
            setMpError(confirmData.error_message || "Erro ao confirmar pedido.");
          }
        } else if (data.status === "rejected" || data.status === "cancelled") {
          setMpError("Pagamento PIX expirado ou cancelado. Tente novamente.");
          setCheckoutStep("form");
        }
      } catch (err) {
        console.error("PIX poll error:", err);
      }
    };

    const interval = setInterval(poll, 5000);
    // First check after 3s
    const timeout = setTimeout(poll, 3000);

    return () => {
      cancelled = true;
      clearInterval(interval);
      clearTimeout(timeout);
    };
  }, [checkoutStep, pixPaymentId, restaurant]);


  const requestNotificationPermission = useCallback(() => {
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission();
    }
  }, []);

  useEffect(() => {
    if (successOrderId) requestNotificationPermission();
  }, [successOrderId, requestNotificationPermission]);

  useEffect(() => {
    if (!successOrderId) return;

    const handleOrderCompleted = () => {
      setOrderCompleted(true);
      toast.success("Pedido finalizado! 🎉", {
        description: currentUser && loyaltyPreview.length > 0
          ? "Seus pontos/recompensas foram creditados na sua conta!"
          : "Seu pedido foi concluído com sucesso.",
        duration: 8000,
      });
      if ('Notification' in window && Notification.permission === 'granted') {
        try {
          new Notification("Pedido finalizado! 🎉", {
            body: currentUser && loyaltyPreview.length > 0
              ? "Seus pontos/recompensas foram creditados na sua conta!"
              : "Seu pedido foi concluído com sucesso.",
            icon: restaurant?.logo_url || '/favicon.ico',
            tag: `order-${successOrderId}`,
          });
        } catch (e) {
          console.log('Notification not supported in this context');
        }
      }
    };

    // Realtime channel (works for authenticated users)
    const channel = supabase
      .channel(`order-status-${successOrderId}`)
      .on(
        'postgres_changes',
        { event: 'UPDATE', schema: 'public', table: 'orders', filter: `id=eq.${successOrderId}` },
        (payload: any) => {
          if (payload.new?.status === 'completed') handleOrderCompleted();
        }
      )
      .subscribe();

    // Polling fallback for anonymous users (every 15s)
    const pollInterval = setInterval(async () => {
      try {
        const { data } = await supabase.functions.invoke("get-order-tracking", {
          body: { order_id: successOrderId },
        });
        if (data?.order?.status === 'completed' && !orderCompleted) {
          handleOrderCompleted();
        }
      } catch {}
    }, 15000);

    return () => {
      supabase.removeChannel(channel);
      clearInterval(pollInterval);
    };
  }, [successOrderId, currentUser, loyaltyPreview, restaurant, orderCompleted]);



  // Fetch per-day schedules for this restaurant
  const [daySchedules, setDaySchedules] = useState<any[]>([]);
  useEffect(() => {
    if (!restaurant) return;
    supabase
      .from("restaurant_schedules" as any)
      .select("*")
      .eq("restaurant_id", restaurant.id)
      .then(({ data }) => setDaySchedules(data || []));
  }, [restaurant]);

  // Compute store open/closed status based on per-day schedule
  const { isStoreClosed, isClosingSoon, closingMessage, todaySchedule } = useMemo(() => {
    if (!restaurant) return { isStoreClosed: false, isClosingSoon: false, closingMessage: "", todaySchedule: null };
    if (restaurant.is_manually_closed) {
      return { isStoreClosed: true, isClosingSoon: false, closingMessage: "", todaySchedule: null };
    }

    const now = new Date();
    const dayMap: Record<number, string> = { 0: "dom", 1: "seg", 2: "ter", 3: "qua", 4: "qui", 5: "sex", 6: "sab" };
    const todayKey = dayMap[now.getDay()];

    // Check per-day schedule first
    const todaySched = daySchedules.find((s: any) => s.day_of_week === todayKey);

    if (todaySched) {
      // Per-day schedule exists
      if (!todaySched.is_open) {
        return { isStoreClosed: true, isClosingSoon: false, closingMessage: "", todaySchedule: todaySched };
      }
      if (todaySched.is_24h) {
        return { isStoreClosed: false, isClosingSoon: false, closingMessage: "", todaySchedule: todaySched };
      }
      if (todaySched.opening_time && todaySched.closing_time) {
        const nowMinutes = now.getHours() * 60 + now.getMinutes();
        const [openH, openM] = todaySched.opening_time.slice(0, 5).split(":").map(Number);
        const [closeH, closeM] = todaySched.closing_time.slice(0, 5).split(":").map(Number);
        const openMinutes = openH * 60 + openM;
        const closeMinutes = closeH * 60 + closeM;
        const isOvernight = closeMinutes <= openMinutes;
        let isOpen: boolean;
        let minutesUntilClose: number;
        if (isOvernight) {
          isOpen = nowMinutes >= openMinutes || nowMinutes < closeMinutes;
          minutesUntilClose = isOpen
            ? (nowMinutes >= openMinutes ? (1440 - nowMinutes) + closeMinutes : closeMinutes - nowMinutes)
            : 0;
        } else {
          isOpen = nowMinutes >= openMinutes && nowMinutes < closeMinutes;
          minutesUntilClose = isOpen ? closeMinutes - nowMinutes : 0;
        }
        if (!isOpen) return { isStoreClosed: true, isClosingSoon: false, closingMessage: "", todaySchedule: todaySched };
        if (minutesUntilClose > 0 && minutesUntilClose <= 30) {
          return { isStoreClosed: false, isClosingSoon: true, closingMessage: `A loja fecha em ${minutesUntilClose} minuto${minutesUntilClose !== 1 ? "s" : ""}`, todaySchedule: todaySched };
        }
        return { isStoreClosed: false, isClosingSoon: false, closingMessage: "", todaySchedule: todaySched };
      }
      return { isStoreClosed: false, isClosingSoon: false, closingMessage: "", todaySchedule: todaySched };
    }

    // Fallback to legacy fields
    const operatingDays = Array.isArray((restaurant as any).operating_days) ? (restaurant as any).operating_days as string[] : [];
    if (operatingDays.length > 0 && !operatingDays.includes(todayKey)) {
      return { isStoreClosed: true, isClosingSoon: false, closingMessage: "", todaySchedule: null };
    }

    if ((restaurant as any).is_24h) {
      return { isStoreClosed: false, isClosingSoon: false, closingMessage: "", todaySchedule: null };
    }

    if (restaurant.opening_time && restaurant.closing_time) {
      const nowMinutes = now.getHours() * 60 + now.getMinutes();
      const [openH, openM] = restaurant.opening_time.slice(0, 5).split(":").map(Number);
      const [closeH, closeM] = restaurant.closing_time.slice(0, 5).split(":").map(Number);
      const openMinutes = openH * 60 + openM;
      const closeMinutes = closeH * 60 + closeM;
      const isOvernight = closeMinutes <= openMinutes;
      let isOpen: boolean;
      let minutesUntilClose: number;
      if (isOvernight) {
        isOpen = nowMinutes >= openMinutes || nowMinutes < closeMinutes;
        minutesUntilClose = isOpen
          ? (nowMinutes >= openMinutes ? (1440 - nowMinutes) + closeMinutes : closeMinutes - nowMinutes)
          : 0;
      } else {
        isOpen = nowMinutes >= openMinutes && nowMinutes < closeMinutes;
        minutesUntilClose = isOpen ? closeMinutes - nowMinutes : 0;
      }
      if (!isOpen) return { isStoreClosed: true, isClosingSoon: false, closingMessage: "", todaySchedule: null };
      if (minutesUntilClose > 0 && minutesUntilClose <= 30) {
        return { isStoreClosed: false, isClosingSoon: true, closingMessage: `A loja fecha em ${minutesUntilClose} minuto${minutesUntilClose !== 1 ? "s" : ""}`, todaySchedule: null };
      }
    }

    return { isStoreClosed: false, isClosingSoon: false, closingMessage: "", todaySchedule: null };
  }, [restaurant, daySchedules]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[hsl(0,0%,98%)] flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-[hsl(0,100%,45%)]" />
      </div>
    );
  }

  if (!restaurant) {
    return (
      <div className="min-h-screen bg-[hsl(0,0%,98%)] flex items-center justify-center p-8 text-center">
        <div>
          <h2 className="font-bold text-xl mb-2 text-[hsl(0,0%,13%)]">Restaurante não encontrado</h2>
          <p className="text-[hsl(0,0%,45%)] text-sm">Verifique o link e tente novamente.</p>
        </div>
      </div>
    );
  }

  const primaryColor = restaurant.primary_color || "#f97316";
  const menuFont = (restaurant as any).menu_font || "Inter";
  const menuBg = (restaurant as any).menu_bg_color || "#ffffff";
  const menuText = (restaurant as any).menu_text_color || "#222222";
  const menuAccent = (restaurant as any).menu_accent_color || primaryColor;
  const menuLayout = (restaurant as any).menu_layout || "list";
  const menuCardStyle = (restaurant as any).menu_card_style || "rounded";
  const cardRadius = menuCardStyle === "sharp" ? "0" : menuCardStyle === "pill" ? "20px" : "12px";
  const fontLink = `https://fonts.googleapis.com/css2?family=${menuFont.replace(/ /g, "+")}:wght@400;500;600;700&display=swap`;

  return (
    <>
    <link rel="stylesheet" href={fontLink} />
    <div className="min-h-screen bg-[hsl(0,0%,95%)]" style={{ fontFamily: `'${menuFont}', -apple-system, BlinkMacSystemFont, sans-serif` }}>
    <div className="min-h-screen max-w-lg md:max-w-3xl lg:max-w-5xl xl:max-w-6xl mx-auto relative md:shadow-xl" style={{ backgroundColor: menuBg, color: menuText }}>
      <PwaInstallBanner accentColor={menuAccent} />
      {/* Restaurant Header / Cover */}
      <div className="relative" style={{ backgroundColor: menuBg }}>
        {restaurant.cover_url ? (
          <div className="w-full h-48 md:h-64 lg:h-80 overflow-hidden relative">
            <img src={restaurant.cover_url} alt="" className="w-full h-full object-cover" />
            <div className="absolute inset-0" style={{ background: `linear-gradient(to top, ${menuBg} 0%, transparent 60%)` }} />
          </div>
        ) : (
          <div className="w-full h-36 md:h-48 lg:h-56 relative" style={{ background: `linear-gradient(135deg, ${menuAccent}ee, ${primaryColor}dd, ${menuAccent}99)` }}>
            <div className="absolute inset-0 opacity-10" style={{ backgroundImage: `radial-gradient(circle at 20% 50%, white 1px, transparent 1px), radial-gradient(circle at 80% 20%, white 1px, transparent 1px)`, backgroundSize: '40px 40px' }} />
            <div className="absolute inset-0" style={{ background: `linear-gradient(to top, ${menuBg} 0%, transparent 50%)` }} />
          </div>
        )}

        {/* Restaurant Info Card */}
        <div className="px-4 md:px-8 pb-4 -mt-10 relative z-10">
          <div className="rounded-2xl p-4 md:p-6 border backdrop-blur-sm" style={{ backgroundColor: `${menuBg}f5`, borderColor: `${menuText}08`, boxShadow: `0 4px 24px ${menuText}08, 0 1px 3px ${menuText}05` }}>
            <div className="flex items-start gap-3.5 md:gap-5">
              {restaurant.logo_url ? (
                <img src={restaurant.logo_url} alt={restaurant.name} className="w-16 h-16 md:w-20 md:h-20 rounded-2xl object-cover border-2 shrink-0 shadow-sm" style={{ borderColor: `${menuText}10` }} />
              ) : (
                <div className="w-16 h-16 md:w-20 md:h-20 rounded-2xl flex items-center justify-center text-white text-xl md:text-2xl font-bold shrink-0 shadow-md" style={{ background: `linear-gradient(135deg, ${menuAccent}, ${primaryColor})` }}>
                  {restaurant.name.charAt(0)}
                </div>
              )}
              <div className="flex-1 min-w-0">
                <h1 className="font-bold text-lg md:text-xl lg:text-2xl leading-tight tracking-tight" style={{ color: menuText }}>{restaurant.name}</h1>
                {restaurant.description && (
                  <p className="text-xs md:text-sm mt-1 line-clamp-2 md:line-clamp-3 leading-relaxed" style={{ color: `${menuText}b0` }}>{restaurant.description}</p>
                )}
                <div className="flex flex-wrap items-center gap-2.5 mt-2.5">
                  {!isStoreClosed && (
                    <span className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full" style={{ backgroundColor: 'hsl(142, 71%, 95%)', color: 'hsl(142, 71%, 35%)' }}>
                      <span className="w-1.5 h-1.5 rounded-full bg-current animate-pulse" />
                      Aberto
                    </span>
                  )}
                  {isStoreClosed && (
                    <span className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full" style={{ backgroundColor: 'hsl(0, 80%, 95%)', color: 'hsl(0, 70%, 45%)' }}>
                      <span className="w-1.5 h-1.5 rounded-full bg-current" />
                      Fechado
                    </span>
                  )}
                  {storeRating && storeRating.count > 0 && (
                    <span className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full" style={{ backgroundColor: 'hsl(45, 100%, 94%)', color: 'hsl(40, 90%, 35%)' }}>
                      <Star className="h-3 w-3 fill-current" />
                      {storeRating.avg} ({storeRating.count})
                    </span>
                  )}
                  {(() => {
                    if (todaySchedule) {
                      if (todaySchedule.is_24h) return (
                        <span className="flex items-center gap-1 text-[11px]" style={{ color: `${menuText}90` }}>
                          <Clock className="h-3 w-3" />Aberto 24h
                        </span>
                      );
                      if (todaySchedule.opening_time && todaySchedule.closing_time) return (
                        <span className="flex items-center gap-1 text-[11px]" style={{ color: `${menuText}90` }}>
                          <Clock className="h-3 w-3" />
                          {todaySchedule.opening_time.slice(0, 5)} - {todaySchedule.closing_time.slice(0, 5)}
                        </span>
                      );
                    }
                    if ((restaurant as any).is_24h) return (
                      <span className="flex items-center gap-1 text-[11px]" style={{ color: `${menuText}90` }}>
                        <Clock className="h-3 w-3" />Aberto 24h
                      </span>
                    );
                    if (restaurant.opening_time && restaurant.closing_time) return (
                      <span className="flex items-center gap-1 text-[11px]" style={{ color: `${menuText}90` }}>
                        <Clock className="h-3 w-3" />
                        {restaurant.opening_time.slice(0, 5)} - {restaurant.closing_time.slice(0, 5)}
                      </span>
                    );
                    return null;
                  })()}
                  {restaurant.delivery_fee !== null && restaurant.delivery_fee !== undefined && (
                    <span className="flex items-center gap-1 text-[11px]" style={{ color: `${menuText}90` }}>
                      <MapPin className="h-3 w-3" />
                      {Number((restaurant as any).delivery_fee_per_km || 0) > 0
                        ? `A partir de ${Number((restaurant as any).delivery_free_until_km || 0) > 0 ? "grátis" : fmt(Number((restaurant as any).delivery_fee_per_km))}`
                        : Number(restaurant.delivery_fee) === 0 ? "Grátis" : fmt(Number(restaurant.delivery_fee))}
                    </span>
                  )}
                </div>
              </div>
            </div>

            {/* Action buttons row */}
            <div className="flex items-center gap-2 mt-3.5 pt-3.5" style={{ borderTop: `1px solid ${menuText}08` }}>
              <button
                onClick={() => setShowStoreInfo(true)}
                className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-medium transition-all active:scale-95"
                style={{ backgroundColor: `${menuText}10`, color: `${menuText}90` }}
              >
                <Info className="h-3.5 w-3.5" /> Sobre
              </button>
              <div className="flex-1" />
              {/* Cart button */}
              <button
                onClick={() => setShowCart(true)}
                className="relative w-10 h-10 rounded-xl flex items-center justify-center transition-all active:scale-95 shadow-sm"
                style={{ backgroundColor: cartCount > 0 ? menuAccent : `${menuText}06`, color: cartCount > 0 ? '#ffffff' : `${menuText}60` }}
              >
                <ShoppingCart className="h-4.5 w-4.5" />
                {cartCount > 0 && (
                  <span className="absolute -top-1.5 -right-1.5 min-w-[20px] h-[20px] rounded-full text-white text-[10px] font-bold flex items-center justify-center px-1 shadow-md" style={{ backgroundColor: 'hsl(0, 85%, 50%)' }}>
                    {cartCount}
                  </span>
                )}
              </button>
              {currentUser ? (
                <button onClick={() => navigate(`/minha-conta${restaurantSlug ? `?r=${restaurantSlug}` : ""}`)} className="w-10 h-10 rounded-xl flex items-center justify-center transition-all active:scale-95" style={{ backgroundColor: `${menuText}06`, color: `${menuText}60` }}>
                  <User className="h-4.5 w-4.5" />
                </button>
              ) : (
                <button
                  onClick={() => { setAuthMode("login"); setAuthError(""); setAuthModal(true); }}
                  className="h-10 px-4 rounded-xl flex items-center gap-1.5 text-xs font-bold text-white transition-all active:scale-95 shadow-sm"
                  style={{ backgroundColor: menuAccent }}
                >
                  <LogIn className="h-3.5 w-3.5" /> Entrar
                </button>
              )}
            </div>

            {/* Loyalty banner */}
            {loyaltyPrograms.length > 0 && (
              <div className="flex gap-2 mt-3 overflow-x-auto scrollbar-hide">
                {loyaltyPrograms.map((lp) => {
                  const cl = customerLoyalty.find(c => c.loyalty_program_id === lp.id);
                  return (
                    <div key={lp.id} className="shrink-0 flex items-center gap-1.5 px-3 py-2 rounded-xl border" style={{ backgroundColor: 'hsl(45, 100%, 97%)', borderColor: 'hsl(45, 100%, 90%)', color: 'hsl(35, 100%, 30%)' }}>
                      {lp.type === "points" && <Star className="h-3.5 w-3.5" />}
                      {lp.type === "stamp" && <Stamp className="h-3.5 w-3.5" />}
                      {lp.type === "cashback" && <CreditCard className="h-3.5 w-3.5" />}
                      <span className="text-[11px] font-semibold whitespace-nowrap">
                        {lp.type === "points" && (cl ? `${cl.points_balance} pts` : `${lp.points_per_real} pt/R$`)}
                        {lp.type === "stamp" && (cl ? `${cl.stamps_count}/${lp.stamps_required}` : `${lp.stamps_required} carimbos`)}
                        {lp.type === "cashback" && (cl ? `${fmt(cl.cashback_balance)}` : `${lp.cashback_percentage}% volta`)}
                      </span>
                    </div>
                  );
                })}
                {currentUser && loyaltyPrograms.length > 0 && (
                  <button
                    onClick={() => navigate(`/minha-conta${restaurantSlug ? `?r=${restaurantSlug}` : ""}`)}
                    className="shrink-0 flex items-center gap-1.5 px-3 py-2 rounded-xl border transition-all active:scale-95"
                    style={{ backgroundColor: 'hsl(270, 80%, 96%)', borderColor: 'hsl(270, 60%, 88%)', color: 'hsl(270, 60%, 40%)' }}
                  >
                    <Trophy className="h-3.5 w-3.5" />
                    <span className="text-[11px] font-semibold whitespace-nowrap">Recompensas</span>
                  </button>
                )}
                {availableCoupons.length > 0 && (
                  <button
                    onClick={() => setShowCouponsPopup(true)}
                    className="shrink-0 flex items-center gap-1.5 px-3 py-2 rounded-xl border transition-all active:scale-95 relative"
                    style={{ backgroundColor: 'hsl(0, 85%, 96%)', borderColor: 'hsl(0, 70%, 88%)', color: 'hsl(0, 75%, 45%)' }}
                  >
                    <Ticket className="h-3.5 w-3.5" />
                    <span className="text-[11px] font-semibold whitespace-nowrap">Cupons</span>
                    <span className="absolute -top-1 -right-1 min-w-[16px] h-[16px] rounded-full text-[9px] font-bold flex items-center justify-center px-0.5 text-white" style={{ backgroundColor: 'hsl(0, 85%, 50%)' }}>
                      {availableCoupons.length}
                    </span>
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Sticky Nav: Search + Category Tabs */}
      <div className="sticky top-0 z-40 backdrop-blur-xl border-b" style={{ backgroundColor: `${menuBg}e8`, borderColor: `${menuText}08` }}>
        {/* Search Bar */}
        <div className="px-4 md:px-8 pt-3 pb-2">
          <div className="relative md:max-w-xl lg:max-w-2xl">
            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4" style={{ color: `${menuText}40` }} />
            <input
              className="w-full h-11 pl-10 pr-10 text-sm outline-none transition-all border"
              style={{ backgroundColor: `${menuText}04`, color: menuText, borderRadius: '12px', borderColor: `${menuText}08` }}
              placeholder="O que você procura?"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            {searchQuery && (
              <button onClick={() => setSearchQuery("")} className="absolute right-3 top-1/2 -translate-y-1/2 w-6 h-6 rounded-full flex items-center justify-center" style={{ backgroundColor: `${menuText}10` }}>
                <X className="h-3.5 w-3.5" style={{ color: `${menuText}50` }} />
              </button>
            )}
          </div>
        </div>

        {/* Category Tabs */}
        {categoryTabs.length > 0 && (
          <div ref={tabsRef} className="flex gap-1.5 px-4 md:px-8 pb-3 overflow-x-auto scrollbar-hide">
            {categoryTabs.map((c) => (
              <button
                key={c.id}
                onClick={() => scrollToCategory(c.id)}
                className="shrink-0 px-4 py-2 text-[13px] font-semibold transition-all whitespace-nowrap border"
                style={{
                  backgroundColor: activeCat === c.id ? menuAccent : 'transparent',
                  color: activeCat === c.id ? "#ffffff" : menuText,
                  borderColor: activeCat === c.id ? menuAccent : `${menuText}20`,
                  borderRadius: menuCardStyle === "sharp" ? "6px" : menuCardStyle === "pill" ? "999px" : "10px",
                  boxShadow: activeCat === c.id ? `0 2px 8px ${menuAccent}30` : 'none',
                }}
              >
                {c.name}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Closed Banner */}
      {isStoreClosed && (
        <div className="mx-4 md:mx-8 mt-4 p-4 rounded-2xl bg-[hsl(0,90%,95%)] border border-[hsl(0,80%,85%)] flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-[hsl(0,80%,90%)] flex items-center justify-center shrink-0">
            <Clock className="h-5 w-5 text-[hsl(0,70%,45%)]" />
          </div>
          <div>
            <p className="font-semibold text-sm text-[hsl(0,70%,35%)]">Loja fechada no momento</p>
            <p className="text-xs text-[hsl(0,50%,45%)] mt-0.5">
              {todaySchedule && !todaySchedule.is_open
                ? "Não funciona hoje. Veja os horários na aba de informações."
                : todaySchedule?.opening_time && todaySchedule?.closing_time
                  ? `Horário de funcionamento hoje: ${todaySchedule.opening_time.slice(0, 5)} - ${todaySchedule.closing_time.slice(0, 5)}`
                  : restaurant.opening_time && restaurant.closing_time
                    ? `Horário de funcionamento: ${restaurant.opening_time.slice(0, 5)} - ${restaurant.closing_time.slice(0, 5)}`
                    : "Você pode visualizar o cardápio, mas não é possível fazer pedidos agora."}
            </p>
          </div>
        </div>
      )}

      {/* Closing Soon Warning */}
      {isClosingSoon && !isStoreClosed && (
        <div className="mx-4 md:mx-8 mt-4 p-3 rounded-2xl bg-[hsl(40,100%,95%)] border border-[hsl(40,80%,80%)] flex items-center gap-3">
          <div className="w-9 h-9 rounded-full bg-[hsl(40,90%,88%)] flex items-center justify-center shrink-0">
            <AlertTriangle className="h-4 w-4 text-[hsl(35,90%,40%)]" />
          </div>
          <div>
            <p className="font-semibold text-xs text-[hsl(35,80%,30%)]">{closingMessage}</p>
            <p className="text-[11px] text-[hsl(35,50%,45%)] mt-0.5">Finalize seu pedido antes do fechamento.</p>
          </div>
        </div>
      )}

      {/* AI Combo Suggestions */}
      {aiCombos.length > 0 && !isStoreClosed && (
        <div className="px-4 md:px-8 pt-5 pb-2">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ background: `linear-gradient(135deg, ${menuAccent}20, ${menuAccent}10)` }}>
              <Sparkles className="h-4 w-4" style={{ color: menuAccent }} />
            </div>
            <h2 className="font-bold text-[15px] tracking-tight" style={{ color: menuText }}>Combos sugeridos para você</h2>
          </div>
          <div className="flex gap-3 overflow-x-auto scrollbar-hide pb-2">
            {aiCombos.map((combo) => (
              <button
                key={combo.id}
                onClick={() => setShowComboModal(combo)}
                className="shrink-0 w-[200px] rounded-2xl border overflow-hidden text-left transition-all active:scale-[0.97]"
                style={{ borderColor: `${menuText}08`, backgroundColor: menuBg, boxShadow: `0 2px 12px ${menuText}06` }}
              >
                <div className="p-3 pb-0">
                  <div className="flex items-center gap-1.5 mb-1.5">
                    <Zap className="h-3.5 w-3.5" style={{ color: menuAccent }} />
                    <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full" style={{ backgroundColor: 'hsl(142, 71%, 93%)', color: 'hsl(142, 71%, 30%)' }}>
                      -{combo.discount_percent}%
                    </span>
                  </div>
                  <p className="font-bold text-[13px] leading-tight line-clamp-1" style={{ color: menuText }}>{combo.name}</p>
                  <p className="text-[11px] mt-0.5 line-clamp-2 leading-relaxed" style={{ color: `${menuText}60` }}>{combo.description}</p>
                </div>
                <div className="p-3 pt-2 flex items-center justify-between">
                  <div>
                    <span className="font-bold text-sm" style={{ color: menuAccent }}>{fmt(combo.combo_price)}</span>
                    <span className="text-[11px] line-through ml-1.5" style={{ color: `${menuText}35` }}>{fmt(combo.original_price)}</span>
                  </div>
                  <div className="w-7 h-7 rounded-lg flex items-center justify-center text-white" style={{ backgroundColor: menuAccent }}>
                    <Plus className="h-3.5 w-3.5" />
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Products grouped by category */}
      <div className="pb-28">
        {groupedProducts.length === 0 ? (
          <div className="text-center py-16 px-4">
            <p className="text-sm" style={{ color: `${menuText}60` }}>{searchQuery ? "Nenhum item encontrado" : "Nenhum item disponível"}</p>
          </div>
        ) : (
          groupedProducts.map((group) => (
            <div key={group.id} ref={(el) => { categoryRefs.current[group.id] = el; }}>
              <div className="px-4 md:px-8 pt-7 pb-3 flex items-center gap-3">
                <div className="w-1 h-6 rounded-full" style={{ backgroundColor: menuAccent }} />
                <h2 className="font-bold text-base md:text-lg tracking-tight" style={{ color: menuText }}>{group.name}</h2>
                <div className="flex-1 h-[1px]" style={{ backgroundColor: `${menuText}10` }} />
                <span className="text-xs font-medium px-2 py-0.5 rounded-full" style={{ color: `${menuText}50`, backgroundColor: `${menuText}05` }}>{group.items.length}</span>
              </div>

              {menuLayout === "grid" ? (
                <div className="px-4 md:px-8 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4">
                  {group.items.map((p) => {
                    const addons = productAddons[p.id];
                    const hasAddons = addons && addons.length > 0;
                    const itemInCart = cart.filter(c => c.productId === p.id).reduce((s, c) => s + c.qty, 0);
                    return (
                      <motion.button
                        key={p.id}
                        whileTap={!isStoreClosed ? { scale: 0.96 } : undefined}
                        onClick={() => !isStoreClosed && setDetailProduct(p)}
                        disabled={isStoreClosed}
                        className={`text-left overflow-hidden border transition-all ${isStoreClosed ? 'opacity-50 cursor-not-allowed' : 'hover:shadow-xl active:shadow-md'}`}
                        style={{ borderColor: `${menuText}08`, borderRadius: cardRadius, backgroundColor: menuBg, boxShadow: `0 2px 12px ${menuText}06` }}
                      >
                        <div className="w-full aspect-square overflow-hidden relative" style={{ backgroundColor: `${menuText}04` }}>
                          {p.image_url ? <img src={p.image_url} alt={p.name} className="w-full h-full object-cover transition-transform duration-500 hover:scale-110" /> : <div className="w-full h-full flex items-center justify-center text-3xl" style={{ background: `linear-gradient(135deg, ${menuText}04, ${menuText}08)` }}>🍽️</div>}
                          {p.is_featured && <span className="absolute top-2 left-2 text-[9px] font-bold px-2 py-0.5 rounded-full shadow-sm" style={{ backgroundColor: 'hsl(45, 100%, 50%)', color: '#222' }}>⭐ DESTAQUE</span>}
                          {currentUser && (
                            <button
                              onClick={(e) => { e.stopPropagation(); toggleFavorite(p.id); }}
                              className="absolute top-2 right-2 w-7 h-7 rounded-full flex items-center justify-center transition-all active:scale-90 backdrop-blur-sm"
                              style={{ backgroundColor: customerFavorites.has(p.id) ? 'hsl(0, 80%, 55%)' : `${menuText}20`, color: customerFavorites.has(p.id) ? 'white' : `${menuText}60` }}
                            >
                              <Heart className="h-3.5 w-3.5" fill={customerFavorites.has(p.id) ? 'currentColor' : 'none'} />
                            </button>
                          )}
                          {!isStoreClosed && (
                            <div className="absolute bottom-2 right-2 w-9 h-9 rounded-xl text-white flex items-center justify-center shadow-lg transition-transform active:scale-90" style={{ backgroundColor: menuAccent, boxShadow: `0 3px 10px ${menuAccent}40` }}>
                              {itemInCart > 0 ? <span className="text-xs font-bold">{itemInCart}</span> : <Plus className="h-4 w-4" />}
                            </div>
                          )}
                        </div>
                        <div className="p-3">
                          <p className="font-semibold text-[13px] leading-tight line-clamp-2" style={{ color: menuText }}>{p.name}</p>
                          <div className="flex items-center gap-1.5 mt-2">
                            {(p as any).discount_price && Number((p as any).discount_price) > 0 && Number((p as any).discount_price) < Number(p.price) ? (
                              <>
                                <span className="font-bold text-sm" style={{ color: menuAccent }}>{fmt(Number((p as any).discount_price))}</span>
                                <span className="text-[11px] line-through" style={{ color: `${menuText}40` }}>{fmt(Number(p.price))}</span>
                              </>
                            ) : (
                              <span className="font-bold text-sm" style={{ color: menuAccent }}>{fmt(Number(p.price))}</span>
                            )}
                            {hasAddons && <span className="text-[9px] px-1.5 py-0.5 rounded-full" style={{ color: `${menuText}55`, backgroundColor: `${menuText}06` }}>+ adicionais</span>}
                          </div>
                        </div>
                      </motion.button>
                    );
                  })}
                </div>
              ) : (
              <div className="px-4 md:px-8 space-y-2 md:grid md:grid-cols-2 lg:grid-cols-3 md:gap-3 md:space-y-0">
                  {group.items.map((p) => {
                    const addons = productAddons[p.id];
                    const hasAddons = addons && addons.length > 0;
                    const itemInCart = cart.filter(c => c.productId === p.id).reduce((s, c) => s + c.qty, 0);
                    return (
                      <motion.button
                        key={p.id}
                        whileTap={!isStoreClosed ? { scale: 0.98 } : undefined}
                        onClick={() => !isStoreClosed && setDetailProduct(p)}
                        disabled={isStoreClosed}
                        className={`w-full text-left flex gap-3 p-3 rounded-2xl border transition-all relative ${isStoreClosed ? 'opacity-50 cursor-not-allowed' : 'active:bg-[${menuText}02]'}`}
                        style={{ borderColor: `${menuText}06`, boxShadow: `0 1px 4px ${menuText}04` }}
                      >
                        <div className="flex-1 min-w-0 py-0.5">
                          <div className="flex items-center gap-2">
                            <p className="font-semibold text-sm leading-tight" style={{ color: menuText }}>{p.name}</p>
                            {itemInCart > 0 && (
                              <span className="shrink-0 min-w-[22px] h-[22px] rounded-lg text-white text-[10px] font-bold flex items-center justify-center px-1.5" style={{ backgroundColor: menuAccent }}>
                                {itemInCart}x
                              </span>
                            )}
                          </div>
                          {p.description && <p className="text-xs mt-1 line-clamp-2 leading-relaxed" style={{ color: `${menuText}65` }}>{p.description}</p>}
                          <div className="flex items-center gap-2 mt-2.5">
                            {(p as any).discount_price && Number((p as any).discount_price) > 0 && Number((p as any).discount_price) < Number(p.price) ? (
                              <>
                                <span className="font-bold text-[15px]" style={{ color: menuAccent }}>{fmt(Number((p as any).discount_price))}</span>
                                <span className="text-[12px] line-through" style={{ color: `${menuText}40` }}>{fmt(Number(p.price))}</span>
                                <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full" style={{ backgroundColor: 'hsl(0, 85%, 95%)', color: 'hsl(0, 75%, 45%)' }}>
                                  -{Math.round((1 - Number((p as any).discount_price) / Number(p.price)) * 100)}%
                                </span>
                              </>
                            ) : (
                              <span className="font-bold text-[15px]" style={{ color: menuAccent }}>{fmt(Number(p.price))}</span>
                            )}
                            {hasAddons && <span className="text-[10px] px-1.5 py-0.5 rounded-full" style={{ color: `${menuText}55`, backgroundColor: `${menuText}06` }}>+ adicionais</span>}
                          </div>
                        </div>
                        <div className="w-[100px] h-[100px] md:w-[120px] md:h-[120px] shrink-0 overflow-hidden relative" style={{ backgroundColor: `${menuText}04`, borderRadius: cardRadius }}>
                          {p.image_url ? <img src={p.image_url} alt={p.name} className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center text-2xl" style={{ background: `linear-gradient(135deg, ${menuText}04, ${menuText}08)` }}>🍽️</div>}
                          {p.is_featured && <span className="absolute top-1.5 left-1.5 text-[9px] font-bold px-1.5 py-0.5 rounded-full shadow-sm" style={{ backgroundColor: 'hsl(45, 100%, 50%)', color: '#222' }}>⭐</span>}
                          {currentUser && (
                            <button
                              onClick={(e) => { e.stopPropagation(); toggleFavorite(p.id); }}
                              className="absolute top-1.5 right-1.5 w-6 h-6 rounded-full flex items-center justify-center transition-all active:scale-90 backdrop-blur-sm z-10"
                              style={{ backgroundColor: customerFavorites.has(p.id) ? 'hsl(0, 80%, 55%)' : `${menuText}30`, color: customerFavorites.has(p.id) ? 'white' : `${menuText}70` }}
                            >
                              <Heart className="h-3 w-3" fill={customerFavorites.has(p.id) ? 'currentColor' : 'none'} />
                            </button>
                          )}
                          {!isStoreClosed && (
                            <div className="absolute bottom-1.5 right-1.5 w-8 h-8 rounded-xl text-white flex items-center justify-center shadow-md transition-transform active:scale-90" style={{ backgroundColor: menuAccent, boxShadow: `0 2px 8px ${menuAccent}35` }}>
                              <Plus className="h-4 w-4" />
                            </div>
                          )}
                        </div>
                      </motion.button>
                    );
                  })}
                </div>
              )}
            </div>
          ))
        )}
      </div>

      {/* Cart Bar — floating bottom */}
      {cartCount > 0 && !showCart && !showComanda && !addonModal && !isStoreClosed && (
        <motion.div initial={{ y: 100, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ type: "spring", damping: 22, stiffness: 280 }} className="fixed bottom-0 left-0 right-0 max-w-lg md:max-w-3xl lg:max-w-5xl xl:max-w-6xl mx-auto p-4 z-30">
          <motion.button
            whileTap={{ scale: 0.97 }}
            onClick={() => setShowCart(true)}
            className="w-full flex items-center justify-between p-4 rounded-2xl text-white backdrop-blur-sm"
            style={{ background: `linear-gradient(135deg, ${primaryColor}, ${menuAccent})`, boxShadow: `0 8px 32px ${primaryColor}45, 0 2px 8px ${primaryColor}20` }}
          >
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-xl bg-white/20 flex items-center justify-center backdrop-blur-sm">
                <ShoppingCart className="h-4.5 w-4.5" />
              </div>
              <div className="text-left">
                <span className="font-bold text-sm block leading-tight">Ver sacola</span>
                <span className="text-[11px] text-white/70">{cartCount} {cartCount === 1 ? 'item' : 'itens'}</span>
              </div>
            </div>
            <span className="font-bold text-lg tracking-tight">{fmt(cartTotal)}</span>
          </motion.button>
        </motion.div>
      )}

      {/* Comanda Bar — shown in table mode when items exist in tab */}
      {isTableMode && tabItemCount > 0 && cartCount === 0 && !showCart && !showComanda && !addonModal && (
        <motion.div initial={{ y: 100 }} animate={{ y: 0 }} className="fixed bottom-0 left-0 right-0 max-w-lg md:max-w-3xl lg:max-w-5xl xl:max-w-6xl mx-auto p-4 z-30">
          <button
            onClick={() => setShowComanda(true)}
            className="w-full flex items-center justify-between p-4 rounded-2xl text-[hsl(0,0%,100%)] shadow-[0_4px_20px_hsl(210,100%,35%,0.35)] bg-[hsl(210,80%,45%)]"
          >
            <div className="flex items-center gap-3">
              <div className="w-7 h-7 rounded-lg bg-[hsl(0,0%,100%)]/20 flex items-center justify-center">
                <Receipt className="h-4 w-4" />
              </div>
              <span className="font-semibold text-sm">Ver Comanda</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="bg-[hsl(0,0%,100%)]/20 text-xs font-bold px-2 py-0.5 rounded-full">{tabItemCount}</span>
              <span className="font-bold">{fmt(tabTotal)}</span>
            </div>
          </button>
        </motion.div>
      )}

      {/* Table mode indicator */}
      {isTableMode && tab && !showCart && !showComanda && (
        <div className="fixed top-0 left-0 right-0 max-w-lg md:max-w-3xl lg:max-w-5xl xl:max-w-6xl mx-auto z-[45]">
          <div className="mx-4 mt-2 flex items-center justify-between px-3 py-2 rounded-xl bg-[hsl(210,80%,95%)] border border-[hsl(210,80%,85%)] shadow-sm">
            <div className="flex items-center gap-2 text-[hsl(210,80%,35%)]">
              <Users className="h-4 w-4" />
              <span className="text-xs font-semibold">Mesa {mesaParam} • Comanda aberta</span>
            </div>
            {tabItemCount > 0 && (
              <button onClick={() => setShowComanda(true)} className="text-xs font-bold text-[hsl(210,80%,40%)] underline">
                {tabItemCount} itens
              </button>
            )}
          </div>
        </div>
      )}

      {/* Product Detail Modal */}
      <AnimatePresence>
        {detailProduct && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-50 bg-[hsl(0,0%,0%)]/70 backdrop-blur-sm flex items-end md:items-center md:justify-center" onClick={() => { setDetailProduct(null); setDetailQty(1); }}>
            <motion.div initial={{ y: "100%" }} animate={{ y: 0 }} exit={{ y: "100%" }} transition={{ type: "spring", damping: 28, stiffness: 300 }} className="w-full max-w-lg md:rounded-2xl bg-[hsl(0,0%,100%)] rounded-t-[28px] max-h-[92vh] flex flex-col shadow-[0_-8px_40px_hsl(0,0%,0%,0.25)]" onClick={(e) => e.stopPropagation()}>
              {/* Drag handle */}
              <div className="flex justify-center pt-3 pb-1 relative z-20">
                <div className="w-10 h-1.5 rounded-full bg-[hsl(0,0%,100%)]/60" />
              </div>

              {/* Close button */}
              <button onClick={() => { setDetailProduct(null); setDetailQty(1); }} className="absolute right-4 top-4 z-20 w-10 h-10 rounded-full bg-[hsl(0,0%,0%)]/30 backdrop-blur-md flex items-center justify-center text-[hsl(0,0%,100%)] transition-all active:scale-90 hover:bg-[hsl(0,0%,0%)]/50">
                <X className="h-5 w-5" />
              </button>

              {/* Large Image with gradient overlay */}
              <div className="w-full aspect-[4/3] overflow-hidden relative -mt-6">
                {detailProduct.image_url ? (
                  <>
                    <img src={detailProduct.image_url} alt={detailProduct.name} className="w-full h-full object-cover" />
                    <div className="absolute inset-0 bg-gradient-to-t from-[hsl(0,0%,0%)]/40 via-transparent to-transparent" />
                  </>
                ) : (
                  <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-[hsl(0,0%,97%)] to-[hsl(0,0%,92%)]">
                    <span className="text-7xl opacity-60">🍽️</span>
                  </div>
                )}
                {detailProduct.is_featured && (
                  <motion.span initial={{ x: -20, opacity: 0 }} animate={{ x: 0, opacity: 1 }} transition={{ delay: 0.2 }} className="absolute top-16 left-4 bg-gradient-to-r from-[hsl(45,100%,50%)] to-[hsl(38,100%,55%)] text-[hsl(0,0%,8%)] text-xs font-bold px-3.5 py-1.5 rounded-full shadow-[0_4px_12px_hsl(45,100%,50%,0.4)]">⭐ DESTAQUE</motion.span>
                )}
                {detailProduct.preparation_time_min && (
                  <motion.span initial={{ x: 20, opacity: 0 }} animate={{ x: 0, opacity: 1 }} transition={{ delay: 0.25 }} className="absolute bottom-3 right-4 bg-[hsl(0,0%,0%)]/50 backdrop-blur-md text-[hsl(0,0%,100%)] text-[11px] font-medium px-2.5 py-1 rounded-full flex items-center gap-1">
                    <Clock className="h-3 w-3" /> ~{detailProduct.preparation_time_min} min
                  </motion.span>
                )}
              </div>

              {/* Product Info */}
              <div className="flex-1 overflow-auto">
                <div className="p-5 space-y-4">
                  <div>
                    <motion.h3 initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="font-bold text-[22px] text-[hsl(0,0%,10%)] leading-tight tracking-tight">{detailProduct.name}</motion.h3>
                    {(detailProduct as any).discount_price && Number((detailProduct as any).discount_price) > 0 && Number((detailProduct as any).discount_price) < Number(detailProduct.price) ? (
                      <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }} className="flex items-center gap-2 mt-1.5">
                        <span className="text-xl font-extrabold" style={{ color: menuAccent }}>{fmt(Number((detailProduct as any).discount_price))}</span>
                        <span className="text-sm line-through text-[hsl(0,0%,55%)]">{fmt(Number(detailProduct.price))}</span>
                        <span className="text-xs font-bold px-2 py-0.5 rounded-full" style={{ backgroundColor: 'hsl(0, 85%, 95%)', color: 'hsl(0, 75%, 45%)' }}>
                          -{Math.round((1 - Number((detailProduct as any).discount_price) / Number(detailProduct.price)) * 100)}%
                        </span>
                      </motion.div>
                    ) : (
                      <motion.p initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }} className="text-xl font-extrabold mt-1.5" style={{ color: menuAccent }}>{fmt(Number(detailProduct.price))}</motion.p>
                    )}
                  </div>

                  {detailProduct.description && (
                    <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }} className="text-sm text-[hsl(0,0%,45%)] leading-relaxed">{detailProduct.description}</motion.p>
                  )}

                  {/* Addons preview grouped by category */}
                  {productAddons[detailProduct.id] && productAddons[detailProduct.id].length > 0 && (() => {
                    const allAddons = productAddons[detailProduct.id];
                    const categories = productAddonCategories[detailProduct.id] || [];
                    const uncategorized = allAddons.filter(a => !a.addon_category_id);

                    return (
                      <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.25 }} className="space-y-3">
                        <p className="text-xs font-bold text-[hsl(0,0%,30%)] uppercase tracking-wider">Personalize seu pedido</p>

                        {categories.map((cat) => {
                          const catAddons = allAddons.filter(a => a.addon_category_id === cat.id);
                          if (catAddons.length === 0) return null;
                          return (
                            <div key={cat.id} className="rounded-2xl border border-[hsl(0,0%,92%)] overflow-hidden">
                              <div className="px-4 py-2.5 bg-[hsl(0,0%,97%)] flex items-center justify-between">
                                <div>
                                  <p className="text-[13px] font-bold text-[hsl(0,0%,20%)]">{cat.name}</p>
                                  <p className="text-[10px] text-[hsl(0,0%,55%)] mt-0.5">
                                    {cat.is_required ? `Escolha de ${cat.min_select} até ${cat.max_select}` : `Até ${cat.max_select} opções`}
                                  </p>
                                </div>
                                {cat.is_required && (
                                  <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-[hsl(0,90%,95%)] text-[hsl(0,80%,45%)]">
                                    obrigatório
                                  </span>
                                )}
                              </div>
                              <div className="divide-y divide-[hsl(0,0%,95%)]">
                                {catAddons.map((addon) => (
                                  <div key={addon.id} className="flex items-center gap-3 px-4 py-2.5">
                                    {addon.image_url && (
                                      <img src={addon.image_url} alt={addon.name} className="w-8 h-8 rounded-lg object-cover shrink-0" />
                                    )}
                                    <span className="text-sm text-[hsl(0,0%,35%)] flex-1">{addon.name}</span>
                                    {addon.price > 0 && (
                                      <span className="text-sm font-semibold shrink-0" style={{ color: menuAccent }}>+ {fmt(addon.price)}</span>
                                    )}
                                  </div>
                                ))}
                              </div>
                            </div>
                          );
                        })}

                        {uncategorized.length > 0 && (
                          <div className="rounded-2xl border border-[hsl(0,0%,92%)] overflow-hidden">
                            <div className="px-4 py-2.5 bg-[hsl(0,0%,97%)]">
                              <p className="text-[13px] font-bold text-[hsl(0,0%,20%)]">Extras</p>
                            </div>
                            <div className="divide-y divide-[hsl(0,0%,95%)]">
                              {uncategorized.map((addon) => (
                                <div key={addon.id} className="flex items-center gap-3 px-4 py-2.5">
                                  {addon.image_url && (
                                    <img src={addon.image_url} alt={addon.name} className="w-8 h-8 rounded-lg object-cover shrink-0" />
                                  )}
                                  <span className="text-sm text-[hsl(0,0%,35%)] flex-1">{addon.name}</span>
                                  {addon.price > 0 && (
                                    <span className="text-sm font-semibold shrink-0" style={{ color: menuAccent }}>+ {fmt(addon.price)}</span>
                                  )}
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                        <p className="text-[11px] text-[hsl(0,0%,50%)] text-center mt-1 flex items-center justify-center gap-1">
                          <Info className="h-3 w-3" />
                          Clique em <span className="font-semibold">Adicionar</span> para selecionar suas opções
                        </p>
                      </motion.div>
                    );
                  })()}
                </div>
              </div>

              {/* Bottom action bar */}
              {!isStoreClosed && (
                <div className="p-4 border-t border-[hsl(0,0%,94%)] bg-[hsl(0,0%,100%)] flex items-center gap-3">
                  {/* Quantity selector */}
                  <div className="flex items-center gap-1 bg-[hsl(0,0%,96%)] rounded-2xl px-1.5 h-[52px]">
                    <button
                      onClick={() => setDetailQty(q => Math.max(1, q - 1))}
                      className="w-10 h-10 rounded-xl flex items-center justify-center text-[hsl(0,0%,40%)] hover:bg-[hsl(0,0%,90%)] transition-colors active:scale-90"
                    >
                      <Minus className="h-4 w-4" />
                    </button>
                    <span className="text-base font-bold text-[hsl(0,0%,10%)] w-8 text-center">{detailQty}</span>
                    <button
                      onClick={() => setDetailQty(q => q + 1)}
                      className="w-10 h-10 rounded-xl flex items-center justify-center text-[hsl(0,0%,40%)] hover:bg-[hsl(0,0%,90%)] transition-colors active:scale-90"
                    >
                      <Plus className="h-4 w-4" />
                    </button>
                  </div>

                  {/* Add to cart button */}
                  <button
                    onClick={() => {
                      const addons = productAddons[detailProduct.id];
                      if (addons && addons.length > 0) {
                        setAddonModal(detailProduct);
                        setSelectedAddons({});
                      } else {
                        for (let i = 0; i < detailQty; i++) {
                          addToCart(detailProduct, []);
                        }
                        toast.success(`${detailQty}x ${detailProduct.name} adicionado!`);
                      }
                      setDetailProduct(null);
                      setDetailQty(1);
                    }}
                    className="flex-1 h-[52px] rounded-2xl font-bold text-[15px] text-[hsl(0,0%,100%)] flex items-center justify-center gap-2.5 transition-all active:scale-[0.97] shadow-lg"
                    style={{ backgroundColor: primaryColor, boxShadow: `0 6px 20px ${primaryColor}50` }}
                  >
                    <ShoppingCart className="h-[18px] w-[18px]" />
                    Adicionar · {fmt(Number((detailProduct as any).discount_price && Number((detailProduct as any).discount_price) > 0 ? (detailProduct as any).discount_price : detailProduct.price) * detailQty)}
                  </button>
                </div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Combo Detail Modal */}
      <AnimatePresence>
        {showComboModal && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-50 bg-[hsl(0,0%,0%)]/70 backdrop-blur-sm flex items-end md:items-center md:justify-center" onClick={() => setShowComboModal(null)}>
            <motion.div initial={{ y: "100%" }} animate={{ y: 0 }} exit={{ y: "100%" }} transition={{ type: "spring", damping: 28, stiffness: 300 }} className="w-full max-w-lg md:rounded-2xl bg-[hsl(0,0%,100%)] rounded-t-[28px] max-h-[70vh] flex flex-col shadow-[0_-8px_40px_hsl(0,0%,0%,0.25)]" onClick={(e) => e.stopPropagation()}>
              <div className="flex justify-center pt-3 pb-1"><div className="w-10 h-1.5 rounded-full bg-[hsl(0,0%,85%)]" /></div>
              <div className="p-5 space-y-4">
                <div className="flex items-start justify-between">
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <Sparkles className="h-4 w-4" style={{ color: menuAccent }} />
                      <span className="text-xs font-bold px-2 py-0.5 rounded-full" style={{ backgroundColor: 'hsl(142, 71%, 93%)', color: 'hsl(142, 71%, 30%)' }}>
                        Economize {showComboModal.discount_percent}%
                      </span>
                    </div>
                    <h3 className="font-bold text-xl text-[hsl(0,0%,10%)]">{showComboModal.name}</h3>
                    <p className="text-sm text-[hsl(0,0%,55%)] mt-1">{showComboModal.description}</p>
                  </div>
                  <button onClick={() => setShowComboModal(null)} className="w-9 h-9 rounded-full bg-[hsl(0,0%,95%)] flex items-center justify-center">
                    <X className="h-4 w-4 text-[hsl(0,0%,45%)]" />
                  </button>
                </div>

                <div className="rounded-2xl border border-[hsl(0,0%,92%)] overflow-hidden">
                  <div className="px-4 py-2.5 bg-[hsl(0,0%,97%)]">
                    <p className="text-xs font-bold text-[hsl(0,0%,30%)] uppercase tracking-wider">Itens do combo</p>
                  </div>
                  <div className="divide-y divide-[hsl(0,0%,95%)]">
                    {showComboModal.items.map((item: any) => (
                      <div key={item.id} className="flex items-center justify-between px-4 py-3">
                        <span className="text-sm font-medium text-[hsl(0,0%,20%)]">{item.name}</span>
                        <div className="flex items-center gap-2">
                          {item.discount_price ? (
                            <span className="text-xs line-through text-[hsl(0,0%,60%)]">{fmt(item.original_price)}</span>
                          ) : (
                            <span className="text-xs text-[hsl(0,0%,60%)]">{fmt(item.original_price)}</span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="flex items-center justify-between py-3 px-4 rounded-2xl" style={{ backgroundColor: `${menuAccent}08` }}>
                  <div>
                    <p className="text-xs" style={{ color: `${menuText}55` }}>Valor original: <span className="line-through">{fmt(showComboModal.original_price)}</span></p>
                    <p className="font-bold text-lg" style={{ color: menuAccent }}>Combo: {fmt(showComboModal.combo_price)}</p>
                  </div>
                  <span className="text-sm font-bold px-3 py-1 rounded-full text-white" style={{ backgroundColor: 'hsl(142, 71%, 45%)' }}>
                    -{fmt(showComboModal.original_price - showComboModal.combo_price)}
                  </span>
                </div>

                <button
                  onClick={() => addComboToCart(showComboModal)}
                  className="w-full h-[52px] rounded-2xl font-bold text-[15px] text-white flex items-center justify-center gap-2.5 transition-all active:scale-[0.97] shadow-lg"
                  style={{ backgroundColor: primaryColor, boxShadow: `0 6px 20px ${primaryColor}50` }}
                >
                  <ShoppingCart className="h-[18px] w-[18px]" />
                  Adicionar combo · {fmt(showComboModal.combo_price)}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Addon Selection Modal — iFood style */}
      <AnimatePresence>
        {addonModal && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-50 bg-[hsl(0,0%,0%)]/70 backdrop-blur-sm flex items-end md:items-center md:justify-center" onClick={() => setAddonModal(null)}>
            <motion.div initial={{ y: "100%" }} animate={{ y: 0 }} exit={{ y: "100%" }} transition={{ type: "spring", damping: 25 }} className="w-full max-w-lg md:rounded-2xl bg-[hsl(0,0%,100%)] rounded-t-[28px] max-h-[85vh] flex flex-col shadow-[0_-8px_40px_hsl(0,0%,0%,0.25)]" onClick={(e) => e.stopPropagation()}>
              {/* Product header */}
              {addonModal.image_url && (
                <div className="w-full h-44 overflow-hidden rounded-t-[28px] relative">
                  <img src={addonModal.image_url} alt={addonModal.name} className="w-full h-full object-cover" />
                  <div className="absolute inset-0 bg-gradient-to-t from-[hsl(0,0%,0%)]/40 via-transparent to-transparent" />
                </div>
              )}
              <div className="p-5 border-b border-[hsl(0,0%,94%)]">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <h3 className="font-bold text-lg text-[hsl(0,0%,10%)]">{addonModal.name}</h3>
                    {addonModal.description && <p className="text-xs text-[hsl(0,0%,55%)] mt-1 leading-relaxed">{addonModal.description}</p>}
                    <p className="text-base font-extrabold mt-2" style={{ color: menuAccent }}>{fmt(Number(addonModal.price))}</p>
                  </div>
                  <button onClick={() => setAddonModal(null)} className="w-9 h-9 rounded-full bg-[hsl(0,0%,95%)] flex items-center justify-center transition-colors hover:bg-[hsl(0,0%,90%)]">
                    <X className="h-4 w-4 text-[hsl(0,0%,45%)]" />
                  </button>
                </div>
              </div>
              <div className="flex-1 overflow-auto p-5 space-y-4">
                {(() => {
                  const allAddons = productAddons[addonModal.id] || [];
                  const categories = productAddonCategories[addonModal.id] || [];
                  const uncategorized = allAddons.filter(a => !a.addon_category_id);
                  
                  const renderAddonItem = (addon: Addon, maxQtyOverride?: number, categoryId?: string) => {
                    const qty = selectedAddons[addon.id] || 0;
                    const effectiveMax = maxQtyOverride ?? addon.max_qty;
                    // Check if this category already hit its max (for disabling)
                    let categoryAtMax = false;
                    if (categoryId && addonModal) {
                      const cats = productAddonCategories[addonModal.id] || [];
                      const cat = cats.find(c => c.id === categoryId);
                      if (cat) {
                        const catAddons = (productAddons[addonModal.id] || []).filter(a => a.addon_category_id === categoryId);
                        const catTotal = catAddons.reduce((s, a) => s + (selectedAddons[a.id] || 0), 0);
                        categoryAtMax = catTotal >= cat.max_select;
                      }
                    }
                    return (
                      <div key={addon.id} className="flex items-center justify-between py-3 border-b border-[hsl(0,0%,96%)] last:border-0">
                        <div className="flex items-center gap-3 flex-1 min-w-0">
                          {addon.image_url && (
                            <img src={addon.image_url} alt={addon.name} className="w-10 h-10 rounded-lg object-cover shrink-0" />
                          )}
                          <div className="min-w-0">
                            <p className="text-sm font-medium text-[hsl(0,0%,13%)] truncate">{addon.name}</p>
                            {addon.price > 0 && (
                              <span className="text-xs font-semibold" style={{ color: menuAccent }}>+ {fmt(addon.price)}</span>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          {effectiveMax === 1 ? (
                            <button onClick={() => updateAddonQty(addon.id, qty > 0 ? -1 : 1, effectiveMax, categoryId)} disabled={qty === 0 && categoryAtMax} className={`w-9 h-9 rounded-full border-2 flex items-center justify-center transition-all active:scale-90 ${qty > 0 ? "text-[hsl(0,0%,100%)] shadow-md" : qty === 0 && categoryAtMax ? "border-[hsl(0,0%,90%)] text-[hsl(0,0%,90%)] cursor-not-allowed" : "border-[hsl(0,0%,82%)] text-[hsl(0,0%,82%)]"}`} style={qty > 0 ? { borderColor: menuAccent, backgroundColor: menuAccent } : {}}>
                              {qty > 0 ? <Check className="h-4 w-4" /> : <Plus className="h-4 w-4" />}
                            </button>
                          ) : (
                            <div className="flex items-center gap-1.5 bg-[hsl(0,0%,96%)] rounded-xl px-1 py-1">
                              <button onClick={() => updateAddonQty(addon.id, -1, effectiveMax, categoryId)} className="w-8 h-8 rounded-lg flex items-center justify-center text-[hsl(0,0%,50%)] hover:bg-[hsl(0,0%,90%)] transition-colors disabled:opacity-30" disabled={qty <= 0}><Minus className="h-3.5 w-3.5" /></button>
                              <span className="text-sm font-bold w-5 text-center text-[hsl(0,0%,13%)]">{qty}</span>
                              <button onClick={() => updateAddonQty(addon.id, 1, effectiveMax, categoryId)} className="w-8 h-8 rounded-lg text-[hsl(0,0%,100%)] flex items-center justify-center transition-all active:scale-90 disabled:opacity-30" style={{ backgroundColor: menuAccent }} disabled={qty >= effectiveMax || categoryAtMax}><Plus className="h-3.5 w-3.5" /></button>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  };

                  return (
                    <>
                      {categories.map((cat) => {
                        const catAddons = allAddons.filter(a => a.addon_category_id === cat.id);
                        if (catAddons.length === 0) return null;
                        const totalSelected = catAddons.reduce((s, a) => s + (selectedAddons[a.id] || 0), 0);
                        const isFulfilled = !cat.is_required || totalSelected >= cat.min_select;
                        return (
                          <div key={cat.id}>
                            <div className="flex items-center justify-between mb-1">
                              <div>
                                <h4 className="text-xs font-bold text-[hsl(0,0%,25%)] uppercase tracking-wider">{cat.name}</h4>
                                <p className="text-[10px] text-[hsl(0,0%,55%)] mt-0.5">
                                  {cat.is_required ? `Escolha de ${cat.min_select} até ${cat.max_select}` : `Escolha até ${cat.max_select}`}
                                </p>
                              </div>
                              {cat.is_required && (
                                <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${isFulfilled ? "bg-[hsl(140,60%,93%)] text-[hsl(140,60%,30%)]" : "bg-[hsl(0,90%,95%)] text-[hsl(0,80%,45%)]"}`}>
                                  {isFulfilled ? "✓" : "obrigatório"}
                                </span>
                              )}
                            </div>
                            {catAddons.map(addon => renderAddonItem(addon, cat.max_select, cat.id))}
                          </div>
                        );
                      })}
                      {uncategorized.length > 0 && (
                        <div>
                          <h4 className="text-xs font-bold text-[hsl(0,0%,30%)] uppercase tracking-wider mb-2">Personalize seu pedido</h4>
                          {uncategorized.map(addon => renderAddonItem(addon))}
                        </div>
                      )}
                    </>
                  );
                })()}
              </div>
              <div className="p-4 border-t border-[hsl(0,0%,94%)] bg-[hsl(0,0%,100%)]">
                {(() => {
                  const allAddons = productAddons[addonModal.id] || [];
                  const categories = productAddonCategories[addonModal.id] || [];
                  const addonTotal = allAddons.reduce((s, a) => s + (selectedAddons[a.id] || 0) * a.price, 0);
                  
                  // Check category requirements
                  let hasMinError = false;
                  for (const cat of categories) {
                    if (cat.is_required) {
                      const catAddons = allAddons.filter(a => a.addon_category_id === cat.id);
                      const totalSelected = catAddons.reduce((s, a) => s + (selectedAddons[a.id] || 0), 0);
                      if (totalSelected < cat.min_select) { hasMinError = true; break; }
                    }
                  }
                  // Also check legacy uncategorized
                  if (!hasMinError) {
                    const uncategorized = allAddons.filter(a => !a.addon_category_id);
                    hasMinError = uncategorized.some(a => (selectedAddons[a.id] || 0) < a.min_qty);
                  }
                  
                  return (
                    <button
                      onClick={confirmAddons}
                      disabled={hasMinError}
                      className="w-full h-[52px] rounded-2xl font-bold text-[15px] text-[hsl(0,0%,100%)] disabled:opacity-40 transition-all active:scale-[0.97] shadow-lg"
                      style={{ backgroundColor: primaryColor, boxShadow: hasMinError ? 'none' : `0 6px 20px ${primaryColor}50` }}
                    >
                      Adicionar · {fmt(Number(addonModal.price) + addonTotal)}
                    </button>
                  );
                })()}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Auth Modal — iFood style */}
      <AnimatePresence>
        {authModal && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-50 bg-[hsl(0,0%,0%)]/50 flex items-end md:items-center md:justify-center" onClick={() => setAuthModal(false)}>
            <motion.div initial={{ y: "100%" }} animate={{ y: 0 }} exit={{ y: "100%" }} transition={{ type: "spring", damping: 25 }} className="w-full max-w-lg md:rounded-2xl bg-[hsl(0,0%,100%)] rounded-t-3xl max-h-[85vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
              <div className="p-4 border-b border-[hsl(0,0%,94%)] flex items-center justify-between">
                <h3 className="font-bold text-lg text-[hsl(0,0%,13%)]">{authMode === "login" ? "Entrar" : "Criar conta"}</h3>
                <button onClick={() => setAuthModal(false)} className="w-8 h-8 rounded-full bg-[hsl(0,0%,96%)] flex items-center justify-center">
                  <X className="h-4 w-4 text-[hsl(0,0%,45%)]" />
                </button>
              </div>
              <div className="flex-1 overflow-auto p-4 space-y-4">
                {loyaltyPrograms.length > 0 && (
                  <div className="rounded-xl bg-[hsl(45,100%,96%)] p-3 border border-[hsl(45,100%,88%)]">
                    <p className="text-xs font-bold text-[hsl(35,100%,35%)] flex items-center gap-1 mb-1"><Gift className="h-3.5 w-3.5" /> Cadastre-se e aproveite!</p>
                    {loyaltyPrograms.map((lp) => (
                      <p key={lp.id} className="text-[11px] text-[hsl(0,0%,50%)]">
                        {lp.type === "points" && `⭐ ${lp.name} — Ganhe ${lp.points_per_real} ponto a cada R$ 1`}
                        {lp.type === "stamp" && `🎫 ${lp.name} — Compre ${lp.stamps_required}, ganhe ${lp.stamp_reward_description}`}
                        {lp.type === "cashback" && `💰 ${lp.name} — ${lp.cashback_percentage}% de volta`}
                      </p>
                    ))}
                  </div>
                )}

                {authMode === "register" && (
                  <>
                    <input className="w-full h-12 rounded-xl bg-[hsl(0,0%,96%)] px-4 text-sm text-[hsl(0,0%,13%)] placeholder:text-[hsl(0,0%,60%)] outline-none focus:ring-2 focus:ring-[hsl(0,100%,45%)]/20" placeholder="Nome completo *" value={authName} onChange={(e) => setAuthName(e.target.value)} />
                    <input className="w-full h-12 rounded-xl bg-[hsl(0,0%,96%)] px-4 text-sm text-[hsl(0,0%,13%)] placeholder:text-[hsl(0,0%,60%)] outline-none focus:ring-2 focus:ring-[hsl(0,100%,45%)]/20" placeholder="WhatsApp / Telefone" value={authPhone} onChange={(e) => setAuthPhone(e.target.value)} />
                  </>
                )}
                <input className="w-full h-12 rounded-xl bg-[hsl(0,0%,96%)] px-4 text-sm text-[hsl(0,0%,13%)] placeholder:text-[hsl(0,0%,60%)] outline-none focus:ring-2 focus:ring-[hsl(0,100%,45%)]/20" placeholder="E-mail *" type="email" value={authEmail} onChange={(e) => setAuthEmail(e.target.value)} />
                <input className="w-full h-12 rounded-xl bg-[hsl(0,0%,96%)] px-4 text-sm text-[hsl(0,0%,13%)] placeholder:text-[hsl(0,0%,60%)] outline-none focus:ring-2 focus:ring-[hsl(0,100%,45%)]/20" placeholder="Senha *" type="password" value={authPassword} onChange={(e) => setAuthPassword(e.target.value)} />

                {authError && <p className="text-xs text-[hsl(0,72%,51%)] text-center">{authError}</p>}

                <button
                  onClick={authMode === "login" ? handleLogin : handleRegister}
                  disabled={authLoading || !authEmail || !authPassword || (authMode === "register" && !authName)}
                  className="w-full h-14 rounded-xl font-bold text-base text-[hsl(0,0%,100%)] disabled:opacity-40 transition-opacity"
                  style={{ backgroundColor: primaryColor }}
                >
                  {authLoading ? <Loader2 className="h-5 w-5 animate-spin mx-auto" /> : authMode === "login" ? "Entrar" : "Criar conta"}
                </button>

                <button onClick={() => { setAuthMode(authMode === "login" ? "register" : "login"); setAuthError(""); }} className="w-full text-center text-sm text-[hsl(0,100%,45%)] font-semibold py-2">
                  {authMode === "login" ? "Criar uma conta" : "Já tenho conta"}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Cart / Checkout Modal — iFood style */}
      <AnimatePresence>
        {showCart && (
          <>
          {/* Desktop backdrop */}
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="hidden md:block fixed inset-0 z-[49] bg-[hsl(0,0%,0%)]/50 backdrop-blur-sm" onClick={resetCheckout} />
          <motion.div initial={{ y: "100%" }} animate={{ y: 0 }} exit={{ y: "100%" }} transition={{ type: "spring", damping: 25 }} className="fixed inset-0 z-50 bg-[hsl(0,0%,100%)] flex flex-col md:left-1/2 md:right-auto md:top-6 md:bottom-6 md:-translate-x-1/2 md:translate-y-0 md:w-full md:max-w-xl md:rounded-2xl md:shadow-2xl md:border md:border-[hsl(0,0%,90%)]">
            {/* Header */}
            <div className="flex items-center justify-between px-4 py-3.5 border-b border-[hsl(0,0%,94%)] bg-[hsl(0,0%,100%)]">
              {(checkoutStep === "form" || checkoutStep === "auth" || checkoutStep === "review" || checkoutStep === "mp_payment" || checkoutStep === "mp_pix") ? (
                <button onClick={() => {
                  if (checkoutStep === "mp_payment") {
                    if (mpBrickRef.current) { try { mpBrickRef.current.unmount(); } catch (e) {} mpBrickRef.current = null; }
                    setMpError("");
                    setCheckoutStep("form");
                  } else if (checkoutStep === "mp_pix") {
                    setPixPaymentId(null);
                    setPixQrCode("");
                    setPixQrCodeBase64("");
                    setMpError("");
                    setCheckoutStep("form");
                  } else if (checkoutStep === "review") {
                    setCheckoutStep("form");
                  } else {
                    setCheckoutStep("cart");
                  }
                }} className="flex items-center gap-1.5 text-sm font-medium text-[hsl(0,0%,40%)] transition-colors hover:text-[hsl(0,0%,20%)]">
                  <ArrowLeft className="h-4 w-4" /> Voltar
                </button>
              ) : <div />}
              <h2 className="font-bold text-lg text-[hsl(0,0%,10%)]">
                {checkoutStep === "cart" ? "Sacola" : checkoutStep === "auth" ? "Identificação" : (checkoutStep === "mp_payment" || checkoutStep === "mp_pix") ? "Pagamento" : checkoutStep === "form" ? "Finalizar pedido" : checkoutStep === "review" ? "Revise o pedido" : checkoutStep === "rating" ? "Avalie seu pedido" : "Pedido enviado"}
              </h2>
              <button onClick={resetCheckout} className="w-9 h-9 rounded-full bg-[hsl(0,0%,95%)] flex items-center justify-center transition-colors hover:bg-[hsl(0,0%,90%)]">
                <X className="h-4 w-4 text-[hsl(0,0%,45%)]" />
              </button>
            </div>

            {/* Cart Step */}
            {checkoutStep === "cart" && (
              <>
                <div className="flex-1 min-h-0 overflow-auto">
                  {cart.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-20 px-4">
                      <div className="w-20 h-20 rounded-full bg-[hsl(0,0%,96%)] flex items-center justify-center mb-5">
                        <ShoppingCart className="h-9 w-9 text-[hsl(0,0%,75%)]" />
                      </div>
                      <p className="text-[hsl(0,0%,40%)] font-semibold text-base">Sua sacola está vazia</p>
                      <p className="text-sm text-[hsl(0,0%,60%)] mt-1">Adicione itens do cardápio para continuar</p>
                    </div>
                  ) : (
                    <div className="p-4 space-y-3">
                      {cart.map((item, idx) => {
                        const addonTotal = item.addons.reduce((a, ad) => a + ad.price * ad.qty, 0);
                        return (
                          <motion.div key={item.cartId} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: idx * 0.05 }} className="rounded-2xl border border-[hsl(0,0%,93%)] bg-[hsl(0,0%,100%)] p-4 shadow-[0_1px_4px_hsl(0,0%,0%,0.04)]">
                            <div className="flex items-start justify-between gap-3">
                              <div className="flex-1 min-w-0">
                                <p className="font-semibold text-sm text-[hsl(0,0%,10%)]">{item.name}</p>
                                {item.addons.length > 0 && (() => {
                                  // Group addons by category
                                  const grouped: Record<string, typeof item.addons> = {};
                                  item.addons.forEach(ad => {
                                    const cat = ad.categoryName || "";
                                    if (!grouped[cat]) grouped[cat] = [];
                                    grouped[cat].push(ad);
                                  });
                                  return (
                                    <div className="mt-1 space-y-1">
                                      {Object.entries(grouped).map(([cat, ads]) => (
                                        <div key={cat}>
                                          {cat && <p className="text-[10px] font-semibold text-[hsl(0,0%,40%)] uppercase tracking-wider mt-1">{cat}:</p>}
                                          {ads.map(ad => (
                                            <p key={ad.id} className="text-[11px] text-[hsl(0,0%,55%)] flex items-center gap-1">
                                              <span className="w-1 h-1 rounded-full bg-[hsl(0,0%,70%)]" />
                                              {ad.name}{ad.qty > 1 ? ` (x${ad.qty})` : ""}
                                            </p>
                                          ))}
                                        </div>
                                      ))}
                                    </div>
                                  );
                                })()}
                                <p className="text-sm font-bold mt-2" style={{ color: menuAccent }}>{fmt((item.price + addonTotal) * item.qty)}</p>
                              </div>
                              <div className="flex items-center gap-1 bg-[hsl(0,0%,96%)] rounded-xl px-1 py-1">
                                <button onClick={() => updateCartQty(item.cartId, -1)} className="w-8 h-8 rounded-lg flex items-center justify-center transition-colors hover:bg-[hsl(0,0%,90%)]" style={{ color: menuAccent }}><Minus className="h-4 w-4" /></button>
                                <span className="text-sm font-bold w-5 text-center text-[hsl(0,0%,10%)]">{item.qty}</span>
                                <button onClick={() => updateCartQty(item.cartId, 1)} className="w-8 h-8 rounded-lg flex items-center justify-center transition-colors hover:bg-[hsl(0,0%,90%)]" style={{ color: menuAccent }}><Plus className="h-4 w-4" /></button>
                              </div>
                            </div>
                          </motion.div>
                        );
                      })}
                    </div>
                  )}
                </div>
                {cart.length > 0 && (
                  <div className="p-4 border-t border-[hsl(0,0%,94%)] bg-[hsl(0,0%,100%)]">
                    <div className="flex justify-between mb-3.5">
                      <span className="text-sm text-[hsl(0,0%,45%)]">{cartCount} {cartCount === 1 ? 'item' : 'itens'}</span>
                      <span className="font-bold text-lg text-[hsl(0,0%,10%)]">{fmt(cartTotal)}</span>
                    </div>
                    <button
                      onClick={proceedToCheckout}
                      disabled={submitting}
                      className="w-full h-[52px] rounded-2xl font-bold text-[15px] text-[hsl(0,0%,100%)] transition-all active:scale-[0.97] disabled:opacity-60 shadow-lg"
                      style={{ backgroundColor: isTableMode ? "hsl(210,80%,45%)" : primaryColor, boxShadow: `0 6px 20px ${isTableMode ? "hsl(210,80%,45%,0.4)" : `${primaryColor}50`}` }}
                    >
                      {submitting ? <Loader2 className="h-5 w-5 animate-spin mx-auto" /> : isTableMode ? "Adicionar à comanda" : "Continuar"}
                    </button>
                  </div>
                )}
              </>
            )}

            {/* Auth Step */}
            {checkoutStep === "auth" && (
              <div className="flex-1 min-h-0 overflow-auto p-4 space-y-4">
                <p className="text-sm text-[hsl(0,0%,50%)] text-center">Faça login para acumular pontos e usar cupons</p>

                {loyaltyPrograms.length > 0 && (
                  <div className="rounded-xl bg-[hsl(45,100%,96%)] border border-[hsl(45,100%,88%)] p-4 space-y-2">
                    <p className="text-sm font-bold text-[hsl(35,100%,30%)] flex items-center gap-2"><Crown className="h-4 w-4" /> Vantagens de ter conta</p>
                    {loyaltyPrograms.map((lp) => (
                      <p key={lp.id} className="text-xs text-[hsl(0,0%,50%)]">
                        {lp.type === "points" && `⭐ ${lp.name} — Acumule pontos e troque por descontos`}
                        {lp.type === "stamp" && `🎫 ${lp.name} — Complete ${lp.stamps_required} carimbos e ganhe ${lp.stamp_reward_description}`}
                        {lp.type === "cashback" && `💰 ${lp.name} — Receba ${lp.cashback_percentage}% de volta`}
                      </p>
                    ))}
                  </div>
                )}

                <button
                  onClick={() => { setAuthMode("login"); setAuthError(""); setAuthModal(true); }}
                  className="w-full h-14 rounded-xl font-bold text-base text-[hsl(0,0%,100%)] flex items-center justify-center gap-2"
                  style={{ backgroundColor: primaryColor }}
                >
                  <LogIn className="h-5 w-5" /> Entrar
                </button>
                <button
                  onClick={() => { setAuthMode("register"); setAuthError(""); setAuthModal(true); }}
                  className="w-full h-14 rounded-xl font-bold text-base border-2 border-[hsl(0,0%,88%)] text-[hsl(0,0%,30%)] flex items-center justify-center gap-2"
                >
                  <User className="h-5 w-5" /> Criar uma conta
                </button>
                <button onClick={() => setCheckoutStep("form")} className="w-full text-center text-sm text-[hsl(0,0%,50%)] py-3">
                  Continuar sem conta →
                </button>
              </div>
            )}

            {/* Checkout Form Step */}
            {checkoutStep === "form" && (
              <>
                <div className="flex-1 min-h-0 overflow-auto p-4 space-y-5">
                  {/* Customer Info */}
                  <div className="space-y-3">
                    <h3 className="text-sm font-bold text-[hsl(0,0%,10%)] flex items-center gap-2"><User className="h-4 w-4" style={{ color: menuAccent }} /> Seus dados</h3>
                    <input className="w-full h-12 rounded-2xl bg-[hsl(0,0%,96%)] px-4 text-sm text-[hsl(0,0%,13%)] placeholder:text-[hsl(0,0%,60%)] outline-none focus:ring-2 focus:ring-[hsl(0,100%,45%)]/20 transition-shadow" placeholder="Seu nome *" value={customerName} onChange={(e) => setCustomerName(e.target.value)} />
                    <input className="w-full h-12 rounded-2xl bg-[hsl(0,0%,96%)] px-4 text-sm text-[hsl(0,0%,13%)] placeholder:text-[hsl(0,0%,60%)] outline-none focus:ring-2 focus:ring-[hsl(0,100%,45%)]/20 transition-shadow" placeholder="WhatsApp / Telefone" value={customerPhone} onChange={(e) => setCustomerPhone(e.target.value)} />
                    <div className="flex items-start gap-2 px-1 py-1.5 rounded-xl bg-[hsl(142,60%,95%)] border border-[hsl(142,50%,85%)]">
                      <Bell className="h-4 w-4 text-[hsl(142,60%,35%)] shrink-0 mt-0.5" />
                      <p className="text-[11px] text-[hsl(142,40%,30%)] leading-relaxed">
                        📲 Informe seu WhatsApp e receba <strong>atualizações em tempo real</strong> sobre o status do seu pedido diretamente no seu celular!
                      </p>
                    </div>
                  </div>

                  {/* Order Type */}
                  <div className="space-y-3">
                    <h3 className="text-sm font-bold text-[hsl(0,0%,13%)]">Tipo do pedido</h3>
                    <div className="grid grid-cols-3 gap-2">
                      {([
                        { value: "dine_in", label: "No local", icon: "🍽️" },
                        { value: "takeout", label: "Retirada", icon: "🥡" },
                        { value: "delivery", label: "Entrega", icon: "🛵" },
                      ] as const).map((t) => (
                        <button key={t.value} onClick={() => setOrderType(t.value)} className={`p-3 rounded-xl border-2 text-center text-sm font-medium transition-all ${orderType === t.value ? "border-[hsl(0,100%,45%)] bg-[hsl(0,100%,97%)] text-[hsl(0,100%,45%)]" : "border-[hsl(0,0%,92%)] text-[hsl(0,0%,50%)]"}`}>
                          <span className="text-lg block mb-1">{t.icon}</span>{t.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  {orderType === "dine_in" && (
                    <input className="w-full h-12 rounded-xl bg-[hsl(0,0%,96%)] px-4 text-sm text-[hsl(0,0%,13%)] placeholder:text-[hsl(0,0%,60%)] outline-none focus:ring-2 focus:ring-[hsl(0,100%,45%)]/20" placeholder="Número da mesa" type="number" value={tableNumber} onChange={(e) => setTableNumber(e.target.value)} />
                  )}
                  {orderType === "delivery" && (
                    <DeliveryAddressForm
                      restaurant={restaurant}
                      primaryColor={primaryColor}
                      onValidated={(address, dist, fee, within) => {
                        setDeliveryAddress(address);
                        setDeliveryDistance(dist);
                        setCalculatedDeliveryFee(fee);
                      }}
                      onReset={() => {
                        setDeliveryValidation("idle");
                        setDeliveryDistance(null);
                        setCalculatedDeliveryFee(null);
                      }}
                      validation={deliveryValidation}
                      setValidation={setDeliveryValidation}
                      distance={deliveryDistance}
                    />
                  )}

                  {/* Scheduling */}
                  {(restaurant as any).scheduled_orders_enabled && orderType !== "dine_in" && (
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <h3 className="text-sm font-bold text-[hsl(0,0%,13%)] flex items-center gap-2">
                          <CalendarDays className="h-4 w-4" style={{ color: menuAccent }} /> Agendar pedido
                        </h3>
                        <button
                          onClick={() => { setIsScheduled(!isScheduled); if (isScheduled) { setScheduledDate(""); setScheduledTime(""); } }}
                          className={`relative w-11 h-6 rounded-full transition-colors ${isScheduled ? "bg-[hsl(0,100%,45%)]" : "bg-[hsl(0,0%,85%)]"}`}
                          style={isScheduled ? { backgroundColor: menuAccent } : {}}
                        >
                          <span className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform ${isScheduled ? "translate-x-5" : ""}`} />
                        </button>
                      </div>
                      {isScheduled && (
                        <div className="space-y-2 p-3 rounded-xl bg-[hsl(0,0%,97%)] border border-[hsl(0,0%,92%)]">
                          <div className="space-y-1">
                            <label className="text-xs font-medium text-[hsl(0,0%,45%)]">📅 Data</label>
                            <input
                              type="date"
                              value={scheduledDate}
                              min={(() => { const d = new Date(); d.setDate(d.getDate() + 1); return d.toISOString().slice(0,10); })()}
                              onChange={(e) => setScheduledDate(e.target.value)}
                              className="w-full h-10 rounded-lg bg-[hsl(0,0%,100%)] border border-[hsl(0,0%,88%)] px-3 text-sm text-[hsl(0,0%,13%)] outline-none focus:ring-2 focus:ring-[hsl(0,100%,45%)]/20"
                            />
                          </div>
                          <div className="space-y-1">
                            <label className="text-xs font-medium text-[hsl(0,0%,45%)]">🕐 Horário</label>
                            <select
                              value={scheduledTime}
                              onChange={(e) => setScheduledTime(e.target.value)}
                              className="w-full h-10 rounded-lg bg-[hsl(0,0%,100%)] border border-[hsl(0,0%,88%)] px-3 text-sm text-[hsl(0,0%,13%)] outline-none focus:ring-2 focus:ring-[hsl(0,100%,45%)]/20"
                            >
                              <option value="">Selecione o horário</option>
                              {(() => {
                                const open = restaurant.opening_time?.slice(0, 5) || "08:00";
                                const close = restaurant.closing_time?.slice(0, 5) || "22:00";
                                const [oh, om] = open.split(":").map(Number);
                                const [ch, cm] = close.split(":").map(Number);
                                const slots: string[] = [];
                                let h = oh, m = om;
                                while (h < ch || (h === ch && m <= cm)) {
                                  slots.push(`${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`);
                                  m += 30;
                                  if (m >= 60) { h++; m = 0; }
                                }
                                return slots.map(s => <option key={s} value={s}>{s}</option>);
                              })()}
                            </select>
                          </div>
                          {scheduledDate && scheduledTime && (
                            <p className="text-xs text-[hsl(142,60%,35%)] font-medium mt-1">
                              ✅ Agendado para {new Date(scheduledDate + "T" + scheduledTime).toLocaleDateString("pt-BR", { weekday: "long", day: "2-digit", month: "long" })} às {scheduledTime}
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Payment */}
                  {(() => {
                    const hasMp = !!((restaurant as any).mp_access_token && (restaurant as any).mp_public_key);
                    const isDelivery = orderType === "delivery";

                    return (
                      <div className="space-y-3">
                        <h3 className="text-sm font-bold text-[hsl(0,0%,13%)]">Pagamento</h3>

                        {hasMp ? (
                          // With MercadoPago configured
                          <div className="space-y-2">
                            <div className="grid grid-cols-3 gap-2">
                              <button
                                onClick={() => { setPaymentMethod("mercadopago"); setDeliveryPaymentMethod(""); setNeedsChange(false); setChangeAmount(""); }}
                                className={`p-3 rounded-xl border-2 text-sm font-medium transition-all flex flex-col items-center gap-1 ${paymentMethod === "mercadopago" ? "border-[hsl(200,100%,45%)] bg-[hsl(200,100%,97%)] text-[hsl(200,100%,40%)]" : "border-[hsl(0,0%,92%)] text-[hsl(0,0%,50%)]"}`}
                              >
                                <span className="text-lg">💳</span>
                                <span className="text-[11px]">Cartão</span>
                              </button>

                              <button
                                onClick={() => { setPaymentMethod("mp_pix"); setDeliveryPaymentMethod(""); setNeedsChange(false); setChangeAmount(""); }}
                                className={`p-3 rounded-xl border-2 text-sm font-medium transition-all flex flex-col items-center gap-1 ${paymentMethod === "mp_pix" ? "border-[hsl(170,100%,35%)] bg-[hsl(170,100%,97%)] text-[hsl(170,100%,30%)]" : "border-[hsl(0,0%,92%)] text-[hsl(0,0%,50%)]"}`}
                              >
                                <span className="text-lg">📱</span>
                                <span className="text-[11px]">PIX</span>
                              </button>

                              {isDelivery ? (
                                <button
                                  onClick={() => { setPaymentMethod("pay_on_delivery"); setDeliveryPaymentMethod(""); setNeedsChange(false); setChangeAmount(""); }}
                                  className={`p-3 rounded-xl border-2 text-sm font-medium transition-all flex flex-col items-center gap-1 ${paymentMethod === "pay_on_delivery" ? "border-[hsl(0,100%,45%)] bg-[hsl(0,100%,97%)] text-[hsl(0,100%,45%)]" : "border-[hsl(0,0%,92%)] text-[hsl(0,0%,50%)]"}`}
                                >
                                  <span className="text-lg">🛵</span>
                                  <span className="text-[11px]">Na Entrega</span>
                                </button>
                              ) : (
                                <button
                                  onClick={() => { setPaymentMethod("cash"); setDeliveryPaymentMethod(""); setNeedsChange(false); setChangeAmount(""); }}
                                  className={`p-3 rounded-xl border-2 text-sm font-medium transition-all flex flex-col items-center gap-1 ${paymentMethod === "cash" ? "border-[hsl(0,100%,45%)] bg-[hsl(0,100%,97%)] text-[hsl(0,100%,45%)]" : "border-[hsl(0,0%,92%)] text-[hsl(0,0%,50%)]"}`}
                                >
                                  <span className="text-lg">💵</span>
                                  <span className="text-[11px]">Dinheiro</span>
                                </button>
                              )}
                            </div>

                            {/* PIX email/CPF fields */}
                            {paymentMethod === "mp_pix" && (
                              <div className="p-3 rounded-xl bg-[hsl(170,30%,97%)] border border-[hsl(170,30%,88%)] space-y-2">
                                <p className="text-xs font-medium text-[hsl(170,50%,30%)]">📱 Dados para gerar o PIX</p>
                                <input
                                  className="w-full h-10 rounded-lg bg-[hsl(0,0%,100%)] border border-[hsl(0,0%,88%)] px-3 text-sm text-[hsl(0,0%,13%)] outline-none focus:ring-2 focus:ring-[hsl(170,100%,35%)]/20"
                                  placeholder="E-mail (opcional)"
                                  type="email"
                                  value={pixEmail}
                                  onChange={(e) => setPixEmail(e.target.value)}
                                />
                                <input
                                  className="w-full h-10 rounded-lg bg-[hsl(0,0%,100%)] border border-[hsl(0,0%,88%)] px-3 text-sm text-[hsl(0,0%,13%)] outline-none focus:ring-2 focus:ring-[hsl(170,100%,35%)]/20"
                                  placeholder="CPF (opcional)"
                                  value={pixCpf}
                                  onChange={(e) => setPixCpf(e.target.value)}
                                />
                              </div>
                            )}

                            {/* Cash: needs change? */}
                            {paymentMethod === "cash" && (
                              <div className="p-3 rounded-xl bg-[hsl(0,0%,97%)] space-y-2">
                                <p className="text-xs font-medium text-[hsl(0,0%,40%)]">Vai precisar de troco?</p>
                                <div className="grid grid-cols-2 gap-2">
                                  <button onClick={() => { setNeedsChange(false); setChangeAmount(""); }} className={`p-2 rounded-lg border text-sm font-medium transition-all ${!needsChange ? "border-[hsl(0,100%,45%)] bg-[hsl(0,100%,97%)] text-[hsl(0,100%,45%)]" : "border-[hsl(0,0%,90%)] text-[hsl(0,0%,55%)]"}`}>Não</button>
                                  <button onClick={() => setNeedsChange(true)} className={`p-2 rounded-lg border text-sm font-medium transition-all ${needsChange ? "border-[hsl(0,100%,45%)] bg-[hsl(0,100%,97%)] text-[hsl(0,100%,45%)]" : "border-[hsl(0,0%,90%)] text-[hsl(0,0%,55%)]"}`}>Sim</button>
                                </div>
                                {needsChange && (
                                  <div>
                                    <p className="text-xs text-[hsl(0,0%,55%)] mb-1">Troco para quanto?</p>
                                    <input
                                      className="w-full h-10 rounded-lg bg-[hsl(0,0%,100%)] border border-[hsl(0,0%,88%)] px-3 text-sm text-[hsl(0,0%,13%)] outline-none focus:ring-2 focus:ring-[hsl(0,100%,45%)]/20"
                                      placeholder="R$ 0,00"
                                      type="number"
                                      min={finalTotal}
                                      step="0.01"
                                      value={changeAmount}
                                      onChange={(e) => setChangeAmount(e.target.value)}
                                    />
                                    {changeAmount && Number(changeAmount) > finalTotal && (
                                      <p className="text-xs text-[hsl(142,71%,40%)] font-medium mt-1">
                                        Troco: {fmt(Number(changeAmount) - finalTotal)}
                                      </p>
                                    )}
                                  </div>
                                )}
                              </div>
                            )}

                            {/* Delivery sub-payment options */}
                            {paymentMethod === "pay_on_delivery" && (
                              <div className="p-3 rounded-xl bg-[hsl(0,0%,97%)] space-y-2">
                                <p className="text-xs font-medium text-[hsl(0,0%,40%)]">Como vai pagar na entrega?</p>
                                <div className="space-y-2">
                                  {/* Pix */}
                                  <button
                                    onClick={() => { setDeliveryPaymentMethod("pix"); setNeedsChange(false); setChangeAmount(""); }}
                                    className={`w-full p-3 rounded-xl border-2 text-sm font-medium transition-all text-left flex items-center gap-3 ${deliveryPaymentMethod === "pix" ? "border-[hsl(170,100%,35%)] bg-[hsl(170,100%,97%)] text-[hsl(170,100%,30%)]" : "border-[hsl(0,0%,92%)] text-[hsl(0,0%,50%)]"}`}
                                  >
                                    <span className="text-lg">📱</span>
                                    <span>PIX</span>
                                  </button>

                                  {/* Cartão de Crédito */}
                                  <button
                                    onClick={() => { setDeliveryPaymentMethod("credit_card"); setNeedsChange(false); setChangeAmount(""); }}
                                    className={`w-full p-3 rounded-xl border-2 text-sm font-medium transition-all text-left ${deliveryPaymentMethod === "credit_card" ? "border-[hsl(220,80%,50%)] bg-[hsl(220,80%,97%)] text-[hsl(220,80%,40%)]" : "border-[hsl(0,0%,92%)] text-[hsl(0,0%,50%)]"}`}
                                  >
                                    <div className="flex items-center gap-3">
                                      <span className="text-lg">💳</span>
                                      <div>
                                        <span>Cartão de Crédito</span>
                                        <div className="flex gap-1.5 mt-1">
                                          <span className="text-[10px] px-1.5 py-0.5 rounded bg-[hsl(220,80%,95%)] text-[hsl(220,80%,40%)]">Visa</span>
                                          <span className="text-[10px] px-1.5 py-0.5 rounded bg-[hsl(0,80%,95%)] text-[hsl(0,80%,40%)]">Master</span>
                                          <span className="text-[10px] px-1.5 py-0.5 rounded bg-[hsl(210,60%,95%)] text-[hsl(210,60%,40%)]">Elo</span>
                                          <span className="text-[10px] px-1.5 py-0.5 rounded bg-[hsl(40,70%,95%)] text-[hsl(40,70%,40%)]">Amex</span>
                                        </div>
                                      </div>
                                    </div>
                                  </button>

                                  {/* Dinheiro */}
                                  <button
                                    onClick={() => { setDeliveryPaymentMethod("cash"); setNeedsChange(false); setChangeAmount(""); }}
                                    className={`w-full p-3 rounded-xl border-2 text-sm font-medium transition-all text-left flex items-center gap-3 ${deliveryPaymentMethod === "cash" ? "border-[hsl(120,50%,40%)] bg-[hsl(120,50%,97%)] text-[hsl(120,50%,35%)]" : "border-[hsl(0,0%,92%)] text-[hsl(0,0%,50%)]"}`}
                                  >
                                    <span className="text-lg">💵</span>
                                    <span>Dinheiro</span>
                                  </button>

                                  {/* Cash change for delivery */}
                                  {deliveryPaymentMethod === "cash" && (
                                    <div className="p-3 rounded-xl bg-[hsl(0,0%,100%)] border border-[hsl(0,0%,90%)] space-y-2">
                                      <p className="text-xs font-medium text-[hsl(0,0%,40%)]">Vai precisar de troco?</p>
                                      <div className="grid grid-cols-2 gap-2">
                                        <button onClick={() => { setNeedsChange(false); setChangeAmount(""); }} className={`p-2 rounded-lg border text-sm font-medium transition-all ${!needsChange ? "border-[hsl(0,100%,45%)] bg-[hsl(0,100%,97%)] text-[hsl(0,100%,45%)]" : "border-[hsl(0,0%,90%)] text-[hsl(0,0%,55%)]"}`}>Não</button>
                                        <button onClick={() => setNeedsChange(true)} className={`p-2 rounded-lg border text-sm font-medium transition-all ${needsChange ? "border-[hsl(0,100%,45%)] bg-[hsl(0,100%,97%)] text-[hsl(0,100%,45%)]" : "border-[hsl(0,0%,90%)] text-[hsl(0,0%,55%)]"}`}>Sim</button>
                                      </div>
                                      {needsChange && (
                                        <div>
                                          <p className="text-xs text-[hsl(0,0%,55%)] mb-1">Troco para quanto?</p>
                                          <input
                                            className="w-full h-10 rounded-lg bg-[hsl(0,0%,100%)] border border-[hsl(0,0%,88%)] px-3 text-sm text-[hsl(0,0%,13%)] outline-none focus:ring-2 focus:ring-[hsl(0,100%,45%)]/20"
                                            placeholder="R$ 0,00"
                                            type="number"
                                            min={finalTotal}
                                            step="0.01"
                                            value={changeAmount}
                                            onChange={(e) => setChangeAmount(e.target.value)}
                                          />
                                          {changeAmount && Number(changeAmount) > finalTotal && (
                                            <p className="text-xs text-[hsl(142,71%,40%)] font-medium mt-1">
                                              Troco: {fmt(Number(changeAmount) - finalTotal)}
                                            </p>
                                          )}
                                        </div>
                                      )}
                                    </div>
                                  )}
                                </div>
                              </div>
                            )}
                          </div>
                        ) : (
                          // Without MercadoPago — show standard options
                          <div className="grid grid-cols-2 gap-2">
                            {([{ value: "pix", label: "PIX", icon: "📱" }, { value: "credit_card", label: "Crédito", icon: "💳" }, { value: "debit_card", label: "Débito", icon: "💳" }, { value: "cash", label: "Dinheiro", icon: "💵" }] as const).map((p) => (
                              <button key={p.value} onClick={() => { setPaymentMethod(p.value); setNeedsChange(false); setChangeAmount(""); }} className={`p-3 rounded-xl border-2 text-sm font-medium transition-all flex flex-col items-center gap-1 ${paymentMethod === p.value ? "border-[hsl(0,100%,45%)] bg-[hsl(0,100%,97%)] text-[hsl(0,100%,45%)]" : "border-[hsl(0,0%,92%)] text-[hsl(0,0%,50%)]"}`}>
                                <span className="text-lg">{p.icon}</span>
                                {p.label}
                              </button>
                            ))}
                          </div>
                        )}

                        {/* Cash change for non-MP standard option */}
                        {!hasMp && paymentMethod === "cash" && (
                          <div className="p-3 rounded-xl bg-[hsl(0,0%,97%)] space-y-2">
                            <p className="text-xs font-medium text-[hsl(0,0%,40%)]">Vai precisar de troco?</p>
                            <div className="grid grid-cols-2 gap-2">
                              <button onClick={() => { setNeedsChange(false); setChangeAmount(""); }} className={`p-2 rounded-lg border text-sm font-medium transition-all ${!needsChange ? "border-[hsl(0,100%,45%)] bg-[hsl(0,100%,97%)] text-[hsl(0,100%,45%)]" : "border-[hsl(0,0%,90%)] text-[hsl(0,0%,55%)]"}`}>Não</button>
                              <button onClick={() => setNeedsChange(true)} className={`p-2 rounded-lg border text-sm font-medium transition-all ${needsChange ? "border-[hsl(0,100%,45%)] bg-[hsl(0,100%,97%)] text-[hsl(0,100%,45%)]" : "border-[hsl(0,0%,90%)] text-[hsl(0,0%,55%)]"}`}>Sim</button>
                            </div>
                            {needsChange && (
                              <div>
                                <p className="text-xs text-[hsl(0,0%,55%)] mb-1">Troco para quanto?</p>
                                <input
                                  className="w-full h-10 rounded-lg bg-[hsl(0,0%,100%)] border border-[hsl(0,0%,88%)] px-3 text-sm text-[hsl(0,0%,13%)] outline-none focus:ring-2 focus:ring-[hsl(0,100%,45%)]/20"
                                  placeholder="R$ 0,00"
                                  type="number"
                                  min={finalTotal}
                                  step="0.01"
                                  value={changeAmount}
                                  onChange={(e) => setChangeAmount(e.target.value)}
                                />
                                {changeAmount && Number(changeAmount) > finalTotal && (
                                  <p className="text-xs text-[hsl(142,71%,40%)] font-medium mt-1">
                                    Troco: {fmt(Number(changeAmount) - finalTotal)}
                                  </p>
                                )}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })()}

                  {/* Coupon */}
                  <div className="space-y-2">
                    <h3 className="text-sm font-bold text-[hsl(0,0%,13%)] flex items-center gap-2"><Ticket className="h-4 w-4 text-[hsl(0,100%,45%)]" /> Cupom de desconto</h3>
                    {appliedCoupon ? (
                      <div className="flex items-center justify-between p-3 rounded-xl bg-[hsl(142,50%,96%)] border border-[hsl(142,50%,85%)]">
                        <div>
                          <p className="text-sm font-bold text-[hsl(142,71%,35%)] flex items-center gap-1"><CheckCircle2 className="h-3.5 w-3.5" /> {appliedCoupon.code}</p>
                          <p className="text-[11px] text-[hsl(0,0%,50%)]">
                            {appliedCoupon.type === "percentage" && `${appliedCoupon.value}% de desconto`}
                            {appliedCoupon.type === "fixed" && `${fmt(Number(appliedCoupon.value))} de desconto`}
                            {appliedCoupon.type === "free_shipping" && "Frete grátis"}
                            {appliedCoupon.type === "free_product" && "Produto grátis"}
                          </p>
                        </div>
                        <button onClick={removeCoupon} className="text-[hsl(0,0%,60%)]"><X className="h-4 w-4" /></button>
                      </div>
                    ) : (
                      <div className="flex gap-2">
                        <input className="flex-1 h-12 rounded-xl bg-[hsl(0,0%,96%)] px-4 text-sm font-mono tracking-wider text-[hsl(0,0%,13%)] placeholder:text-[hsl(0,0%,60%)] outline-none focus:ring-2 focus:ring-[hsl(0,100%,45%)]/20 uppercase" placeholder="CÓDIGO" value={couponCode} onChange={(e) => setCouponCode(e.target.value.toUpperCase())} />
                        <button onClick={applyCoupon} disabled={couponLoading || !couponCode.trim()} className="h-12 px-5 rounded-xl border-2 border-[hsl(0,100%,45%)] text-[hsl(0,100%,45%)] font-semibold text-sm disabled:opacity-40">
                          {couponLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Aplicar"}
                        </button>
                      </div>
                    )}
                    {couponError && <p className="text-xs text-[hsl(0,72%,51%)]">{couponError}</p>}
                  </div>

                  {/* Loyalty Redemption */}
                  {currentUser && customerLoyalty.length > 0 && (
                    <div className="space-y-2">
                      <h3 className="text-sm font-bold text-[hsl(0,0%,13%)] flex items-center gap-2"><Trophy className="h-4 w-4 text-[hsl(35,100%,40%)]" /> Usar recompensas</h3>
                      {(() => {
                        const pointsProg = loyaltyPrograms.find(p => p.type === "points");
                        const pointsLoyalty = customerLoyalty.find(cl => cl.loyalty_program_id === pointsProg?.id);
                        const hasEnoughPoints = pointsProg && pointsLoyalty && Number(pointsLoyalty.points_balance) >= (pointsProg.points_to_redeem || 100);
                        const redeemSets = hasEnoughPoints ? Math.floor(Number(pointsLoyalty!.points_balance) / (pointsProg!.points_to_redeem || 100)) : 0;
                        const maxPointsDiscount = redeemSets * Number(pointsProg?.reward_value || 10);

                        const cashbackProg = loyaltyPrograms.find(p => p.type === "cashback");
                        const cashbackLoyalty = customerLoyalty.find(cl => cl.loyalty_program_id === cashbackProg?.id);
                        const hasCashback = cashbackProg && cashbackLoyalty && Number(cashbackLoyalty.cashback_balance) > 0;

                        if (!hasEnoughPoints && !hasCashback) return null;

                        return (
                          <div className="space-y-2">
                            {hasEnoughPoints && (
                              <button
                                onClick={() => setRedeemPoints(!redeemPoints)}
                                className={`w-full flex items-center justify-between p-3 rounded-xl border-2 transition-all text-left ${
                                  redeemPoints ? "border-[hsl(0,100%,45%)] bg-[hsl(0,100%,97%)]" : "border-[hsl(0,0%,92%)]"
                                }`}
                              >
                                <div className="flex items-center gap-2">
                                  <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all ${
                                    redeemPoints ? "border-[hsl(0,100%,45%)] bg-[hsl(0,100%,45%)]" : "border-[hsl(0,0%,75%)]"
                                  }`}>
                                    {redeemPoints && <Check className="h-3 w-3 text-[hsl(0,0%,100%)]" />}
                                  </div>
                                  <div>
                                    <p className="text-sm font-medium text-[hsl(0,0%,13%)]">Usar pontos</p>
                                    <p className="text-[11px] text-[hsl(0,0%,55%)]">
                                      {Math.floor(Number(pointsLoyalty!.points_balance))} pts → até {fmt(maxPointsDiscount)} off
                                    </p>
                                  </div>
                                </div>
                                <Star className="h-4 w-4 text-[hsl(45,100%,50%)] shrink-0" />
                              </button>
                            )}

                            {hasCashback && (
                              <button
                                onClick={() => setRedeemCashback(!redeemCashback)}
                                className={`w-full flex items-center justify-between p-3 rounded-xl border-2 transition-all text-left ${
                                  redeemCashback ? "border-[hsl(0,100%,45%)] bg-[hsl(0,100%,97%)]" : "border-[hsl(0,0%,92%)]"
                                }`}
                              >
                                <div className="flex items-center gap-2">
                                  <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all ${
                                    redeemCashback ? "border-[hsl(0,100%,45%)] bg-[hsl(0,100%,45%)]" : "border-[hsl(0,0%,75%)]"
                                  }`}>
                                    {redeemCashback && <Check className="h-3 w-3 text-[hsl(0,0%,100%)]" />}
                                  </div>
                                  <div>
                                    <p className="text-sm font-medium text-[hsl(0,0%,13%)]">Usar cashback</p>
                                    <p className="text-[11px] text-[hsl(0,0%,55%)]">
                                      {fmt(Number(cashbackLoyalty!.cashback_balance))} disponível
                                    </p>
                                  </div>
                                </div>
                                <CreditCard className="h-4 w-4 text-[hsl(142,71%,45%)] shrink-0" />
                              </button>
                            )}
                          </div>
                        );
                      })()}
                    </div>
                  )}

                  {/* Loyalty earn preview */}
                  {currentUser && loyaltyPreview.length > 0 && (
                    <div className="rounded-xl bg-[hsl(45,100%,96%)] border border-[hsl(45,100%,88%)] p-3">
                      <p className="text-xs font-bold text-[hsl(35,100%,35%)] mb-1 flex items-center gap-1"><Gift className="h-3.5 w-3.5" /> Você vai ganhar:</p>
                      {loyaltyPreview.map((lp) => (
                        <p key={lp.id} className="text-[11px] text-[hsl(0,0%,50%)]">{lp.earn}</p>
                      ))}
                    </div>
                    )}
                  <textarea className="w-full rounded-xl bg-[hsl(0,0%,96%)] px-4 py-3 text-sm text-[hsl(0,0%,13%)] placeholder:text-[hsl(0,0%,60%)] outline-none focus:ring-2 focus:ring-[hsl(0,100%,45%)]/20 resize-none" placeholder="Observações (opcional)" rows={2} value={orderNotes} onChange={(e) => setOrderNotes(e.target.value)} />

                  {/* Summary */}
                  <div className="p-4 rounded-2xl bg-gradient-to-b from-[hsl(0,0%,98%)] to-[hsl(0,0%,96%)] border border-[hsl(0,0%,92%)] space-y-2.5">
                    <p className="text-xs font-medium text-[hsl(0,0%,50%)]">{cart.reduce((s, i) => s + i.qty, 0)} item(ns)</p>
                    <div className="flex justify-between text-sm text-[hsl(0,0%,30%)]">
                      <span>Subtotal</span>
                      <span>{fmt(cartTotal)}</span>
                    </div>
                    {couponDiscount > 0 && (
                      <div className="flex justify-between text-sm text-[hsl(142,71%,35%)] font-medium">
                        <span className="flex items-center gap-1"><Tag className="h-3 w-3" /> Cupom</span>
                        <span>-{fmt(couponDiscount)}</span>
                      </div>
                    )}
                    {loyaltyRedemptionDiscount > 0 && (
                      <div className="flex justify-between text-sm text-[hsl(142,71%,35%)] font-medium">
                        <span className="flex items-center gap-1"><Trophy className="h-3 w-3" /> Recompensas</span>
                        <span>-{fmt(loyaltyRedemptionDiscount)}</span>
                      </div>
                    )}
                    {orderType === "delivery" && effectiveDeliveryFee > 0 && (
                      <div className="flex justify-between text-sm text-[hsl(0,0%,30%)]">
                        <span className="flex items-center gap-1"><MapPin className="h-3 w-3" /> Entrega ({deliveryDistance ? `${deliveryDistance} km` : ""})</span>
                        <span>{fmt(effectiveDeliveryFee)}</span>
                      </div>
                    )}
                    {orderType === "delivery" && effectiveDeliveryFee === 0 && deliveryValidation === "ok" && (
                      <div className="flex justify-between text-sm text-[hsl(142,71%,35%)] font-medium">
                        <span className="flex items-center gap-1"><MapPin className="h-3 w-3" /> Entrega</span>
                        <span>Grátis</span>
                      </div>
                    )}
                    <div className="flex justify-between font-extrabold text-xl pt-3 border-t border-[hsl(0,0%,88%)] text-[hsl(0,0%,8%)]">
                      <span>Total</span>
                      <span>{fmt(finalTotal)}</span>
                    </div>

                    {/* Split Payment Calculator */}
                    {(restaurant as any).split_payment_enabled && (
                      <div className="pt-3 border-t border-[hsl(0,0%,90%)]">
                        {!showSplitCalc ? (
                          <button
                            onClick={() => setShowSplitCalc(true)}
                            className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl border-2 border-dashed border-[hsl(0,0%,85%)] text-sm font-medium text-[hsl(0,0%,50%)] hover:border-[hsl(0,0%,70%)] hover:text-[hsl(0,0%,35%)] transition-colors"
                          >
                            <Users className="h-4 w-4" /> Dividir conta
                          </button>
                        ) : (
                          <div className="space-y-3">
                            <div className="flex items-center justify-between">
                              <p className="text-xs font-bold text-[hsl(0,0%,40%)] flex items-center gap-1.5">
                                <Users className="h-3.5 w-3.5" /> Dividir entre:
                              </p>
                              <button onClick={() => { setShowSplitCalc(false); setSplitPeople(2); }} className="text-[hsl(0,0%,60%)]"><X className="h-4 w-4" /></button>
                            </div>
                            <div className="flex items-center justify-center gap-4">
                              <button
                                onClick={() => setSplitPeople(Math.max(2, splitPeople - 1))}
                                className="w-10 h-10 rounded-full bg-[hsl(0,0%,94%)] flex items-center justify-center text-lg font-bold text-[hsl(0,0%,40%)] hover:bg-[hsl(0,0%,88%)] transition-colors"
                              >−</button>
                              <span className="text-2xl font-extrabold text-[hsl(0,0%,13%)] w-10 text-center">{splitPeople}</span>
                              <button
                                onClick={() => setSplitPeople(Math.min(10, splitPeople + 1))}
                                className="w-10 h-10 rounded-full bg-[hsl(0,0%,94%)] flex items-center justify-center text-lg font-bold text-[hsl(0,0%,40%)] hover:bg-[hsl(0,0%,88%)] transition-colors"
                              >+</button>
                            </div>
                            <div className="text-center p-3 rounded-xl bg-[hsl(142,60%,96%)] border border-[hsl(142,50%,85%)]">
                              <p className="text-xs text-[hsl(142,40%,35%)]">Cada pessoa paga</p>
                              <p className="text-lg font-extrabold text-[hsl(142,60%,30%)]">{fmt(finalTotal / splitPeople)}</p>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                <div className="p-4 border-t border-[hsl(0,0%,94%)] bg-[hsl(0,0%,100%)]">
                  {mpError && paymentMethod === "mp_pix" && (
                    <div className="mb-3 p-3 rounded-xl bg-[hsl(0,90%,96%)] border border-[hsl(0,80%,85%)] flex items-start gap-2">
                      <AlertTriangle className="h-4 w-4 text-[hsl(0,70%,45%)] mt-0.5 shrink-0" />
                      <p className="text-xs text-[hsl(0,0%,50%)]">{mpError}</p>
                    </div>
                  )}
                  <button
                    onClick={() => setCheckoutStep("review")}
                    disabled={submitting || pixLoading || !customerName.trim() || (orderType === "delivery" && !deliveryAddress.trim()) || deliveryValidation === "too_far" || deliveryValidation === "checking" || (isScheduled && (!scheduledDate || !scheduledTime))}
                    className="w-full h-[52px] rounded-2xl font-bold text-[15px] text-[hsl(0,0%,100%)] disabled:opacity-40 transition-all active:scale-[0.97] shadow-lg"
                    style={{ backgroundColor: primaryColor, boxShadow: `0 6px 20px ${primaryColor}50` }}
                  >
                    Revisar pedido · {fmt(finalTotal)}
                  </button>
                </div>
              </>
            )}

            {/* Review Step */}
            {checkoutStep === "review" && (
              <div className="flex-1 min-h-0 flex flex-col overflow-hidden">
                <CheckoutReviewSheet
                  cart={cart}
                  cartTotal={cartTotal}
                  finalTotal={finalTotal}
                  couponDiscount={couponDiscount}
                  loyaltyDiscount={loyaltyRedemptionDiscount}
                  deliveryFee={effectiveDeliveryFee}
                  orderType={orderType}
                  deliveryAddress={deliveryAddress}
                  paymentMethod={paymentMethod === "pay_on_delivery" && deliveryPaymentMethod ? `pay_on_delivery_${deliveryPaymentMethod}` : paymentMethod}
                  customerName={customerName}
                  restaurantName={restaurant?.name || ""}
                  primaryColor={primaryColor}
                  onConfirm={() => {
                    if (paymentMethod === "mercadopago") {
                      setMpError("");
                      setMpLoading(true);
                      setCheckoutStep("mp_payment");
                    } else if (paymentMethod === "mp_pix") {
                      handlePixPayment();
                    } else {
                      handleSubmitOrder();
                    }
                  }}
                  onEdit={() => setCheckoutStep("form")}
                  submitting={submitting || pixLoading}
                  fmt={fmt}
                  scheduledFor={isScheduled && scheduledDate && scheduledTime ? `${scheduledDate}T${scheduledTime}:00` : null}
                  splitInfo={showSplitCalc && splitPeople >= 2 && (restaurant as any).split_payment_enabled ? `${splitPeople} pessoas — ${fmt(finalTotal / splitPeople)} cada` : null}
                />
              </div>
            )}

            {/* MercadoPago Payment Step */}
            {checkoutStep === "mp_payment" && (
              <div className="flex-1 flex flex-col">
                <div className="flex-1 overflow-auto p-4 space-y-4">
                  {/* Order summary */}
                  <div className="p-3 rounded-xl bg-[hsl(0,0%,98%)] border border-[hsl(0,0%,92%)] space-y-1">
                    <div className="flex justify-between text-sm text-[hsl(0,0%,30%)]">
                      <span>Subtotal</span><span>{fmt(cartTotal)}</span>
                    </div>
                    {(couponDiscount + loyaltyRedemptionDiscount) > 0 && (
                      <div className="flex justify-between text-sm text-[hsl(142,71%,35%)]">
                        <span>Descontos</span><span>-{fmt(couponDiscount + loyaltyRedemptionDiscount)}</span>
                      </div>
                    )}
                    <div className="flex justify-between font-bold text-lg pt-1 border-t border-[hsl(0,0%,90%)] text-[hsl(0,0%,13%)]">
                      <span>Total</span><span>{fmt(finalTotal)}</span>
                    </div>
                  </div>

                  {mpError && (
                    <div className="p-3 rounded-xl bg-[hsl(0,90%,96%)] border border-[hsl(0,80%,85%)] flex items-start gap-2">
                      <AlertTriangle className="h-4 w-4 text-[hsl(0,70%,45%)] mt-0.5 shrink-0" />
                      <div>
                        <p className="text-sm font-medium text-[hsl(0,70%,40%)]">Pagamento não aprovado</p>
                        <p className="text-xs text-[hsl(0,0%,50%)] mt-0.5">{mpError}</p>
                      </div>
                    </div>
                  )}

                  {mpLoading && (
                    <div className="flex items-center justify-center py-8">
                      <Loader2 className="h-6 w-6 animate-spin text-[hsl(200,100%,45%)]" />
                      <span className="ml-2 text-sm text-[hsl(0,0%,50%)]">Carregando formulário de pagamento...</span>
                    </div>
                  )}

                  {mpProcessing && (
                    <div className="flex flex-col items-center justify-center py-12">
                      <Loader2 className="h-8 w-8 animate-spin text-[hsl(200,100%,45%)] mb-3" />
                      <p className="text-sm font-medium text-[hsl(0,0%,30%)]">Processando pagamento...</p>
                      <p className="text-xs text-[hsl(0,0%,55%)] mt-1">Aguarde, não feche esta tela.</p>
                    </div>
                  )}

                  {/* MP Brick container */}
                  <div id="mp-card-container" ref={mpContainerRef} style={{ display: mpProcessing ? 'none' : 'block' }} />
                </div>
              </div>
            )}


            {/* PIX QR Code Step */}
            {checkoutStep === "mp_pix" && (
              <div className="flex-1 flex flex-col">
                <div className="flex-1 overflow-auto p-4 space-y-4">
                  {/* Order summary */}
                  <div className="p-3 rounded-xl bg-[hsl(0,0%,98%)] border border-[hsl(0,0%,92%)] space-y-1">
                    <div className="flex justify-between text-sm text-[hsl(0,0%,30%)]">
                      <span>Subtotal</span><span>{fmt(cartTotal)}</span>
                    </div>
                    {(couponDiscount + loyaltyRedemptionDiscount) > 0 && (
                      <div className="flex justify-between text-sm text-[hsl(142,71%,35%)]">
                        <span>Descontos</span><span>-{fmt(couponDiscount + loyaltyRedemptionDiscount)}</span>
                      </div>
                    )}
                    <div className="flex justify-between font-bold text-lg pt-1 border-t border-[hsl(0,0%,90%)] text-[hsl(0,0%,13%)]">
                      <span>Total</span><span>{fmt(finalTotal)}</span>
                    </div>
                  </div>

                  {mpError && (
                    <div className="p-3 rounded-xl bg-[hsl(0,90%,96%)] border border-[hsl(0,80%,85%)] flex items-start gap-2">
                      <AlertTriangle className="h-4 w-4 text-[hsl(0,70%,45%)] mt-0.5 shrink-0" />
                      <div>
                        <p className="text-sm font-medium text-[hsl(0,70%,40%)]">Erro no pagamento</p>
                        <p className="text-xs text-[hsl(0,0%,50%)] mt-0.5">{mpError}</p>
                      </div>
                    </div>
                  )}

                  {/* QR Code */}
                  <div className="flex flex-col items-center text-center space-y-4">
                    <div className="w-16 h-16 rounded-full bg-[hsl(170,60%,94%)] flex items-center justify-center">
                      <span className="text-3xl">📱</span>
                    </div>
                    <div>
                      <h3 className="font-bold text-lg text-[hsl(0,0%,13%)]">Pague com PIX</h3>
                      <p className="text-xs text-[hsl(0,0%,55%)] mt-1">Escaneie o QR Code ou copie o código</p>
                    </div>

                    {pixQrCodeBase64 ? (
                      <div className="bg-[hsl(0,0%,100%)] p-4 rounded-2xl border-2 border-[hsl(170,60%,85%)] shadow-sm">
                        <img
                          src={`data:image/png;base64,${pixQrCodeBase64}`}
                          alt="QR Code PIX"
                          className="w-56 h-56"
                        />
                      </div>
                    ) : pixQrCode ? (
                      <div className="bg-[hsl(0,0%,100%)] p-4 rounded-2xl border-2 border-[hsl(170,60%,85%)] shadow-sm">
                        <div className="w-56 h-56 flex items-center justify-center">
                          <p className="text-xs text-[hsl(0,0%,55%)] text-center px-4">Use o código "Copia e Cola" abaixo</p>
                        </div>
                      </div>
                    ) : null}

                    {/* Copy PIX code */}
                    {pixQrCode && (
                      <button
                        onClick={() => {
                          navigator.clipboard.writeText(pixQrCode);
                          setPixCopied(true);
                          setTimeout(() => setPixCopied(false), 3000);
                          toast.success("Código PIX copiado!");
                        }}
                        className={`w-full max-w-xs h-12 rounded-xl font-bold text-sm transition-all flex items-center justify-center gap-2 ${
                          pixCopied
                            ? "bg-[hsl(142,71%,45%)] text-[hsl(0,0%,100%)]"
                            : "bg-[hsl(170,60%,94%)] text-[hsl(170,80%,25%)] border-2 border-[hsl(170,60%,75%)]"
                        }`}
                      >
                        {pixCopied ? (
                          <><Check className="h-4 w-4" /> Código copiado!</>
                        ) : (
                          <><CreditCard className="h-4 w-4" /> Copiar código PIX</>
                        )}
                      </button>
                    )}

                    {/* Waiting status */}
                    <div className="flex items-center gap-2 text-sm text-[hsl(0,0%,50%)]">
                      <Loader2 className="h-4 w-4 animate-spin text-[hsl(170,100%,35%)]" />
                      <span>Aguardando pagamento...</span>
                    </div>
                    <p className="text-[11px] text-[hsl(0,0%,65%)]">
                      O pagamento será confirmado automaticamente. Não feche esta tela.
                    </p>
                  </div>
                </div>
              </div>
            )}


            {checkoutStep === "rating" && (
              <div className="flex-1 flex flex-col p-6 overflow-auto">
                <div className="flex-1 flex flex-col items-center justify-center text-center">
                  <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: "spring", damping: 15 }}>
                    <div className="w-16 h-16 rounded-full bg-[hsl(45,100%,94%)] flex items-center justify-center mb-4">
                      <span className="text-3xl">⭐</span>
                    </div>
                  </motion.div>

                  <h3 className="font-bold text-xl text-[hsl(0,0%,13%)] mb-1">Como foi seu pedido?</h3>
                  <p className="text-[hsl(0,0%,50%)] text-sm mb-8">Sua opinião é muito importante para nós</p>

                  {/* Food Rating */}
                  <div className="w-full max-w-sm mb-6">
                    <p className="text-sm font-semibold text-[hsl(0,0%,25%)] mb-3">🍽️ Comida</p>
                    <div className="flex justify-center gap-2">
                      {[1, 2, 3, 4, 5].map((star) => (
                        <button
                          key={star}
                          onClick={() => setFoodRating(star)}
                          className="transition-all duration-150 active:scale-90"
                        >
                          <Star
                            className={`h-10 w-10 transition-colors ${
                              star <= foodRating
                                ? "fill-[hsl(45,100%,50%)] text-[hsl(45,100%,50%)]"
                                : "fill-none text-[hsl(0,0%,80%)]"
                            }`}
                          />
                        </button>
                      ))}
                    </div>
                    {foodRating > 0 && (
                      <motion.p initial={{ opacity: 0, y: -5 }} animate={{ opacity: 1, y: 0 }} className="text-xs text-[hsl(0,0%,55%)] mt-1">
                        {foodRating <= 2 ? "Pode melhorar" : foodRating <= 3 ? "Regular" : foodRating === 4 ? "Muito bom!" : "Excelente!"}
                      </motion.p>
                    )}
                  </div>

                  {/* Service Rating */}
                  <div className="w-full max-w-sm mb-6">
                    <p className="text-sm font-semibold text-[hsl(0,0%,25%)] mb-3">👋 Atendimento</p>
                    <div className="flex justify-center gap-2">
                      {[1, 2, 3, 4, 5].map((star) => (
                        <button
                          key={star}
                          onClick={() => setServiceRating(star)}
                          className="transition-all duration-150 active:scale-90"
                        >
                          <Star
                            className={`h-10 w-10 transition-colors ${
                              star <= serviceRating
                                ? "fill-[hsl(45,100%,50%)] text-[hsl(45,100%,50%)]"
                                : "fill-none text-[hsl(0,0%,80%)]"
                            }`}
                          />
                        </button>
                      ))}
                    </div>
                    {serviceRating > 0 && (
                      <motion.p initial={{ opacity: 0, y: -5 }} animate={{ opacity: 1, y: 0 }} className="text-xs text-[hsl(0,0%,55%)] mt-1">
                        {serviceRating <= 2 ? "Pode melhorar" : serviceRating <= 3 ? "Regular" : serviceRating === 4 ? "Muito bom!" : "Excelente!"}
                      </motion.p>
                    )}
                  </div>

                  {/* Comment */}
                  <div className="w-full max-w-sm mb-6">
                    <textarea
                      className="w-full rounded-xl bg-[hsl(0,0%,96%)] px-4 py-3 text-sm text-[hsl(0,0%,13%)] placeholder:text-[hsl(0,0%,60%)] outline-none focus:ring-2 focus:ring-[hsl(45,100%,50%)]/30 resize-none"
                      placeholder="Deixe um comentário (opcional)"
                      rows={3}
                      value={ratingComment}
                      onChange={(e) => setRatingComment(e.target.value)}
                    />
                  </div>
                </div>

                {/* Rating Actions */}
                <div className="space-y-3 w-full max-w-sm mx-auto">
                  <button
                    onClick={handleSubmitRating}
                    disabled={ratingSubmitting || foodRating === 0 || serviceRating === 0}
                    className="w-full h-14 rounded-xl font-bold text-base text-[hsl(0,0%,100%)] disabled:opacity-40 transition-opacity"
                    style={{ backgroundColor: primaryColor }}
                  >
                    {ratingSubmitting ? <Loader2 className="h-5 w-5 animate-spin mx-auto" /> : "Enviar avaliação"}
                  </button>
                  <button
                    onClick={() => setCheckoutStep("success")}
                    className="w-full text-center text-sm text-[hsl(0,0%,55%)] py-2"
                  >
                    Pular
                  </button>
                </div>
              </div>
            )}

            {/* Success Step */}
            {checkoutStep === "success" && (
              <div className="flex-1 flex flex-col items-center justify-center p-8 text-center relative overflow-hidden">
                {/* Decorative circles */}
                <div className="absolute top-10 left-6 w-16 h-16 rounded-full bg-[hsl(142,50%,90%)]/30 blur-xl" />
                <div className="absolute bottom-20 right-8 w-24 h-24 rounded-full bg-[hsl(45,100%,90%)]/30 blur-xl" />
                <div className="absolute top-1/3 right-4 w-12 h-12 rounded-full bg-[hsl(210,80%,90%)]/30 blur-xl" />

                <motion.div initial={{ scale: 0, rotate: -30 }} animate={{ scale: 1, rotate: 0 }} transition={{ type: "spring", damping: 12, stiffness: 200 }}>
                  <div className="w-24 h-24 rounded-full bg-gradient-to-br from-[hsl(142,60%,92%)] to-[hsl(142,60%,85%)] flex items-center justify-center mb-6 shadow-[0_8px_30px_hsl(142,50%,50%,0.2)]">
                    <CheckCircle2 className="h-12 w-12 text-[hsl(142,71%,40%)]" />
                  </div>
                </motion.div>
                <motion.h3 initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="font-extrabold text-2xl text-[hsl(0,0%,8%)] mb-2">Pedido enviado!</motion.h3>
                <motion.p initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }} className="text-[hsl(0,0%,45%)] text-sm mb-5 max-w-xs">Seu pedido foi recebido e está sendo preparado com carinho.</motion.p>

                {/* Track Order Button for delivery orders */}
                {successOrderId && (
                  <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }} className="w-full max-w-xs mb-3">
                    <button
                      onClick={() => navigate(`/acompanhar?pedido=${successOrderId}${restaurantSlug ? `&r=${restaurantSlug}` : ""}`)}
                      className="w-full h-[52px] rounded-2xl font-bold text-[15px] text-[hsl(0,0%,100%)] flex items-center justify-center gap-2 shadow-lg transition-all active:scale-[0.97]"
                      style={{ backgroundColor: primaryColor, boxShadow: `0 6px 20px ${primaryColor}50` }}
                    >
                      <Clock className="h-5 w-5" /> Acompanhar Pedido
                    </button>
                  </motion.div>
                )}

                {currentUser && loyaltyPreview.length > 0 && (
                  <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5 }} className="rounded-2xl bg-[hsl(210,100%,96%)] border border-[hsl(210,100%,88%)] p-4 mb-3 w-full max-w-xs">
                    <p className="text-xs font-semibold text-[hsl(210,80%,40%)] flex items-center justify-center gap-1"><Clock className="h-3.5 w-3.5" /> Sobre suas recompensas</p>
                    <p className="text-xs text-[hsl(0,0%,50%)] mt-1.5">Seus pontos/recompensas serão adicionados à sua conta assim que o pedido for finalizado.</p>
                  </motion.div>
                )}

                {currentUser && loyaltyPreview.length > 0 && orderCompleted && (
                  <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} className="rounded-2xl bg-gradient-to-br from-[hsl(142,50%,96%)] to-[hsl(142,50%,93%)] border border-[hsl(142,50%,85%)] p-4 mb-3 w-full max-w-xs shadow-sm">
                    <p className="text-xs font-bold text-[hsl(142,71%,35%)] flex items-center justify-center gap-1"><Gift className="h-3.5 w-3.5" /> Recompensas creditadas! 🎉</p>
                    {loyaltyPreview.map((lp) => (
                      <p key={lp.id} className="text-xs text-[hsl(0,0%,50%)] mt-0.5">{lp.earn}</p>
                    ))}
                  </motion.div>
                )}

                {!currentUser && loyaltyPrograms.length > 0 && (
                  <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.6 }} className="rounded-2xl bg-[hsl(45,100%,96%)] border border-[hsl(45,100%,88%)] p-4 mb-3 w-full max-w-xs">
                    <p className="text-xs text-[hsl(0,0%,50%)]">Crie uma conta para acumular recompensas!</p>
                    <button onClick={() => { setAuthMode("register"); setAuthError(""); setAuthModal(true); }} className="text-xs font-bold mt-1.5" style={{ color: menuAccent }}>
                      Criar conta grátis →
                    </button>
                  </motion.div>
                )}

                {orderCompleted && checkoutStep === "success" && (
                  <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-xs mb-3">
                    <button
                      onClick={() => setCheckoutStep("rating")}
                      className="w-full h-12 rounded-2xl font-bold text-sm text-[hsl(0,0%,100%)] transition-all active:scale-[0.97]"
                      style={{ backgroundColor: primaryColor }}
                    >
                      ⭐ Avaliar pedido
                    </button>
                  </motion.div>
                )}

                <div className="flex flex-col items-center gap-3 w-full max-w-xs">
                  {whatsappUrl && (
                    <a href={whatsappUrl} target="_blank" rel="noopener noreferrer" className="w-full inline-flex items-center justify-center gap-2 h-12 rounded-2xl bg-[hsl(142,70%,45%)] text-[hsl(0,0%,100%)] font-bold text-sm hover:opacity-90 transition-all active:scale-[0.97] shadow-[0_4px_16px_hsl(142,70%,45%,0.3)]">
                      <MessageCircle className="h-5 w-5" /> Enviar via WhatsApp
                    </a>
                  )}
                  <button onClick={resetCheckout} className="w-full h-12 rounded-2xl border-2 border-[hsl(0,0%,88%)] text-[hsl(0,0%,30%)] font-semibold text-sm transition-colors hover:bg-[hsl(0,0%,96%)]">
                    Voltar ao cardápio
                  </button>
                </div>
              </div>
            )}
          </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Comanda Modal — Table mode */}
      <AnimatePresence>
        {showComanda && isTableMode && (
          <>
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="hidden md:block fixed inset-0 z-[49] bg-[hsl(0,0%,0%)]/50 backdrop-blur-sm" onClick={() => { setShowComanda(false); setCheckoutStep("cart"); }} />
          <motion.div initial={{ y: "100%" }} animate={{ y: 0 }} exit={{ y: "100%" }} transition={{ type: "spring", damping: 25 }} className="fixed inset-0 z-50 bg-[hsl(0,0%,100%)] flex flex-col md:inset-auto md:top-1/2 md:left-1/2 md:-translate-x-1/2 md:-translate-y-1/2 md:w-full md:max-w-lg md:max-h-[90vh] md:rounded-2xl md:shadow-2xl md:border md:border-[hsl(0,0%,90%)]">
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-[hsl(0,0%,94%)]">
              {checkoutStep === "tab_closing" || checkoutStep === "tab_payment" ? (
                <button onClick={() => setCheckoutStep("comanda")} className="flex items-center gap-1 text-sm text-[hsl(0,0%,45%)]">
                  <ArrowLeft className="h-4 w-4" /> Voltar
                </button>
              ) : <div />}
              <h2 className="font-bold text-lg text-[hsl(0,0%,13%)]">
                {checkoutStep === "tab_closing" ? "Finalizar Atendimento" : checkoutStep === "tab_payment" ? "Pagamento" : `Comanda — Mesa ${mesaParam}`}
              </h2>
              <button onClick={() => { setShowComanda(false); setCheckoutStep("cart"); }} className="w-8 h-8 rounded-full bg-[hsl(0,0%,96%)] flex items-center justify-center">
                <X className="h-4 w-4 text-[hsl(0,0%,45%)]" />
              </button>
            </div>

            {/* Comanda items view */}
            {(checkoutStep === "cart" || checkoutStep === "comanda") && (
              <>
                <div className="flex-1 overflow-auto">
                  {tabItems.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-16 px-4">
                      <Receipt className="h-12 w-12 text-[hsl(0,0%,80%)] mb-4" />
                      <p className="text-[hsl(0,0%,50%)] font-medium">Comanda vazia</p>
                      <p className="text-xs text-[hsl(0,0%,65%)] mt-1">Adicione itens do cardápio</p>
                    </div>
                  ) : (
                    <div className="divide-y divide-[hsl(0,0%,96%)]">
                      {tabItems.map((item) => {
                        const addons = item.addons || [];
                        const addonTotal = addons.reduce((a: number, ad: any) => a + (ad.price || 0) * (ad.qty || 1), 0);
                        return (
                          <div key={item.id} className="px-4 py-4">
                            <div className="flex items-start justify-between gap-3">
                              <div className="flex-1 min-w-0">
                                <p className="font-semibold text-sm text-[hsl(0,0%,13%)]">{item.quantity}x {item.product_name}</p>
                                {addons.length > 0 && (
                                  <div className="mt-0.5">
                                    {addons.map((ad: any, idx: number) => (
                                      <p key={idx} className="text-[11px] text-[hsl(0,0%,55%)]">+ {ad.name}{(ad.qty || 1) > 1 ? ` (x${ad.qty})` : ""}</p>
                                    ))}
                                  </div>
                                )}
                                <p className="text-[10px] text-[hsl(0,0%,65%)] mt-1">{new Date(item.created_at).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })}</p>
                              </div>
                              <p className="text-sm font-bold text-[hsl(0,0%,13%)] shrink-0">{fmt((Number(item.unit_price) + addonTotal) * item.quantity)}</p>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>

                {/* Comanda Footer */}
                {tabItems.length > 0 && (
                  <div className="p-4 border-t border-[hsl(0,0%,94%)] space-y-3">
                    <div className="flex justify-between">
                      <span className="text-sm text-[hsl(0,0%,45%)]">Total da Comanda</span>
                      <span className="font-bold text-lg text-[hsl(0,0%,13%)]">{fmt(tabTotal)}</span>
                    </div>

                    {waiterRequested ? (
                      <div className="p-3 rounded-xl bg-[hsl(45,100%,95%)] border border-[hsl(45,100%,80%)] flex items-center gap-3">
                        <Loader2 className="h-5 w-5 animate-spin text-[hsl(35,100%,40%)]" />
                        <div>
                          <p className="text-sm font-semibold text-[hsl(35,80%,30%)]">Garçom chamado!</p>
                          <p className="text-xs text-[hsl(35,50%,45%)]">Aguarde o atendimento...</p>
                        </div>
                        <button onClick={cancelWaiterRequest} className="ml-auto text-xs text-[hsl(0,0%,55%)] underline">Cancelar</button>
                      </div>
                    ) : (
                      <button
                        onClick={handleFinalizarAtendimento}
                        className="w-full h-14 rounded-xl font-bold text-base text-[hsl(0,0%,100%)] bg-[hsl(35,100%,50%)] shadow-[0_4px_15px_hsl(35,100%,50%,0.3)] flex items-center justify-center gap-2"
                      >
                        <Bell className="h-5 w-5" /> Finalizar Atendimento
                      </button>
                    )}

                    <button
                      onClick={() => { setShowComanda(false); }}
                      className="w-full text-center text-sm text-[hsl(0,0%,55%)] py-2"
                    >
                      Continuar pedindo
                    </button>
                  </div>
                )}
              </>
            )}

            {/* Tab Closing Step — Login + Split + Payment */}
            {checkoutStep === "tab_closing" && (
              <div className="flex-1 overflow-auto p-4 space-y-5">
                {/* Login for loyalty */}
                {!currentUser && loyaltyPrograms.length > 0 && (
                  <div className="rounded-xl bg-[hsl(45,100%,96%)] border border-[hsl(45,100%,88%)] p-4 space-y-3">
                    <p className="text-sm font-bold text-[hsl(35,100%,30%)] flex items-center gap-2"><Crown className="h-4 w-4" /> Possui cadastro?</p>
                    <p className="text-xs text-[hsl(0,0%,50%)]">Faça login para acumular pontos e recompensas neste pedido.</p>
                    <button
                      onClick={() => { setAuthMode("login"); setAuthError(""); setAuthModal(true); }}
                      className="w-full h-12 rounded-xl font-bold text-sm text-[hsl(0,0%,100%)] flex items-center justify-center gap-2"
                      style={{ backgroundColor: primaryColor }}
                    >
                      <LogIn className="h-4 w-4" /> Entrar na conta
                    </button>
                    <button
                      onClick={() => { setAuthMode("register"); setAuthError(""); setAuthModal(true); }}
                      className="w-full text-center text-xs text-[hsl(0,100%,45%)] font-semibold"
                    >
                      Criar uma conta
                    </button>
                  </div>
                )}

                {currentUser && (
                  <div className="rounded-xl bg-[hsl(142,50%,96%)] border border-[hsl(142,50%,85%)] p-3 flex items-center gap-2">
                    <CheckCircle2 className="h-4 w-4 text-[hsl(142,71%,40%)]" />
                    <span className="text-sm text-[hsl(142,71%,30%)] font-medium">Logado como {customerName || currentUser.email}</span>
                  </div>
                )}

                {/* Split mode */}
                <div className="space-y-3">
                  <h3 className="text-sm font-bold text-[hsl(0,0%,13%)]">Como deseja pagar?</h3>
                  <div className="grid grid-cols-3 gap-2">
                    <button onClick={() => setSplitMode("single")} className={`p-3 rounded-xl border-2 text-center text-xs font-medium transition-all ${splitMode === "single" ? "border-[hsl(210,80%,45%)] bg-[hsl(210,80%,97%)] text-[hsl(210,80%,40%)]" : "border-[hsl(0,0%,92%)] text-[hsl(0,0%,50%)]"}`}>
                      <span className="text-lg block mb-1">💳</span>Conta única
                    </button>
                    <button onClick={() => setSplitMode("equal")} className={`p-3 rounded-xl border-2 text-center text-xs font-medium transition-all ${splitMode === "equal" ? "border-[hsl(210,80%,45%)] bg-[hsl(210,80%,97%)] text-[hsl(210,80%,40%)]" : "border-[hsl(0,0%,92%)] text-[hsl(0,0%,50%)]"}`}>
                      <span className="text-lg block mb-1">👥</span>Dividir igual
                    </button>
                    <button onClick={() => setSplitMode("by_item")} className={`p-3 rounded-xl border-2 text-center text-xs font-medium transition-all ${splitMode === "by_item" ? "border-[hsl(210,80%,45%)] bg-[hsl(210,80%,97%)] text-[hsl(210,80%,40%)]" : "border-[hsl(0,0%,92%)] text-[hsl(0,0%,50%)]"}`}>
                      <span className="text-lg block mb-1">📋</span>Por item
                    </button>
                  </div>

                  {splitMode === "equal" && (
                    <div className="p-3 rounded-xl bg-[hsl(0,0%,97%)] space-y-2">
                      <p className="text-xs font-medium text-[hsl(0,0%,40%)]">Dividir por quantas pessoas?</p>
                      <div className="flex items-center justify-center gap-4">
                        <button onClick={() => setSplitCount(Math.max(2, splitCount - 1))} className="w-10 h-10 rounded-full border border-[hsl(0,0%,85%)] flex items-center justify-center text-[hsl(0,0%,50%)]"><Minus className="h-4 w-4" /></button>
                        <span className="text-2xl font-bold text-[hsl(0,0%,13%)] w-8 text-center">{splitCount}</span>
                        <button onClick={() => setSplitCount(splitCount + 1)} className="w-10 h-10 rounded-full bg-[hsl(210,80%,45%)] text-[hsl(0,0%,100%)] flex items-center justify-center"><Plus className="h-4 w-4" /></button>
                      </div>
                      <p className="text-center text-sm font-bold text-[hsl(210,80%,40%)]">{fmt(tabTotal / splitCount)} por pessoa</p>
                    </div>
                  )}

                  {splitMode === "by_item" && (
                    <div className="p-3 rounded-xl bg-[hsl(0,0%,97%)] space-y-2">
                      <p className="text-xs font-medium text-[hsl(0,0%,40%)]">Selecione seus itens:</p>
                      {tabItems.map((item) => {
                        const addons = item.addons || [];
                        const addonTotal = addons.reduce((a: number, ad: any) => a + (ad.price || 0) * (ad.qty || 1), 0);
                        const itemTotal = (Number(item.unit_price) + addonTotal) * item.quantity;
                        const isSelected = selectedSplitItems.has(item.id);
                        return (
                          <button
                            key={item.id}
                            onClick={() => {
                              const next = new Set(selectedSplitItems);
                              if (isSelected) next.delete(item.id); else next.add(item.id);
                              setSelectedSplitItems(next);
                            }}
                            className={`w-full flex items-center justify-between p-3 rounded-xl border-2 transition-all text-left ${isSelected ? "border-[hsl(210,80%,45%)] bg-[hsl(210,80%,97%)]" : "border-[hsl(0,0%,92%)] bg-[hsl(0,0%,100%)]"}`}
                          >
                            <div className="flex items-center gap-2">
                              <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all ${isSelected ? "border-[hsl(210,80%,45%)] bg-[hsl(210,80%,45%)]" : "border-[hsl(0,0%,75%)]"}`}>
                                {isSelected && <Check className="h-3 w-3 text-[hsl(0,0%,100%)]" />}
                              </div>
                              <span className="text-sm text-[hsl(0,0%,13%)]">{item.quantity}x {item.product_name}</span>
                            </div>
                            <span className="text-sm font-bold text-[hsl(0,0%,13%)]">{fmt(itemTotal)}</span>
                          </button>
                        );
                      })}
                      {selectedSplitItems.size > 0 && (
                        <p className="text-center text-sm font-bold text-[hsl(210,80%,40%)]">
                          Seu total: {fmt(tabItems.filter(i => selectedSplitItems.has(i.id)).reduce((s, i) => {
                            const addonTotal = (i.addons || []).reduce((a: number, ad: any) => a + (ad.price || 0) * (ad.qty || 1), 0);
                            return s + (Number(i.unit_price) + addonTotal) * i.quantity;
                          }, 0))}
                        </p>
                      )}
                    </div>
                  )}
                </div>

                {/* Payment method for tab closing */}
                <div className="space-y-3">
                  <h3 className="text-sm font-bold text-[hsl(0,0%,13%)]">Forma de pagamento</h3>
                  <div className="grid grid-cols-2 gap-2">
                    {(restaurant as any)?.mp_access_token && (restaurant as any)?.mp_public_key && (
                      <button onClick={() => setTabPaymentMethod("mp_pix")} className={`p-3 rounded-xl border-2 text-sm font-medium transition-all flex flex-col items-center gap-1 ${tabPaymentMethod === "mp_pix" ? "border-[hsl(170,100%,35%)] bg-[hsl(170,100%,97%)] text-[hsl(170,100%,30%)]" : "border-[hsl(0,0%,92%)] text-[hsl(0,0%,50%)]"}`}>
                        <span className="text-lg">📱</span><span className="text-[11px]">PIX</span>
                      </button>
                    )}
                    <button onClick={() => setTabPaymentMethod("credit_card")} className={`p-3 rounded-xl border-2 text-sm font-medium transition-all flex flex-col items-center gap-1 ${tabPaymentMethod === "credit_card" ? "border-[hsl(220,80%,50%)] bg-[hsl(220,80%,97%)] text-[hsl(220,80%,40%)]" : "border-[hsl(0,0%,92%)] text-[hsl(0,0%,50%)]"}`}>
                      <span className="text-lg">💳</span><span className="text-[11px]">Crédito</span>
                    </button>
                    <button onClick={() => setTabPaymentMethod("debit_card")} className={`p-3 rounded-xl border-2 text-sm font-medium transition-all flex flex-col items-center gap-1 ${tabPaymentMethod === "debit_card" ? "border-[hsl(220,80%,50%)] bg-[hsl(220,80%,97%)] text-[hsl(220,80%,40%)]" : "border-[hsl(0,0%,92%)] text-[hsl(0,0%,50%)]"}`}>
                      <span className="text-lg">💳</span><span className="text-[11px]">Débito</span>
                    </button>
                    <button onClick={() => setTabPaymentMethod("cash")} className={`p-3 rounded-xl border-2 text-sm font-medium transition-all flex flex-col items-center gap-1 ${tabPaymentMethod === "cash" ? "border-[hsl(120,50%,40%)] bg-[hsl(120,50%,97%)] text-[hsl(120,50%,35%)]" : "border-[hsl(0,0%,92%)] text-[hsl(0,0%,50%)]"}`}>
                      <span className="text-lg">💵</span><span className="text-[11px]">Dinheiro</span>
                    </button>
                  </div>
                </div>

                {/* Summary */}
                <div className="p-4 rounded-xl bg-[hsl(0,0%,98%)] border border-[hsl(0,0%,92%)] space-y-2">
                  <div className="flex justify-between text-sm text-[hsl(0,0%,30%)]">
                    <span>Total da comanda</span>
                    <span>{fmt(tabTotal)}</span>
                  </div>
                  {splitMode === "equal" && (
                    <div className="flex justify-between text-sm text-[hsl(210,80%,40%)]">
                      <span>Sua parte ({splitCount} pessoas)</span>
                      <span>{fmt(tabTotal / splitCount)}</span>
                    </div>
                  )}
                  {splitMode === "by_item" && selectedSplitItems.size > 0 && (
                    <div className="flex justify-between text-sm text-[hsl(210,80%,40%)]">
                      <span>Itens selecionados</span>
                      <span>{fmt(tabItems.filter(i => selectedSplitItems.has(i.id)).reduce((s, i) => {
                        const at = (i.addons || []).reduce((a: number, ad: any) => a + (ad.price || 0) * (ad.qty || 1), 0);
                        return s + (Number(i.unit_price) + at) * i.quantity;
                      }, 0))}</span>
                    </div>
                  )}
                  <div className="flex justify-between font-bold text-lg pt-2 border-t border-[hsl(0,0%,90%)] text-[hsl(0,0%,13%)]">
                    <span>A pagar</span>
                    <span>{fmt(
                      splitMode === "equal" ? tabTotal / splitCount :
                      splitMode === "by_item" ? tabItems.filter(i => selectedSplitItems.has(i.id)).reduce((s, i) => {
                        const at = (i.addons || []).reduce((a: number, ad: any) => a + (ad.price || 0) * (ad.qty || 1), 0);
                        return s + (Number(i.unit_price) + at) * i.quantity;
                      }, 0) :
                      tabTotal
                    )}</span>
                  </div>
                </div>

                {/* Close tab button */}
                <button
                  onClick={handleCloseTab}
                  disabled={submitting || !tabPaymentMethod || (splitMode === "by_item" && selectedSplitItems.size === 0)}
                  className="w-full h-14 rounded-xl font-bold text-base text-[hsl(0,0%,100%)] disabled:opacity-40 transition-opacity bg-[hsl(142,60%,42%)] shadow-[0_4px_15px_hsl(142,60%,42%,0.3)]"
                >
                  {submitting ? <Loader2 className="h-5 w-5 animate-spin mx-auto" /> : "Fechar Comanda"}
                </button>
              </div>
            )}
          </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Store Info Dialog */}
      <Dialog open={showStoreInfo} onOpenChange={setShowStoreInfo}>
        <DialogContent className="max-w-md mx-auto rounded-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-lg">
              <Info className="h-5 w-5" />
              Informações da Loja
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            {/* Hours - Per-day schedule */}
            <div className="flex items-start gap-3">
              <Clock className="h-5 w-5 text-[hsl(0,0%,50%)] mt-0.5 shrink-0" />
              <div className="w-full">
                <p className="font-semibold text-sm text-[hsl(0,0%,13%)]">Horário de Funcionamento</p>
                {daySchedules.length > 0 ? (
                  <div className="mt-1 space-y-0.5">
                    {["seg", "ter", "qua", "qui", "sex", "sab", "dom"].map((dk) => {
                      const dayLabel: Record<string, string> = { seg: "Seg", ter: "Ter", qua: "Qua", qui: "Qui", sex: "Sex", sab: "Sáb", dom: "Dom" };
                      const sched = daySchedules.find((s: any) => s.day_of_week === dk);
                      if (!sched || !sched.is_open) return (
                        <div key={dk} className="flex justify-between text-xs text-[hsl(0,0%,60%)]">
                          <span>{dayLabel[dk]}</span><span>Fechado</span>
                        </div>
                      );
                      return (
                        <div key={dk} className="flex justify-between text-xs text-[hsl(0,0%,50%)]">
                          <span className="font-medium text-[hsl(0,0%,30%)]">{dayLabel[dk]}</span>
                          <span>{sched.is_24h ? "24 horas" : `${sched.opening_time?.slice(0,5)} - ${sched.closing_time?.slice(0,5)}`}</span>
                        </div>
                      );
                    })}
                  </div>
                ) : restaurant.opening_time && restaurant.closing_time ? (
                  <>
                    <p className="text-sm text-[hsl(0,0%,50%)]">
                      {(restaurant as any).is_24h ? "Aberto 24 horas" : `${restaurant.opening_time.slice(0, 5)} - ${restaurant.closing_time.slice(0, 5)}`}
                    </p>
                    {Array.isArray((restaurant as any).operating_days) && (restaurant as any).operating_days.length > 0 && (
                      <p className="text-xs text-[hsl(0,0%,60%)] mt-0.5">
                        {((restaurant as any).operating_days as string[]).map((d: string) => {
                          const map: Record<string, string> = { seg: "Seg", ter: "Ter", qua: "Qua", qui: "Qui", sex: "Sex", sab: "Sáb", dom: "Dom" };
                          return map[d] || d;
                        }).join(", ")}
                      </p>
                    )}
                  </>
                ) : (
                  <p className="text-sm text-[hsl(0,0%,50%)]">Horário não informado</p>
                )}
              </div>
            </div>

            {/* Address */}
            {(restaurant.address_street || restaurant.address_city) && (
              <div className="flex items-start gap-3">
                <MapPin className="h-5 w-5 text-[hsl(0,0%,50%)] mt-0.5 shrink-0" />
                <div>
                  <p className="font-semibold text-sm text-[hsl(0,0%,13%)]">Endereço</p>
                  <p className="text-sm text-[hsl(0,0%,50%)]">
                    {[restaurant.address_street, restaurant.address_number].filter(Boolean).join(", ")}
                    {restaurant.address_neighborhood && ` - ${restaurant.address_neighborhood}`}
                  </p>
                  <p className="text-sm text-[hsl(0,0%,50%)]">
                    {[restaurant.address_city, restaurant.address_state].filter(Boolean).join(" - ")}
                    {restaurant.address_zip && ` • ${restaurant.address_zip}`}
                  </p>
                </div>
              </div>
            )}

            {/* Phone */}
            {restaurant.phone && (
              <div className="flex items-start gap-3">
                <Phone className="h-5 w-5 text-[hsl(0,0%,50%)] mt-0.5 shrink-0" />
                <div>
                  <p className="font-semibold text-sm text-[hsl(0,0%,13%)]">Telefone / WhatsApp</p>
                  <a href={`tel:${restaurant.phone}`} className="text-sm text-[hsl(210,100%,45%)] underline">{restaurant.phone}</a>
                </div>
              </div>
            )}

            {/* Social Media */}
            {((restaurant as any).social_instagram || (restaurant as any).social_facebook || (restaurant as any).social_tiktok || (restaurant as any).social_website) && (
              <div className="flex items-start gap-3">
                <Globe className="h-5 w-5 text-[hsl(0,0%,50%)] mt-0.5 shrink-0" />
                <div className="space-y-1">
                  <p className="font-semibold text-sm text-[hsl(0,0%,13%)]">Redes Sociais</p>
                  {(restaurant as any).social_instagram && (
                    <a href={`https://instagram.com/${(restaurant as any).social_instagram.replace("@", "")}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1.5 text-sm text-[hsl(210,100%,45%)] hover:underline">
                      <Instagram className="h-3.5 w-3.5" /> {(restaurant as any).social_instagram}
                    </a>
                  )}
                  {(restaurant as any).social_facebook && (
                    <a href={(restaurant as any).social_facebook.startsWith("http") ? (restaurant as any).social_facebook : `https://${(restaurant as any).social_facebook}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1.5 text-sm text-[hsl(210,100%,45%)] hover:underline">
                      Facebook
                    </a>
                  )}
                  {(restaurant as any).social_tiktok && (
                    <a href={`https://tiktok.com/${(restaurant as any).social_tiktok.replace("@", "@")}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1.5 text-sm text-[hsl(210,100%,45%)] hover:underline">
                      TikTok: {(restaurant as any).social_tiktok}
                    </a>
                  )}
                  {(restaurant as any).social_website && (
                    <a href={(restaurant as any).social_website.startsWith("http") ? (restaurant as any).social_website : `https://${(restaurant as any).social_website}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1.5 text-sm text-[hsl(210,100%,45%)] hover:underline">
                      <Globe className="h-3.5 w-3.5" /> {(restaurant as any).social_website}
                    </a>
                  )}
                </div>
              </div>
            )}

            {/* Delivery info */}
            {restaurant.delivery_fee !== null && (
              <div className="flex items-start gap-3">
                <MapPin className="h-5 w-5 text-[hsl(0,0%,50%)] mt-0.5 shrink-0" />
                <div>
                  <p className="font-semibold text-sm text-[hsl(0,0%,13%)]">Delivery</p>
                  <p className="text-sm text-[hsl(0,0%,50%)]">
                    {Number((restaurant as any).delivery_fee_per_km || 0) > 0
                      ? `R$ ${Number((restaurant as any).delivery_fee_per_km).toFixed(2).replace(".",",")} /km${Number((restaurant as any).delivery_free_until_km || 0) > 0 ? ` • Grátis até ${(restaurant as any).delivery_free_until_km} km` : ""}`
                      : Number(restaurant.delivery_fee) === 0 ? "Entrega grátis" : `Taxa: ${fmt(Number(restaurant.delivery_fee))}`}
                    {Number(restaurant.min_order_value || 0) > 0 && ` • Mínimo: ${fmt(Number(restaurant.min_order_value))}`}
                  </p>
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
    </div>

    {/* Promo Popup Modal */}
    <AnimatePresence>
      {showPromoPopup && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[60] bg-black/50 flex items-center justify-center p-6"
          onClick={() => setShowPromoPopup(null)}
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            className="bg-white rounded-2xl overflow-hidden w-full max-w-sm shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            {showPromoPopup.image_url && (
              <img src={showPromoPopup.image_url} alt="" className="w-full h-48 object-cover" />
            )}
            <div className="p-5">
              <h3 className="font-bold text-lg" style={{ color: menuText }}>{showPromoPopup.title}</h3>
              {showPromoPopup.description && (
                <p className="text-sm mt-1 opacity-70" style={{ color: menuText }}>{showPromoPopup.description}</p>
              )}
              <div className="flex gap-2 mt-4">
                {showPromoPopup.link_url && (
                  <a
                    href={showPromoPopup.link_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex-1 text-center py-2.5 rounded-xl text-sm font-semibold text-white"
                    style={{ backgroundColor: menuAccent }}
                  >
                    Ver mais
                  </a>
                )}
                <button
                  onClick={() => setShowPromoPopup(null)}
                  className="flex-1 py-2.5 rounded-xl text-sm font-semibold border"
                  style={{ borderColor: `${menuText}20`, color: menuText }}
                >
                  Fechar
                </button>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>

    {/* Available Coupons Popup */}
    <AnimatePresence>
      {showCouponsPopup && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[60] bg-black/50 flex items-end md:items-center md:justify-center p-0 md:p-6"
          onClick={() => setShowCouponsPopup(false)}
        >
          <motion.div
            initial={{ y: "100%" }}
            animate={{ y: 0 }}
            exit={{ y: "100%" }}
            transition={{ type: "spring", damping: 28, stiffness: 300 }}
            className="bg-white rounded-t-[28px] md:rounded-2xl w-full max-w-md max-h-[80vh] flex flex-col shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-center pt-3 pb-1 md:hidden">
              <div className="w-10 h-1.5 rounded-full bg-[hsl(0,0%,85%)]" />
            </div>
            <div className="px-5 pt-3 pb-4 border-b border-[hsl(0,0%,94%)]">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: `linear-gradient(135deg, ${menuAccent}20, ${menuAccent}10)` }}>
                    <Ticket className="h-5 w-5" style={{ color: menuAccent }} />
                  </div>
                  <div>
                    <h3 className="font-bold text-base" style={{ color: menuText }}>Cupons disponíveis</h3>
                    <p className="text-[11px]" style={{ color: `${menuText}60` }}>{availableCoupons.length} cupom(ns) disponível(is)</p>
                  </div>
                </div>
                <button onClick={() => setShowCouponsPopup(false)} className="w-8 h-8 rounded-full bg-[hsl(0,0%,96%)] flex items-center justify-center">
                  <X className="h-4 w-4" style={{ color: `${menuText}50` }} />
                </button>
              </div>
            </div>
            <div className="flex-1 overflow-auto p-4 space-y-3">
              {availableCoupons.map((coupon) => (
                <div
                  key={coupon.id}
                  className="border rounded-2xl p-4 transition-all"
                  style={{ borderColor: `${menuText}10`, backgroundColor: `${menuText}02` }}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-mono font-bold text-sm tracking-wider px-2.5 py-1 rounded-lg" style={{ backgroundColor: `${menuAccent}15`, color: menuAccent }}>
                          {coupon.code}
                        </span>
                        {!coupon.applies_to_discounted && (
                          <span className="text-[9px] font-semibold px-1.5 py-0.5 rounded-full bg-[hsl(40,100%,93%)] text-[hsl(35,80%,35%)]">
                            Sem promoção
                          </span>
                        )}
                      </div>
                      <p className="text-sm font-semibold mt-2" style={{ color: menuText }}>
                        {coupon.type === "percentage" && `${coupon.value}% de desconto`}
                        {coupon.type === "fixed" && `${fmt(coupon.value)} de desconto`}
                        {coupon.type === "free_shipping" && "Frete grátis"}
                        {coupon.type === "free_product" && "Produto grátis"}
                      </p>
                      {coupon.description && (
                        <p className="text-xs mt-1" style={{ color: `${menuText}55` }}>{coupon.description}</p>
                      )}
                      <div className="flex flex-wrap items-center gap-2 mt-2">
                        {coupon.min_order_value > 0 && (
                          <span className="text-[10px] px-2 py-0.5 rounded-full" style={{ backgroundColor: `${menuText}06`, color: `${menuText}55` }}>
                            Pedido mín. {fmt(coupon.min_order_value)}
                          </span>
                        )}
                        {coupon.valid_until && (
                          <span className="text-[10px] px-2 py-0.5 rounded-full" style={{ backgroundColor: `${menuText}06`, color: `${menuText}55` }}>
                            Até {new Date(coupon.valid_until).toLocaleDateString("pt-BR")}
                          </span>
                        )}
                        {!coupon.applies_to_discounted && (
                          <span className="text-[10px] px-2 py-0.5 rounded-full bg-[hsl(40,100%,93%)] text-[hsl(35,80%,35%)]">
                            Não cumulativo com promoções
                          </span>
                        )}
                      </div>
                    </div>
                    <button
                      onClick={() => {
                        setCouponCode(coupon.code);
                        setShowCouponsPopup(false);
                        if (!showCart) setShowCart(true);
                        toast.success(`Cupom ${coupon.code} selecionado! Finalize seu pedido para aplicar.`);
                      }}
                      className="shrink-0 ml-3 h-9 px-4 rounded-xl text-xs font-bold text-white transition-all active:scale-95"
                      style={{ backgroundColor: menuAccent }}
                    >
                      Usar
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
    </>
  );
};

export default MenuDigital;
